import { Injectable } from '@angular/core';
import { ChartOptions, ChartData, ChartMarker, HorizontalLine, SystemLine, ChartState, TooltipData, MarkerTooltipData, ATRTooltipData, SeriesType, TickerDetails, TextAnnotation, PenDrawing, Measurement, ArrowDrawing, Indicator, IndicatorType, IndicatorSettings, CrosshairMode, ChartType, ChartIndicator, ChartSettings, ApiDrawing, ChartDrawingApi } from './chart.interface';
import { MarketDataService } from './services/market-data.service';
import { StorageService } from './services/storage.service';
import { IndicatorService } from './services/indicator.service';
import { TechnicalIndicatorsService } from './services/technical-indicators.service';
import { ChartTypeService } from './services/chart-type.service';
import { createChart, IChartApi, ISeriesApi, Time, CandlestickSeries, HistogramSeries, LineSeries, AreaSeries, BarSeries, BaselineSeries, createSeriesMarkers, SeriesMarker, CrosshairMode as LightweightCrosshairMode, LineWidth, TickMarkType } from 'lightweight-charts';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import moment from 'moment-timezone';
import { environment } from '../../environments/environment';

@Injectable({ providedIn: 'root' })
export class TradingViewChartService {
  private chart: IChartApi | null = null;
  private mainSeries: ISeriesApi<any> | null = null; // Main price series (can be any type)
  private volumeSeries: ISeriesApi<'Histogram'> | null = null;
  private volumeVisible = true;
  /** The DOM element passed into `createChart(...)`. Includes the price-scale gutter. */
  private chartHostContainer: HTMLElement | null = null;
  private chartRenderedSubject = new Subject<void>();
  public chartRendered$ = this.chartRenderedSubject.asObservable();
  private smaSeries: { [key: string]: ISeriesApi<'Line'> } = {};
  private atrSeries: { [key: string]: ISeriesApi<'Line'> } = {};
  private rsiSeries: { [key: string]: ISeriesApi<'Line'> } = {};
  private rsiReferenceLines: { [key: string]: { overbought: ISeriesApi<'Line'>; oversold: ISeriesApi<'Line'>; midline?: ISeriesApi<'Line'> } } = {};
  private bollingerSeries: { [key: string]: { upper: ISeriesApi<'Line'>; middle: ISeriesApi<'Line'>; lower: ISeriesApi<'Line'> } } = {};
  private macdSeries: { [key: string]: { macdLine: ISeriesApi<'Line'>; signalLine: ISeriesApi<'Line'>; histogram: ISeriesApi<'Histogram'> } } = {};
  private stochSeries: { [key: string]: { kLine: ISeriesApi<'Line'>; dLine: ISeriesApi<'Line'>; overbought: ISeriesApi<'Line'>; oversold: ISeriesApi<'Line'> } } = {};
  private state: ChartState = {
    markers: [],
    lines: [],
    systemLines: [],
    textAnnotations: [],
    indicators: [], // New indicator system
    markerVisibility: {
      earnings: true,
      dividend: true,
      split: true,
      custom: true,
      alert: true,
      news: true,
      other: true
    },
    systemLineVisibility: {
      SUPPORT: true,
      RESISTANCE: true
    },
    selectedTimePeriod: '1y',
    isDrawingMode: false,
    isTextMode: false,
    isInlineEditing: false,
    isMarkerMode: false,
    isPenMode: false,
    isMeasuringMode: false,
    isArrowMode: false,
    penDrawings: [],
    measurements: [],
    arrowDrawings: [],
    penSize: 2,
    penColor: '#2962FF',
    measureColor: '#00ff00',
    seriesType: 'candlestick',
    crosshairMode: 'normal',
    showGridLines: true
  };

  // fullDatasetAll: full historical dataset received from socket (may be > selected range)
  // fullDataset: the currently displayed dataset after applying selected time range
  private fullDatasetAll: ChartData[] = [];
  private fullDataset: ChartData[] = [];
  private currentSymbol: string = '';
  private currentInterval: string = '';
  /** True once the preset viewport (1D/5D/1M/…) has been applied for the current symbol+range. */
  private presetViewportApplied = false;
  /** Invalidates delayed viewport apply/restore so a later payload cannot keep a snapshot zoom. */
  private viewportApplyToken = 0;
  private static readonly DEFAULT_BAR_SPACING = 8;
  private forceRefresh: boolean = false;
  private backgroundSeries: any = null; // For market hours shading
  private horizontalLines: Map<string, any> = new Map(); // Store price line references
  private systemLines: Map<string, any> = new Map(); // Store system line references
  private lineCounter: number = 1; // Auto-increment counter for line names
  private onLinesChangedCallback?: () => void; // Callback for UI updates
  private onTooltipChangedCallback?: (tooltip: TooltipData | null) => void
  private textAnnotationEventListenersSetup: boolean = false; // Flag to prevent multiple event listener setups
  private onMarkerTooltipChangedCallback?: (tooltip: MarkerTooltipData | null) => void; // Callback for marker tooltip updates
  private onATRTooltipChangedCallback?: (tooltip: ATRTooltipData | null) => void; // Callback for ATR tooltip updates
  private onLineSelectedCallback?: (line: HorizontalLine | null, x?: number, y?: number) => void; // Callback for line selection
  private onTextSelectedCallback?: (text: TextAnnotation | null, x?: number, y?: number) => void;
  private onVisibleLogicalRangeChangedCallback?: (range: any) => void;

  // Pen drawing properties
  private isDrawing: boolean = false;
  private currentDrawing: PenDrawing | null = null;
  private penCanvas: HTMLCanvasElement | null = null;
  private penContext: CanvasRenderingContext2D | null = null;
  private penDrawingsContainer: HTMLDivElement | null = null; // Dedicated overlay so pen drawings always display
  private selectedPenDrawingId: string | null = null;
  private onPenDrawingSelectedCallback?: (drawing: PenDrawing | null, x?: number, y?: number) => void;
  private onArrowSelectedCallback?: (drawing: ArrowDrawing | null, x?: number, y?: number) => void;
  private static readonly PEN_MIN_POINT_DISTANCE_PX = 1.5;
  private onTickerDetailsChangedCallback?: (details: TickerDetails | null) => void; // Callback for ticker details updates
  private selectedLineId: string | null = null;
  private isDragging = false;
  private dragStartY = 0;
  private longPressTimer: any = null;
  private longPressStarted = false;
  private mouseDownOnLine = false;
  private dragThreshold = 5; // pixels - minimum movement to start drag
  private tooltipHideTimeout: ReturnType<typeof setTimeout> | null = null; // Delay hide to prevent flicker
  private static readonly TOOLTIP_HIDE_DELAY_MS = 150;
  private static readonly DEFAULT_RANGE = '1y';

  // Chart types management
  private chartTypesSubject = new BehaviorSubject<ChartType[]>([]);
  public chartTypes$ = this.chartTypesSubject.asObservable();
  private chartTypesLoaded = false;

  // Chart indicators management
  private chartIndicatorsSubject = new BehaviorSubject<ChartIndicator[]>([]);
  public chartIndicators$ = this.chartIndicatorsSubject.asObservable();
  private chartIndicatorsLoaded = false;

  constructor(
    private marketDataService: MarketDataService,
    private storageService: StorageService,
    private indicatorService: IndicatorService,
    private technicalIndicatorsService: TechnicalIndicatorsService,
    private chartTypeService: ChartTypeService
  ) { }

  // Set callback for when lines change
  setOnLinesChangedCallback(callback?: () => void): void {
    this.onLinesChangedCallback = callback;
  }

  // Set callback for tooltip updates
  setOnTooltipChangedCallback(callback?: (tooltip: TooltipData | null) => void): void {
    this.onTooltipChangedCallback = callback;
  }

  // Set callback for marker tooltip updates
  setOnMarkerTooltipChangedCallback(callback?: (tooltip: MarkerTooltipData | null) => void): void {
    this.onMarkerTooltipChangedCallback = callback;
  }

  // Set callback for ATR tooltip updates
  setOnATRTooltipChangedCallback(callback?: (tooltip: ATRTooltipData | null) => void): void {
    this.onATRTooltipChangedCallback = callback;
  }

  // Set callback for line selection
  setOnLineSelectedCallback(callback?: (line: HorizontalLine | null, x?: number, y?: number) => void): void {
    this.onLineSelectedCallback = callback;
  }

  // Set callback for pen drawing selection
  setOnPenDrawingSelectedCallback(callback?: (drawing: PenDrawing | null, x?: number, y?: number) => void): void {
    this.onPenDrawingSelectedCallback = callback;
  }

  // Set callback for text selection
  setOnTextSelectedCallback(callback?: (text: TextAnnotation | null, x?: number, y?: number) => void): void {
    this.onTextSelectedCallback = callback;
  }

  // Set callback for arrow selection
  setOnArrowSelectedCallback(callback?: (drawing: ArrowDrawing | null, x?: number, y?: number) => void): void {
    this.onArrowSelectedCallback = callback;
  }

  setOnVisibleLogicalRangeChangedCallback(callback?: (range: any) => void): void {
    this.onVisibleLogicalRangeChangedCallback = callback;
  }

  // Set callback for ticker details updates
  setOnTickerDetailsChangedCallback(callback?: (details: TickerDetails | null) => void): void {
    this.onTickerDetailsChangedCallback = callback;
  }

  // Get native chart screenshot canvas
  public takeScreenshot(): HTMLCanvasElement | null {
    if (!this.chart) {
      return null;
    }
    return this.chart.takeScreenshot();
  }

  /**
   * Clears ALL visible chart data and history variables — price, volume, indicators,
   * and in-memory datasets. Resets symbol/interval so the next load has no stale state.
   * Call this when switching symbol or range to avoid duplicate or mixed data.
   */
  public clearChartData(): void {
    this.fullDataset = [];
    this.fullDatasetAll = [];
    this.currentSymbol = '';
    this.currentInterval = '';
    this.presetViewportApplied = false;
    this.viewportApplyToken++;
    this.resetTimeScaleForNewData();

    if (this.mainSeries) {
      try { this.mainSeries.setData([]); } catch (_) { }
    }
    if (this.volumeSeries) {
      try { this.volumeSeries.setData([]); } catch (_) { }
    }

    this.clearAllIndicatorSeriesData();
  }

  /** Drop the previous ticker's bar spacing / logical window so the next symbol starts clean. */
  private resetTimeScaleForNewData(): void {
    if (!this.chart) {
      return;
    }
    try {
      this.chart.applyOptions({
        timeScale: {
          barSpacing: TradingViewChartService.DEFAULT_BAR_SPACING,
          rightOffset: 6,
          fixRightEdge: false,
          shiftVisibleRangeOnNewBar: false
        }
      });
    } catch (_) { /* noop */ }
  }

  /** Empties every indicator series on the chart without removing the series objects. */
  private clearAllIndicatorSeriesData(): void {
    const empty: any[] = [];
    // SMA / EMA
    for (const key of Object.keys(this.smaSeries)) {
      try { this.smaSeries[key].setData(empty); } catch (_) { }
    }
    // ATR
    for (const key of Object.keys(this.atrSeries)) {
      try { this.atrSeries[key].setData(empty); } catch (_) { }
    }
    // RSI + reference lines
    for (const key of Object.keys(this.rsiSeries)) {
      try { this.rsiSeries[key].setData(empty); } catch (_) { }
    }
    for (const key of Object.keys(this.rsiReferenceLines)) {
      const ref = this.rsiReferenceLines[key];
      try { ref.overbought.setData(empty); } catch (_) { }
      try { ref.oversold.setData(empty); } catch (_) { }
      if (ref.midline) { try { ref.midline.setData(empty); } catch (_) { } }
    }
    // Bollinger Bands
    for (const key of Object.keys(this.bollingerSeries)) {
      const bb = this.bollingerSeries[key];
      try { bb.upper.setData(empty); } catch (_) { }
      try { bb.middle.setData(empty); } catch (_) { }
      try { bb.lower.setData(empty); } catch (_) { }
    }
    // MACD
    for (const key of Object.keys(this.macdSeries)) {
      const m = this.macdSeries[key];
      try { m.macdLine.setData(empty); } catch (_) { }
      try { m.signalLine.setData(empty); } catch (_) { }
      try { m.histogram.setData(empty); } catch (_) { }
    }
    // Stochastic
    for (const key of Object.keys(this.stochSeries)) {
      const s = this.stochSeries[key];
      try { s.kLine.setData(empty); } catch (_) { }
      try { s.dLine.setData(empty); } catch (_) { }
      try { s.overbought.setData(empty); } catch (_) { }
      try { s.oversold.setData(empty); } catch (_) { }
    }
  }

  /** Returns true when the chart has rendered data (fullDataset has items). */
  public hasChartData(): boolean {
    return this.fullDataset.length > 0;
  }

  /** Returns first candle OHLC from currently displayed dataset. */
  public getFirstCandleOhlc(): { open: number; high: number; low: number; close: number } | null {
    if (!Array.isArray(this.fullDataset) || this.fullDataset.length === 0) return null;
    const first = this.fullDataset[0];
    if (!first) return null;
    const { open, high, low, close } = first;
    if (![open, high, low, close].every(v => typeof v === 'number' && Number.isFinite(v))) return null;
    return { open, high, low, close };
  }

  /** Returns latest candle OHLC from currently displayed dataset. */
  public getLatestCandleOhlc(): { open: number; high: number; low: number; close: number } | null {
    if (!Array.isArray(this.fullDataset) || this.fullDataset.length === 0) return null;
    const latest = this.fullDataset[this.fullDataset.length - 1];
    if (!latest) return null;
    const { open, high, low, close } = latest;
    if (![open, high, low, close].every(v => typeof v === 'number' && Number.isFinite(v))) return null;
    return { open, high, low, close };
  }

  /** Returns latest candle volume from currently displayed dataset. */
  public getLatestCandleVolume(): number | null {
    if (!Array.isArray(this.fullDataset) || this.fullDataset.length === 0) return null;
    const latest = this.fullDataset[this.fullDataset.length - 1];
    const volume = latest?.volume;
    return typeof volume === 'number' && Number.isFinite(volume) ? volume : null;
  }

  /** Returns true when the lightweight-charts instance has been created. */
  public isChartReady(): boolean {
    return !!this.chart && !!this.mainSeries;
  }

  private toTimezoneMoment(time: any): moment.Moment {
    const configuredTimezone = environment.defaultTimezone || 'America/New_York';
    if (typeof time === 'object' && time?.year && time?.month && time?.day) {
      return moment.utc([time.year, time.month - 1, time.day]).tz(configuredTimezone, true);
    }
    return moment.unix(time as number).tz(configuredTimezone);
  }

  private isIntradaySeries(): boolean {
    if (!Array.isArray(this.fullDataset) || this.fullDataset.length < 2) {
      return false;
    }

    const first = Number(this.fullDataset[0]?.time);
    const second = Number(this.fullDataset[1]?.time);
    if (!Number.isFinite(first) || !Number.isFinite(second)) {
      return false;
    }

    // If candle spacing is below one day, treat as intraday (5m/15m/30m/1h/etc.).
    return Math.abs(second - first) < 86400;
  }

  private formatBottomCrosshairTime(time: any): string {
    const m = this.toTimezoneMoment(time);
    if (this.isIntradaySeries()) {
      return `${m.format("DD MMM 'YYYY, h:mm A")}`;
    }
    if (typeof time === 'object' && time?.year && time?.month && time?.day) {
      return moment.utc([time.year, time.month - 1, time.day]).format("DD MMM 'YYYY");
    }
    return moment.unix(time as number).utc().format("DD MMM 'YYYY");
  }

  // Initialize chart
  initializeChart(container: HTMLElement, options: ChartOptions): IChartApi {

    try {
      this.chartHostContainer = container;
      this.chart = createChart(container, {
        width: options.width || container.clientWidth,
        height: options.height || 500,
        layout: {
          background: { color: '#1a1a1a' }, // Default to dark theme
          textColor: '#d1d4dc',
          panes: {
            separatorColor: '#2b2b43', // Dark separator between panes
            separatorHoverColor: '#FF6B35', // Orange on hover to match ATR color
            enableResize: true, // Allow users to resize panes
          },
        },
        grid: {
          vertLines: {
            color: '#2b2b43',
            visible: this.state.showGridLines
          },
          horzLines: {
            color: '#2b2b43',
            style: 1,
            visible: this.state.showGridLines
          },
        },
        localization: {
          timeFormatter: (time: any) => {
            return this.formatBottomCrosshairTime(time);
          },
        },
        crosshair: {
          mode: LightweightCrosshairMode.Normal, // Will be set properly after creation
          vertLine: {
            width: 1,
            color: '#2962FF',
            style: 1,
          },
          horzLine: {
            width: 1,
            color: '#2962FF',
            style: 1,
          },
        },
        timeScale: {
          borderColor: '#2b2b43',
          timeVisible: true,
          secondsVisible: false,
          rightOffset: 14, // Right buffer so most recent candlestick isn't cramped
          barSpacing: TradingViewChartService.DEFAULT_BAR_SPACING,
          fixRightEdge: false, // Allow rightOffset to work properly
          shiftVisibleRangeOnNewBar: false, // Prevent automatic range adjustments
          tickMarkFormatter: (time: any, tickMarkType: TickMarkType) => {
            let m: moment.Moment;

            // Handle BusinessDay object {year, month, day} or UTCTimestamp (number)
            if (typeof time === 'object' && time.year && time.month && time.day) {
              // BusinessDay format - create moment in UTC, then convert to target timezone
              m = moment.utc([time.year, time.month - 1, time.day]).tz(environment.defaultTimezone);
            } else {
              // UTCTimestamp format (Unix seconds) - convert to target timezone
              m = moment.unix(time as number).tz(environment.defaultTimezone);
            }

            // lightweight-charts TickMarkType: Year=0, Month=1, DayOfMonth=2, Time=3
            if (tickMarkType === TickMarkType.Year) return m.format('YYYY');
            if (tickMarkType === TickMarkType.Month) return m.format('MMM');
            if (tickMarkType === TickMarkType.DayOfMonth) return m.format('DD MMM');
            return m.format('h:mm A');
          },
        },
        // Enable pan and zoom functionality
        handleScroll: {
          mouseWheel: true,    // Wheel = zoom only (via handleScale), not pan – avoids mixed feel
          pressedMouseMove: true, // Click-drag to pan
          horzTouchDrag: true,
          vertTouchDrag: true,
        },
        handleScale: {
          axisPressedMouseMove: {
            time: true,     // Enable time axis scaling with mouse
            price: true,    // Enable price axis scaling with mouse
          },
          axisDoubleClickReset: {
            time: true,     // Double-click time axis to reset zoom
            price: true,    // Double-click price axis to reset zoom
          },
          mouseWheel: true,      // Enable mouse wheel scaling
          pinch: true,           // Enable pinch-to-zoom on touch devices
        },
        kineticScroll: {
          mouse: true,    // Enable kinetic scroll with mouse
          touch: true,    // Enable kinetic scroll with touch gestures
        },
      });


      // Initialize series
      this.initializeSeries(options);

      // Apply initial chart settings
      this.setCrosshairMode(this.state.crosshairMode);
      this.setGridLinesVisible(this.state.showGridLines);

      // Ensure right offset is applied
      this.applyRightOffset();

      // Setup click handler for line drawing
      this.setupChartClickHandler();

      // Create pen drawings container so drawings persist when user deactivates pen tool
      const chartElement = this.chartHostContainer ?? this.chart.chartElement();
      if (chartElement && !this.penDrawingsContainer) {
        chartElement.style.position = 'relative';
        this.penDrawingsContainer = document.createElement('div');
        // High z-index so drawings remain visible above axes/price-scale DOM.
        this.penDrawingsContainer.style.cssText = 'position:absolute;inset:0;width:100%;height:100%;z-index:10000;pointer-events:none;visibility:visible;';
        chartElement.appendChild(this.penDrawingsContainer);
      }

      // Redraw pen drawings when time scale changes (zoom/pan) - needed for drawings loaded from API
      this.chart.timeScale().subscribeVisibleTimeRangeChange(() => {
        if (this.state.penDrawings.length > 0) {
          this.redrawAllPenDrawings();
        }
      });

      // Do not refit the right price scale on logical range changes — that resets vertical position while panning.
      // Framing runs on data load (refresh) and after wheel zoom (see attachPriceScaleWheelRefit).
      this.chart.timeScale().subscribeVisibleLogicalRangeChange(() => {
        try {
          const logicalRange = this.chart?.timeScale().getVisibleLogicalRange();
          if (logicalRange && this.onVisibleLogicalRangeChangedCallback) {
            this.onVisibleLogicalRangeChangedCallback(logicalRange);
          }
        } catch (_) { }
      });
      this.attachMousePriceScaleUnlock(chartElement);

      // DO NOT load data here - all data comes from socket
      // Chart will be populated when socket data arrives via setChartDataFromSocket()
      console.log('[CHART] Chart initialized - waiting for socket data');

      // If we already have socket data (arrived before chart initialization), apply it now
      if (this.fullDataset && this.fullDataset.length > 0 && this.mainSeries) {
        console.log('[CHART] Applying existing socket data to newly initialized chart');
        this.updateMainSeriesData(!this.presetViewportApplied);
        // Redraw pen drawings that were loaded before chart init (renderPenDrawing returns early when !chart)
        if (this.state.penDrawings.length > 0) {
          setTimeout(() => this.redrawAllPenDrawings(), 100);
          setTimeout(() => this.redrawAllPenDrawings(), 400);
        }
      }

      // Note: Don't auto-fit content as we set specific time ranges with padding
      // The setVisibleTimeRange method will handle proper time range display
      setTimeout(() => {
        if (this.chart) {
        }
      }, 200);

      return this.chart;
    } catch (error) {
      throw error;
    }
  }

  private initializeSeries(options: ChartOptions): void {
    if (!this.chart) return;

    // Create main price series (default to candlestick)
    this.createMainSeries(this.state.seriesType);

    // Always create volume series (we'll control visibility separately)
    this.volumeSeries = this.chart.addSeries(HistogramSeries, {
      color: 'rgba(38, 166, 154, 0.8)',
      priceFormat: {
        type: 'volume',
      },
      priceScaleId: 'volume',
      visible: this.volumeVisible,
    });

    this.applyVolumeScaleOptions();

    // Initialize SMA series if enabled
    // (Legacy SMA series creation removed - SMAs are now dynamically created by renderIndicators based on indicator IDs)
    // ATR series will be created dynamically when toggled on
  }

  private createMainSeries(seriesType: SeriesType): void {
    if (!this.chart) return;

    // Remove existing main series if it exists
    if (this.mainSeries) {
      this.chart.removeSeries(this.mainSeries);
    }

    // Create new series based on type
    switch (seriesType) {
      case 'candlestick':
        this.mainSeries = this.chart.addSeries(CandlestickSeries, {
          upColor: '#26a69a',
          downColor: '#ef5350',
          borderVisible: false,
          wickUpColor: '#26a69a',
          wickDownColor: '#ef5350',
          lastValueVisible: true, // Show current price on right axis
          priceLineVisible: true, // Show horizontal price line (can be toggled)
        });
        break;
      case 'line':
        this.mainSeries = this.chart.addSeries(LineSeries, {
          color: '#2962FF',
          lineWidth: 2,
          lineStyle: 0, // 0 = Solid line
          lastValueVisible: true, // Keep visible for main price series
          priceLineVisible: true, // Keep price line for main series
        });
        break;
      case 'area':
        this.mainSeries = this.chart.addSeries(AreaSeries, {
          lineColor: '#2962FF',
          topColor: '#2962FF',
          bottomColor: 'rgba(41, 98, 255, 0.28)',
          lastValueVisible: true, // Show current price on right axis
          priceLineVisible: true, // Show horizontal price line (can be toggled)
        });
        break;
      case 'bar':
        this.mainSeries = this.chart.addSeries(BarSeries, {
          upColor: '#26a69a',
          downColor: '#ef5350',
          thinBars: false, // Use thick bars instead of thin bars
          openVisible: true, // Show open tick marks
          lastValueVisible: true, // Show current price on right axis
          priceLineVisible: true, // Show horizontal price line (can be toggled)
        });
        break;
      case 'baseline':
        this.mainSeries = this.chart.addSeries(BaselineSeries, {
          baseValue: { type: 'price', price: 100 }, // Will be updated with actual data
          topLineColor: '#26a69a',
          topFillColor1: 'rgba(38, 166, 154, 0.28)',
          topFillColor2: 'rgba(38, 166, 154, 0.05)',
          bottomLineColor: '#ef5350',
          bottomFillColor1: 'rgba(239, 83, 80, 0.05)',
          bottomFillColor2: 'rgba(239, 83, 80, 0.28)',
          lastValueVisible: true, // Show current price on right axis
          priceLineVisible: true, // Show horizontal price line (can be toggled)
        });
        break;
      case 'heikin-ashi':
        this.mainSeries = this.chart.addSeries(CandlestickSeries, {
          upColor: '#26a69a',
          downColor: '#ef5350',
          borderVisible: false,
          wickUpColor: '#26a69a',
          wickDownColor: '#ef5350',
          lastValueVisible: true, // Show current price on right axis
          priceLineVisible: true, // Show horizontal price line (can be toggled)
        });
        break;
      default:
        // Default to candlestick
        this.mainSeries = this.chart.addSeries(CandlestickSeries, {
          upColor: '#26a69a',
          downColor: '#ef5350',
          borderVisible: false,
          wickUpColor: '#26a69a',
          wickDownColor: '#ef5350',
          lastValueVisible: true, // Show current price on right axis
          priceLineVisible: true, // Show horizontal price line (can be toggled)
        });
    }

    // Update data if we have it (this handles both API and socket data)
    if (this.fullDataset.length > 0) {
      console.log('[CHART] createMainSeries: Found existing data, applying', this.fullDataset.length, 'points');
      this.updateMainSeriesData(!this.presetViewportApplied);
    } else {
      console.log('[CHART] createMainSeries: No data available yet');
    }

    // Re-render all overlay-based drawings because switching/recreating the series
    // removes price lines and can invalidate text/pen coordinates.
    this.renderAllHorizontalLines();
    this.renderAllTextAnnotations();
    this.redrawAllPenDrawings();
  }

  private captureVisibleViewport(): {
    logical: { from: number; to: number } | null;
    time: { from: Time; to: Time } | null;
  } | null {
    if (!this.chart) {
      return null;
    }
    const timeScale = this.chart.timeScale();
    try {
      return {
        logical: timeScale.getVisibleLogicalRange(),
        time: timeScale.getVisibleRange(),
      };
    } catch (_) {
      return null;
    }
  }

  /** Restore pan/zoom after setData — lightweight-charts resets the timescale on every setData call. */
  private restoreVisibleViewport(
    saved: {
      logical: { from: number; to: number } | null;
      time: { from: Time; to: Time } | null;
    },
    applyToken: number = this.viewportApplyToken
  ): void {
    if (!this.chart) {
      return;
    }
    const timeScale = this.chart.timeScale();
    const apply = () => {
      if (applyToken !== this.viewportApplyToken) {
        return;
      }
      try {
        if (saved.time) {
          timeScale.setVisibleRange(saved.time);
          return;
        }
        if (saved.logical) {
          timeScale.setVisibleLogicalRange(saved.logical);
        }
      } catch (error) {
        console.warn('[CHART] Failed to restore visible viewport:', error);
      }
    };
    apply();
    requestAnimationFrame(apply);
    setTimeout(apply, 50);
  }

  private updateMainSeriesData(shouldApplyViewport: boolean = false): void {
    if (!this.mainSeries) {
      console.warn('[CHART] updateMainSeriesData: mainSeries is null');
      return;
    }
    if (this.fullDataset.length === 0) {
      console.warn('[CHART] updateMainSeriesData: fullDataset is empty');
      return;
    }

    // lightweight-charts requires strictly ascending unique timestamps.
    this.fullDataset = this.deduplicateChartData(this.fullDataset);

    if (this.fullDataset.length === 0) {
      console.warn('[CHART] No valid data after verification');
      return;
    }

    const applyToken = ++this.viewportApplyToken;
    const savedViewport = !shouldApplyViewport ? this.captureVisibleViewport() : null;

    console.log('[CHART] updateMainSeriesData: Updating chart with', this.fullDataset.length, 'data points');
    console.log('[CHART] Time range:', Number(this.fullDataset[0].time), 'to', Number(this.fullDataset[this.fullDataset.length - 1].time));
    const seriesType = this.state.seriesType;

    // Setting chart data for rendering

    if (seriesType === 'line' || seriesType === 'area' || seriesType === 'baseline') {
      // For single-value series, use close price
      const lineData = this.fullDataset.map(d => ({
        time: d.time,
        value: d.close
      }));
      console.log('[CHART] Setting line/area/baseline data:', lineData.length, 'points');
      // Setting line chart data
      this.mainSeries.setData(lineData);

      // Update baseline value if it's a baseline series
      if (seriesType === 'baseline' && lineData.length > 0) {
        const avgPrice = lineData.reduce((sum, item) => sum + item.value, 0) / lineData.length;
        this.mainSeries.applyOptions({
          baseValue: { type: 'price', price: avgPrice }
        });
      }
    } else if (seriesType === 'heikin-ashi') {
      // For Heikin-Ashi, calculate transformed OHLC data
      const heikinAshiData = this.calculateHeikinAshi(this.fullDataset);
      console.log('[CHART] Setting Heikin-Ashi data:', heikinAshiData.length, 'points');
      // Setting Heikin-Ashi data
      this.mainSeries.setData(heikinAshiData);
    } else {
      // For OHLC series (candlestick, bar), use full data
      console.log('[CHART] Setting candlestick/bar data:', this.fullDataset.length, 'points');
      console.log('[CHART] Sample data point:', this.fullDataset[0]);

      // Clear existing data first to ensure clean update
      this.mainSeries.setData([]);
      // Then set new data
      this.mainSeries.setData(this.fullDataset);

      // Add pre/post-market background shading (temporarily disabled due to data ordering issues)
      // TODO: Implement proper background shading that doesn't conflict with chart data
      // this.addMarketHoursShading();
    }

    console.log('[CHART] Data applied successfully to chart');

    // Update volume series with color-coded bars
    this.updateVolumeData();

    // Paint MAs immediately so they aren't blank after a symbol/range swap.
    this.renderIndicators();

    const rangeForViewport = this.currentInterval
      || this.state.selectedTimePeriod
      || TradingViewChartService.DEFAULT_RANGE;

    // Apply the selected range immediately on ticker/range loads so leftover zoom
    // from the previous symbol (or a 2–5 bar snapshot) never sticks on screen.
    if (shouldApplyViewport && this.chart) {
      this.setVisibleTimeRange(rangeForViewport);
      this.presetViewportApplied = true;
      this.fitPriceScaleToData();
    }

    // Fit price scale and re-apply after layout. Use a short delay for small datasets
    // (e.g. 1d with ~26–78 bars) so the loading overlay hides sooner.
    const len = this.fullDataset.length;
    const delay = len > 1000 ? 200 : len <= 150 ? 0 : 50;
    setTimeout(() => {
      if (applyToken !== this.viewportApplyToken) {
        return;
      }
      if (shouldApplyViewport && this.chart) {
        // Do NOT auto-fit the entire dataset — always respect the selected range.
        this.setVisibleTimeRange(rangeForViewport);
        this.presetViewportApplied = true;
        this.fitPriceScaleToData();
      } else if (savedViewport && this.chart) {
        this.restoreVisibleViewport(savedViewport, applyToken);
      }

      // Overlay series can clip if setData races with setVisibleRange.
      // Re-paint after the range apply, then once more after the delayed range retry.
      this.renderIndicators();
      setTimeout(() => this.renderIndicators(), 80);

      this.chartRenderedSubject.next();
    }, delay);
  }

  private updateVolumeData(): void {
    if (!this.volumeSeries || this.fullDataset.length === 0) {
      return;
    }

    // Dense short ranges (1M/1D) need slightly stronger bars so volume stays visible.
    const denseRange = this.isDenseVolumeRange();
    const upColor = denseRange ? 'rgba(38, 166, 154, 0.85)' : 'rgba(38, 166, 154, 0.75)';
    const downColor = denseRange ? 'rgba(239, 83, 80, 0.85)' : 'rgba(239, 83, 80, 0.75)';

    // HistogramSeries requires strictly ascending unique times. Merge duplicates.
    const byTime = new Map<number, { time: any; value: number; color: string }>();
    for (const d of this.fullDataset) {
      const t = Math.floor(Number(d.time));
      if (!Number.isFinite(t) || t <= 0) continue;
      const value = Number(d.volume);
      const vol = Number.isFinite(value) && value > 0 ? value : 0;
      const color = d.close >= d.open ? upColor : downColor;
      const existing = byTime.get(t);
      if (existing) {
        existing.value += vol;
        existing.color = color;
      } else {
        byTime.set(t, { time: d.time, value: vol, color });
      }
    }

    const volumeData = Array.from(byTime.values()).sort(
      (a, b) => Number(a.time) - Number(b.time)
    );

    console.log('[CHART] Updating volume data:', volumeData.length, 'points');
    try {
      this.volumeSeries.setData(volumeData);
      this.volumeSeries.applyOptions({ visible: this.volumeVisible });
    } catch (error) {
      console.error('[CHART] Failed to apply volume histogram data:', error);
    }
    this.applyVolumeScaleOptions();
  }

  private isDenseVolumeRange(): boolean {
    const range = (this.currentInterval || this.state.selectedTimePeriod || '').toLowerCase();
    return range === '1d' || range === '5d' || range === '1m' || this.fullDataset.length > 200;
  }

  /** Keep the overlay volume scale after range/resize so 1M/intraday bars don't collapse. */
  private applyVolumeScaleOptions(): void {
    if (!this.chart || !this.volumeSeries) return;
    const top = this.isDenseVolumeRange() ? 0.78 : 0.85;
    try {
      this.chart.priceScale('volume').applyOptions({
        visible: this.volumeVisible,
        scaleMargins: {
          top,
          bottom: 0,
        },
      });
      this.volumeSeries.applyOptions({
        visible: this.volumeVisible,
        priceScaleId: 'volume',
      });
    } catch (error) {
      console.warn('[CHART] Could not apply volume scale options:', error);
    }
  }

  private calculateHeikinAshi(data: ChartData[]): ChartData[] {
    if (data.length === 0) return [];

    const heikinAshiData: ChartData[] = [];

    for (let i = 0; i < data.length; i++) {
      const current = data[i];

      if (i === 0) {
        // First candle: use regular OHLC
        heikinAshiData.push({
          time: current.time,
          open: current.open,
          high: current.high,
          low: current.low,
          close: (current.open + current.high + current.low + current.close) / 4,
          volume: current.volume
        });
      } else {
        const previous = heikinAshiData[i - 1];

        // Heikin-Ashi calculations
        const haClose = (current.open + current.high + current.low + current.close) / 4;
        const haOpen = (previous.open + previous.close) / 2;
        const haHigh = Math.max(current.high, haOpen, haClose);
        const haLow = Math.min(current.low, haOpen, haClose);

        heikinAshiData.push({
          time: current.time,
          open: haOpen,
          high: haHigh,
          low: haLow,
          close: haClose,
          volume: current.volume
        });
      }
    }

    return heikinAshiData;
  }

  private getSMAColor(period: number): string {
    switch (period) {
      case 20: return '#2962FF';
      case 50: return '#FF6B6B';
      case 100: return '#26a69a';
      case 200: return '#FFD700';
      default: return '#2962FF';
    }
  }

  private addMarketHoursShading(): void {
    if (!this.chart || this.fullDataset.length === 0) return;

    // Remove existing background series
    if (this.backgroundSeries) {
      this.chart.removeSeries(this.backgroundSeries);
      this.backgroundSeries = null;
    }

    // For proper vertical time-based shading, we'll use a different approach
    // Create multiple area series for different non-market hour periods
    this.addTimeBasedShading();
  }

  private addTimeBasedShading(): void {
    const firstTime = this.fullDataset[0].time as number;
    const lastTime = this.fullDataset[this.fullDataset.length - 1].time as number;

    // Get price range for full-height shading
    const prices = this.fullDataset.map(d => [d.high, d.low]).flat();
    const minPrice = Math.min(...prices);
    const maxPrice = Math.max(...prices);
    const priceRange = maxPrice - minPrice;
    const paddedMinPrice = minPrice - (priceRange * 0.05);
    const paddedMaxPrice = maxPrice + (priceRange * 0.05);

    // Generate shading periods for non-market hours
    const shadingPeriods = this.generateShadingPeriods(firstTime, lastTime);

    if (shadingPeriods.length > 0 && this.chart) {
      // Create a single background series with all shading data
      this.backgroundSeries = this.chart.addSeries(AreaSeries, {
        lineColor: 'transparent',
        topColor: 'rgba(128, 128, 128, 0.06)',
        bottomColor: 'rgba(128, 128, 128, 0.06)',
        priceLineVisible: false,
        lastValueVisible: false,
        crosshairMarkerVisible: false,
        priceScaleId: '',
      });

      // Create area data for non-market hours shading
      // We need to create continuous area data that doesn't have duplicate timestamps
      const areaData: { time: number; value: number }[] = [];

      // Create a continuous area that fills non-market periods
      for (const period of shadingPeriods) {
        // Add start point at minimum price
        areaData.push({ time: period.start, value: paddedMinPrice });
        // Add start point at maximum price (slightly offset to avoid duplicate times)
        areaData.push({ time: period.start + 1, value: paddedMaxPrice });
        // Add end point at maximum price (slightly offset)
        areaData.push({ time: period.end - 1, value: paddedMaxPrice });
        // Add end point at minimum price
        areaData.push({ time: period.end, value: paddedMinPrice });
      }

      // Sort by time to ensure proper ordering
      areaData.sort((a, b) => a.time - b.time);

      // Remove any duplicate timestamps that might still exist
      const uniqueAreaData = areaData.filter((item, index, arr) =>
        index === 0 || item.time !== arr[index - 1].time
      );

      this.backgroundSeries.setData(uniqueAreaData);

    }
  }

  private generateShadingPeriods(firstTime: number, lastTime: number): Array<{ start: number, end: number }> {
    const periods: Array<{ start: number, end: number }> = [];

    // Convert to Date objects for easier manipulation
    const startDate = new Date(firstTime * 1000);
    const endDate = new Date(lastTime * 1000);

    // Set time to start of first day
    const currentDate = new Date(startDate);
    currentDate.setHours(0, 0, 0, 0);

    // Generate shading periods for each day in the range
    while (currentDate <= endDate) {
      const dayStart = currentDate.getTime() / 1000;

      // Skip weekends (0 = Sunday, 6 = Saturday) - entire weekend is non-market
      if (currentDate.getDay() === 0 || currentDate.getDay() === 6) {
        const weekendStart = dayStart;
        const weekendEnd = dayStart + (24 * 60 * 60); // Full day

        if (weekendStart <= lastTime && weekendEnd >= firstTime) {
          periods.push({
            start: Math.max(weekendStart, firstTime),
            end: Math.min(weekendEnd, lastTime)
          });
        }

        currentDate.setDate(currentDate.getDate() + 1);
        continue;
      }

      // For weekdays, shade non-market hours:
      // Night: 8:00 PM (previous day) - 4:00 AM (current day)
      // Pre-market: 4:00 AM - 9:30 AM ET  
      // Post-market: 4:00 PM - 8:00 PM ET

      const preMarketStart = dayStart + (4 * 60 * 60); // 4:00 AM
      const marketStart = dayStart + (9 * 60 * 60) + (30 * 60); // 9:30 AM
      const marketEnd = dayStart + (16 * 60 * 60); // 4:00 PM
      const postMarketEnd = dayStart + (20 * 60 * 60); // 8:00 PM

      // Night hours (8 PM previous day to 4 AM current day)
      const nightStart = dayStart; // Start of day (midnight)
      if (nightStart <= lastTime && preMarketStart >= firstTime) {
        periods.push({
          start: Math.max(nightStart, firstTime),
          end: Math.min(preMarketStart, lastTime)
        });
      }

      // Pre-market hours (4:00 AM - 9:30 AM)
      if (preMarketStart <= lastTime && marketStart >= firstTime) {
        periods.push({
          start: Math.max(preMarketStart, firstTime),
          end: Math.min(marketStart, lastTime)
        });
      }

      // Post-market hours (4:00 PM - 8:00 PM)
      if (marketEnd <= lastTime && postMarketEnd >= firstTime) {
        periods.push({
          start: Math.max(marketEnd, firstTime),
          end: Math.min(postMarketEnd, lastTime)
        });
      }

      // Night hours after post-market (8 PM - midnight)
      const lateNightStart = postMarketEnd;
      const lateNightEnd = dayStart + (24 * 60 * 60); // End of day
      if (lateNightStart <= lastTime && lateNightEnd >= firstTime) {
        periods.push({
          start: Math.max(lateNightStart, firstTime),
          end: Math.min(lateNightEnd, lastTime)
        });
      }

      // Move to next day
      currentDate.setDate(currentDate.getDate() + 1);
    }

    return periods;
  }

  // Load chart data (load comprehensive dataset once)
  // DEPRECATED: All chart data now comes from socket - this method does nothing
  async loadChartData(symbol: string): Promise<void> {
    console.warn('[CHART] loadChartData called but all data comes from socket - ignoring');
    return;
  }

  // DEPRECATED: All chart data now comes from socket - this method does nothing
  async loadChartDataWithInterval(symbol: string, interval: string): Promise<void> {
    console.warn('[CHART] loadChartDataWithInterval called but all data comes from socket - ignoring');
    return;
  }

  // DEPRECATED: All chart data now comes from socket - this method does nothing
  async loadChartDataWithRangeAndInterval(symbol: string, timeRange: string, candleInterval: string): Promise<void> {
    console.warn('[CHART] loadChartDataWithRangeAndInterval called but all data comes from socket - ignoring');
    return;
  }

  private mapIntervalToAPIRange(interval: string): string {
    // This method maps time ranges to API ranges for data fetching
    // The interval parameter is actually a time range (1d, 5d, 1m, 6m, ytd, 1y, 5y, all)
    switch (interval) {
      case '1d':
      case '5d':
        return '1m'; // 1 month for recent short-term data
      case '1m': // This is actually 1 month time range, not 1 minute
        return '2m'; // 2 months (reduced from 3m to avoid too much data)
      case '6m':
        return '1y'; // 1 year to ensure we have 6 months
      case 'ytd':
        return '2y'; // 2 years to ensure we have full current year
      case '1y':
        return '2y'; // 2 years to ensure we have exact 1 year back
      case '5y':
        return '5y'; // 5 years
      case 'all':
        return 'max'; // Maximum available data
      default:
        return '2y'; // Default to 2 years for safety
    }
  }

  private async loadFullDataset(symbol: string, range: string = '5y', interval?: string, limit?: number): Promise<void> {
    // ALL chart data now comes from socket - this method should never be called
    // Keeping for backward compatibility but it will not load any data
    console.warn('[CHART] loadFullDataset called but all data comes from socket - ignoring');
    return;
  }

  private normalizeRangeKey(range: string): string {
    let selected = (range || TradingViewChartService.DEFAULT_RANGE).toLowerCase().trim();
    const aliasMap: Record<string, string> = {
      '1 year': '1y', '1year': '1y', '1y': '1y',
      '5 year': '5y', '5year': '5y', '5y': '5y',
      '1 day': '1d', '1day': '1d', '1d': '1d',
      '5 day': '5d', '5day': '5d', '5d': '5d',
      '1 month': '1m', '1month': '1m', '1m': '1m',
      '6 month': '6m', '6month': '6m', '6m': '6m'
    };
    return aliasMap[selected] || selected;
  }

  /** Raw calendar bounds for a preset range (before snapping to loaded candles). */
  private computeRawVisibleRangeBounds(range: string): { fromSec: number; toSec: number } | null {
    const data = this.fullDatasetAll?.length ? this.fullDatasetAll : this.fullDataset;
    if (!data?.length) {
      return null;
    }

    const selected = this.normalizeRangeKey(range);
    const lastBarSec = Math.floor(Number(data[data.length - 1].time));
    const daySec = 24 * 3600;
    const nowSec = Math.floor(Date.now() / 1000);
    const anchorEnd = Math.min(nowSec, lastBarSec);
    const anchorDate = new Date(anchorEnd * 1000);

    let fromSec: number;
    let toSec: number;
    switch (selected) {
      case '1d':
        fromSec = anchorEnd - 1 * daySec;
        toSec = anchorEnd;
        break;
      case '5d':
        fromSec = anchorEnd - 5 * daySec;
        toSec = anchorEnd;
        break;
      case '1m':
        fromSec = anchorEnd - 31 * daySec;
        toSec = anchorEnd;
        break;
      case '6m':
        fromSec = anchorEnd - 182 * daySec;
        toSec = anchorEnd;
        break;
      case 'ytd':
        // Use the data's year (not only the clock) so a lagged last bar still frames YTD correctly.
        fromSec = Math.floor(Date.UTC(anchorDate.getUTCFullYear(), 0, 1) / 1000);
        toSec = anchorEnd;
        break;
      case '1y':
        fromSec = anchorEnd - 365 * daySec;
        toSec = anchorEnd;
        break;
      case '2y':
        fromSec = anchorEnd - (2 * 365) * daySec;
        toSec = anchorEnd;
        break;
      case '5y':
        fromSec = anchorEnd - (5 * 365) * daySec;
        toSec = anchorEnd;
        break;
      case 'all':
      case 'max':
        fromSec = anchorEnd - (2 * 365) * daySec;
        toSec = anchorEnd;
        break;
      default:
        fromSec = anchorEnd - 365 * daySec;
        toSec = anchorEnd;
        break;
    }

    return { fromSec, toSec };
  }

  /** Count loaded candles strictly before a unix timestamp (seconds). */
  public countBarsBeforeTimestamp(targetSec: number): number {
    const data = this.fullDatasetAll;
    if (!data?.length || !Number.isFinite(targetSec)) {
      return 0;
    }

    let left = 0;
    let right = data.length;
    while (left < right) {
      const mid = (left + right) >>> 1;
      if (Math.floor(Number(data[mid].time)) < targetSec) {
        left = mid + 1;
      } else {
        right = mid;
      }
    }
    return left;
  }

  /** True when enough history exists before the visible window for indicator warm-up. */
  public hasSufficientIndicatorWarmup(maxLookback: number, range?: string): boolean {
    if (maxLookback <= 0) {
      return true;
    }

    const bounds = this.computeRawVisibleRangeBounds(
      range || this.currentInterval || this.state.selectedTimePeriod || TradingViewChartService.DEFAULT_RANGE
    );
    if (!bounds) {
      return false;
    }

    return this.countBarsBeforeTimestamp(bounds.fromSec) >= maxLookback;
  }

  public getLoadedBarCount(): number {
    return this.fullDatasetAll?.length ?? 0;
  }

  public reapplyVisibleTimeRange(range?: string): void {
    const selectedRange = range || this.currentInterval || this.state.selectedTimePeriod || TradingViewChartService.DEFAULT_RANGE;
    this.viewportApplyToken++;
    this.setVisibleTimeRange(selectedRange);
    this.presetViewportApplied = true;
  }

  /**
   * Zoom the viewport to the selected preset. All bars stay in the series; only the visible window changes.
   * Anchored to the current date so the chart always shows the exact selected time range regardless of
   * how much historical data is loaded. Missing boundary dates (weekends / holidays) snap to the
   * nearest previous available candle.
   */
  private setVisibleTimeRange(range: string): void {
    if (!this.chart || this.fullDataset.length === 0) {
      console.warn('[CHART] setVisibleTimeRange: Chart or dataset not available');
      return;
    }

    const timeScale = this.chart.timeScale();
    if (!timeScale) {
      console.warn('[CHART] setVisibleTimeRange: TimeScale not available');
      return;
    }

    const rawBounds = this.computeRawVisibleRangeBounds(range);
    if (!rawBounds) {
      console.warn('[CHART] setVisibleTimeRange: Could not compute range bounds');
      return;
    }

    const lastBarSec = Math.floor(Number(this.fullDataset[this.fullDataset.length - 1].time));
    const firstBarSec = Math.floor(Number(this.fullDataset[0].time));
    const daySec = 24 * 3600;
    let fromSec = rawBounds.fromSec;
    let toSec = rawBounds.toSec;

    // Snap boundaries to nearest available candle (weekends/holidays → nearest previous candle).
    fromSec = this.findNearestPreviousTimestamp(fromSec);
    toSec = this.findNearestPreviousTimestamp(toSec);

    // Safety: never go past the oldest candle we actually have.
    if (fromSec < firstBarSec) {
      fromSec = firstBarSec;
    }
    // Safety: ensure toSec is strictly after fromSec.
    if (toSec <= fromSec) {
      toSec = Math.min(lastBarSec, fromSec + daySec);
    }

    const lastIndex = this.fullDataset.length - 1;
    let fromIndex = 0;
    let toIndex = lastIndex;
    for (let i = 0; i < this.fullDataset.length; i++) {
      const t = Math.floor(Number(this.fullDataset[i].time));
      if (t < fromSec) {
        fromIndex = i + 1;
      }
      if (t <= toSec) {
        toIndex = i;
      }
    }
    if (fromIndex > toIndex) {
      fromIndex = Math.max(0, lastIndex);
      toIndex = lastIndex;
    }

    const rightOffset = 6;
    const chartWidth = Math.max(timeScale.width() || 0, this.chartHostContainer?.clientWidth || 0, 800);
    const maxBarSpacing = 16;
    const minLogicalSpan = Math.max(40, Math.ceil(chartWidth / maxBarSpacing));
    const rangeEnd = toIndex + rightOffset;
    // If only a few bars fall in YTD/1Y, do not stretch them across the calendar window.
    // Negative `from` leaves empty space on the left so candles keep a normal width.
    const logicalFrom = Math.min(fromIndex, rangeEnd - minLogicalSpan);

    try {
      this.chart?.applyOptions({
        timeScale: {
          rightOffset,
          fixRightEdge: false,
          shiftVisibleRangeOnNewBar: false
        }
      });
    } catch (_) { /* noop */ }

    const applyToken = this.viewportApplyToken;
    const applyRange = () => {
      if (applyToken !== this.viewportApplyToken) {
        return;
      }
      try {
        timeScale.setVisibleLogicalRange({
          from: logicalFrom,
          to: rangeEnd
        });
      } catch (error) {
        console.error('[CHART] Error setting visible time range:', error);
      }
    };

    // Run now AND on the next frame — the timescale sometimes ignores the first call
    // if the chart hasn't finished layout yet (width === 0 on very first render).
    applyRange();
    requestAnimationFrame(() => {
      applyRange();
      this.applyVolumeScaleOptions();
    });
    setTimeout(() => {
      applyRange();
      this.applyVolumeScaleOptions();
    }, 50);
  }

  private parseVolumeValue(raw: unknown): number {
    if (typeof raw === 'number' && Number.isFinite(raw)) {
      return Math.max(0, raw);
    }
    if (typeof raw === 'string') {
      const cleaned = raw.replace(/,/g, '').trim();
      if (!cleaned) return 0;
      const parsed = Number(cleaned);
      return Number.isFinite(parsed) ? Math.max(0, parsed) : 0;
    }
    return 0;
  }

  /**
   * Binary-search the dataset for the closest candle at or before `targetSec`.
   * Used when the requested boundary (e.g. "today" or "Jan 1") falls on a non-trading day.
   */
  private findNearestPreviousTimestamp(targetSec: number): number {
    if (!this.fullDataset.length) return targetSec;

    const firstT = Math.floor(Number(this.fullDataset[0].time));
    const lastT = Math.floor(Number(this.fullDataset[this.fullDataset.length - 1].time));
    if (targetSec <= firstT) return firstT;
    if (targetSec >= lastT) return lastT;

    let left = 0;
    let right = this.fullDataset.length - 1;
    let best = firstT;

    while (left <= right) {
      const mid = (left + right) >>> 1;
      const current = Math.floor(Number(this.fullDataset[mid].time));
      if (current <= targetSec) {
        best = current;
        left = mid + 1;
      } else {
        right = mid - 1;
      }
    }

    return best;
  }



  /**
   * Set chart data from socket payload (historical or live).
   * Normalizes common payload shapes (array, { data: [] }, { result: [] }) to ChartData[].
   */
  setChartDataFromSocket(rawPayload: any, symbol?: string, timeRange?: string): void {
    console.log('[CHART] setChartDataFromSocket called with payload:', rawPayload);
    const data = this.normalizeSocketChartData(rawPayload);
    if (!data || data.length === 0) {
      console.warn('[CHART] No valid chart data in socket payload after normalization');
      return;
    }

    const incomingSymbol = (symbol || rawPayload?.symbol || '').toString().toUpperCase();
    const incomingInterval = timeRange || rawPayload?.range || rawPayload?.timeframe || this.currentInterval;

    // Always clear history when symbol or interval changes so we never merge old + new data
    const symbolChanged = !!incomingSymbol && !!this.currentSymbol && incomingSymbol !== this.currentSymbol;
    const intervalChanged = !!incomingInterval && !!this.currentInterval && incomingInterval !== this.currentInterval;
    const isFreshLoad = !this.currentSymbol || symbolChanged || intervalChanged;
    if (isFreshLoad) {
      console.log(`[CHART] Fresh load (symbol: ${this.currentSymbol}→${incomingSymbol}, interval: ${this.currentInterval}→${incomingInterval}) — clearing history`);
      this.presetViewportApplied = false;
      this.viewportApplyToken++;
      this.fullDataset = [];
      this.fullDatasetAll = [];
      this.resetTimeScaleForNewData();
      if (this.mainSeries) { try { this.mainSeries.setData([]); } catch (_) { } }
      if (this.volumeSeries) { try { this.volumeSeries.setData([]); } catch (_) { } }
      this.clearAllIndicatorSeriesData();
    }

    // Bars already stored for this symbol (0 after a switch). Used to detect snapshot → full history.
    const previousBarCount = this.fullDatasetAll.length;

    // Ensure data is sorted in ascending order (oldest to newest)
    let sortedData = [...data].sort((a, b) => {
      const timeA = Number(a.time);
      const timeB = Number(b.time);
      return timeA - timeB;
    });

    sortedData = this.deduplicateChartData(sortedData);

    // A live snapshot (2-3 bars) must not replace a loaded YTD/1Y history — that
    // stretched those few candles across the whole range.
    if (
      !isFreshLoad &&
      this.fullDatasetAll.length >= 20 &&
      sortedData.length < Math.max(10, Math.floor(this.fullDatasetAll.length * 0.5))
    ) {
      console.warn(
        `[CHART] Ignoring undersized snapshot (${sortedData.length} bars) so it cannot replace ${this.fullDatasetAll.length} loaded bars`
      );
      this.mergeCandlesIntoDataset(sortedData);
      this.applyTimeRangeToDataset(this.currentInterval || this.state.selectedTimePeriod || TradingViewChartService.DEFAULT_RANGE);
      if (this.mainSeries) {
        this.updateMainSeriesData(false);
      }
      if (this.fullDataset.length > 0) {
        this.updateTickerDetails(this.fullDataset, symbol);
      }
      return;
    }

    // Set symbol and interval BEFORE updating ticker details
    if (symbol) {
      this.currentSymbol = symbol.toUpperCase();
      console.log('[CHART] Symbol set to:', this.currentSymbol);
    } else if (!this.currentSymbol && sortedData.length > 0) {
      // Try to get symbol from payload if not provided
      console.warn('[CHART] No symbol provided, using currentSymbol:', this.currentSymbol);
    }
    if (timeRange) {
      this.currentInterval = timeRange;
    }
    this.fullDatasetAll = sortedData;
    if (timeRange) {
      this.currentInterval = timeRange;
      this.state.selectedTimePeriod = timeRange;
    }

    // Apply selected range to determine what we render
    this.applyTimeRangeToDataset(this.currentInterval || this.state.selectedTimePeriod || TradingViewChartService.DEFAULT_RANGE);
    console.log('[CHART] Socket data stored in fullDatasetAll:', this.fullDatasetAll.length, 'points');
    console.log('[CHART] Display dataset (fullDataset):', this.fullDataset.length, 'points');
    console.log('[CHART] Display first (oldest):', this.fullDataset[0]);
    console.log('[CHART] Display last (newest):', this.fullDataset[this.fullDataset.length - 1]);
    console.log('[CHART] mainSeries exists:', !!this.mainSeries);
    console.log('[CHART] chart exists:', !!this.chart);

    // Update chart data - if chart is not initialized yet, wait a bit and retry
    // After a ticker switch, a 2–5 bar snapshot can land first. When the full
    // history follows, re-apply the selected range instead of restoring that zoom.
    const grewFromSparseSnapshot =
      !isFreshLoad &&
      previousBarCount > 0 &&
      previousBarCount < 80 &&
      sortedData.length >= Math.max(40, previousBarCount * 2);
    const shouldApplyViewport = isFreshLoad || !this.presetViewportApplied || grewFromSparseSnapshot;
    if (this.mainSeries) {
      console.log('[CHART] Chart is ready, applying data immediately');
      this.updateMainSeriesData(shouldApplyViewport);
      // MAs/indicators are re-painted inside updateMainSeriesData after candles + visible range.
      if (this.state.penDrawings.length > 0) {
        [100, 300, 600, 1200].forEach(delay => setTimeout(() => this.redrawAllPenDrawings(), delay));
      }
    } else {
      console.log('[CHART] Chart not initialized yet, waiting for initialization...');
      const maxRetries = 20;
      let retryCount = 0;

      const tryApplyData = () => {
        retryCount++;
        if (this.mainSeries && this.fullDataset.length > 0) {
          console.log('[CHART] Chart ready, applying socket data (attempt', retryCount, ')');
          this.updateMainSeriesData(shouldApplyViewport);
          if (this.state.penDrawings.length > 0) {
            [100, 400, 800].forEach(delay => setTimeout(() => this.redrawAllPenDrawings(), delay));
          }
        } else if (retryCount < maxRetries) {
          const delay = Math.min(100 * Math.pow(2, retryCount), 3000);
          console.log('[CHART] Retrying in', delay, 'ms (attempt', retryCount, '/', maxRetries, ')');
          setTimeout(tryApplyData, delay);
        } else {
          console.error('[CHART] Failed to apply socket data after', maxRetries, 'retries');
        }
      };

      setTimeout(tryApplyData, 100);
    }

    this.loadLinesFromLocalStorage();
    this.loadSystemLinesFromLocalStorage();

    // Update ticker details after data is set
    if (this.fullDataset.length > 0) {
      this.updateTickerDetails(this.fullDataset, symbol);
    }

  }

  private normalizeSocketChartData(payload: any): ChartData[] {
    if (!payload || typeof payload !== 'object') {
      console.warn('[CHART] Invalid socket payload:', payload);
      return [];
    }

    let arr: any[] = [];
    if (Array.isArray(payload)) {
      arr = payload;
    } else if (payload.data && Array.isArray(payload.data)) {
      arr = payload.data;
    } else if (payload.candles && Array.isArray(payload.candles)) {
      arr = payload.candles;
    } else if (payload.ohlc && Array.isArray(payload.ohlc)) {
      arr = payload.ohlc;
    } else if (payload.result && Array.isArray(payload.result)) {
      arr = payload.result;
    } else if (payload.body?.data && Array.isArray(payload.body.data)) {
      arr = payload.body.data;
    } else if (payload.body && typeof payload.body === 'object') {
      arr = Object.values(payload.body);
    }

    console.log('[CHART] Normalizing socket data, array length:', arr.length);

    // Convert array to ChartData format - handle various time formats like MarketDataService does
    const data = arr.map((item: any): ChartData | null => {
      if (!item || typeof item !== 'object') {
        return null;
      }

      // Handle different time formats
      let timeValue: number;
      if (typeof item.timestamp_unix === 'number' && !isNaN(item.timestamp_unix)) {
        timeValue = item.timestamp_unix;
      } else if (typeof item.date_utc === 'number' && !isNaN(item.date_utc)) {
        timeValue = item.date_utc;
      } else if (typeof item.timestamp === 'number' && !isNaN(item.timestamp)) {
        timeValue = item.timestamp;
      } else if (item.date && typeof item.date === 'string') {
        const dateValue = new Date(item.date);
        timeValue = isNaN(dateValue.getTime()) ? 0 : dateValue.getTime() / 1000;
      } else if (item.time && typeof item.time === 'string') {
        const timeDate = new Date(item.time);
        timeValue = isNaN(timeDate.getTime()) ? 0 : timeDate.getTime() / 1000;
      } else if (item.time && typeof item.time === 'number') {
        timeValue = item.time;
      } else {
        console.warn('[CHART] No valid time field found in item:', item);
        return null;
      }

      timeValue = this.toUnixSeconds(timeValue);
      if (!this.isValidChartTimestamp(timeValue)) {
        console.warn('[CHART] Invalid time value:', timeValue, 'for item:', item);
        return null;
      }

      // Parse numeric values with fallbacks (support camelCase, PascalCase, shorthand)
      const open = parseFloat(item.open ?? item.Open ?? item.o ?? '0');
      const high = parseFloat(item.high ?? item.High ?? item.h ?? '0');
      const low = parseFloat(item.low ?? item.Low ?? item.l ?? '0');
      const close = parseFloat(item.close ?? item.Close ?? item.c ?? '0');
      const volume = this.parseVolumeValue(
        item.volume ?? item.Volume ?? item.v ?? item.vol ?? item.Vol ?? item.qv ?? 0
      );

      // Invalid volume must not drop the candle — only OHLC is required to render.
      if (isNaN(open) || isNaN(high) || isNaN(low) || isNaN(close)) {
        console.warn('[CHART] Invalid price values for item:', item);
        return null;
      }

      return {
        time: timeValue as any,
        open: open,
        high: high,
        low: low,
        close: close,
        volume: volume
      } as ChartData;
    }).filter((item: ChartData | null): item is ChartData => item !== null);

    // Sort ascending; duplicate unix seconds are merged in deduplicateChartData (required by lightweight-charts)
    const sorted = [...data].sort((a, b) => Number(a.time) - Number(b.time));

    console.log('[CHART] Normalized socket data:', sorted.length, 'valid data points');
    if (sorted.length > 0) {
      console.log('[CHART] First data point:', sorted[0]);
      console.log('[CHART] Last data point:', sorted[sorted.length - 1]);
    }

    if (sorted.length === 0) {
      console.warn('[CHART] No valid data points after normalization. Raw payload:', payload);
      return [];
    }

    return sorted;
  }

  /**
   * Normalize chart data for lightweight-charts:
   * - unix seconds only
   * - sort ascending
   * - merge duplicate times (and same-day bars for daily data)
   */
  private deduplicateChartData(data: ChartData[]): ChartData[] {
    if (!data || data.length === 0) return data;

    const sorted = data
      .map((candle) => ({ ...candle, time: this.toUnixSeconds(Number(candle.time)) as any }))
      .filter((candle) => this.isValidChartTimestamp(Number(candle.time)))
      .sort((a, b) => Number(a.time) - Number(b.time));

    const periodSec = this.inferMinBarPeriodSec(sorted);
    const byTime = new Map<number, ChartData>();
    for (const candle of sorted) {
      const t = this.bucketTimeSec(Number(candle.time), periodSec);
      const existing = byTime.get(t);
      if (!existing) {
        byTime.set(t, { ...candle, time: t as any });
        continue;
      }
      existing.high = Math.max(existing.high, candle.high);
      existing.low = Math.min(existing.low, candle.low);
      existing.close = candle.close;
      existing.volume = candle.volume || existing.volume || 0;
    }

    return Array.from(byTime.values()).sort((a, b) => Number(a.time) - Number(b.time));
  }

  private toUnixSeconds(timeValue: number): number {
    if (!Number.isFinite(timeValue) || timeValue <= 0) return 0;
    if (timeValue > 1e14) return Math.floor(timeValue / 1e6);
    if (timeValue > 1e11) return Math.floor(timeValue / 1e3);
    return Math.floor(timeValue);
  }

  /** Reject year-like numbers, milliseconds leftovers, and timestamps too far in the future. */
  private isValidChartTimestamp(timeSec: number): boolean {
    const sec = this.toUnixSeconds(timeSec);
    if (sec < 631_152_000) return false;
    const maxSec = Math.floor(Date.now() / 1000) + (2 * 365 * 24 * 3600);
    return sec <= maxSec;
  }

  private inferMinBarPeriodSec(data: ChartData[]): number {
    if (!data || data.length < 3) return 0;
    const sampleTo = Math.min(data.length, 80);
    const gaps: number[] = [];
    for (let i = 1; i < sampleTo; i++) {
      const gap = Number(data[i].time) - Number(data[i - 1].time);
      if (Number.isFinite(gap) && gap > 0) {
        gaps.push(gap);
      }
    }
    if (!gaps.length) return 0;
    gaps.sort((a, b) => a - b);
    return gaps[Math.floor(gaps.length / 2)];
  }

  private bucketTimeSec(timeSec: number, periodSec: number): number {
    const sec = this.toUnixSeconds(timeSec);
    if (periodSec >= 20 * 24 * 3600) {
      const d = new Date(sec * 1000);
      return Math.floor(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), 1) / 1000);
    }
    if (periodSec >= 6 * 24 * 3600) {
      const d = new Date(sec * 1000);
      const diff = (d.getUTCDay() + 6) % 7;
      return Math.floor(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate() - diff) / 1000);
    }
    if (periodSec >= 12 * 3600) {
      return Math.floor(sec / 86400) * 86400;
    }
    return sec;
  }

  private mergeCandlesIntoDataset(updateData: ChartData[]): void {
    const existingDataMap = new Map<number, ChartData>();
    this.fullDataset.forEach((candle) => {
      existingDataMap.set(Math.floor(Number(candle.time)), candle);
    });

    updateData.forEach((updateCandle: ChartData) => {
      const updateTime = Math.floor(Number(updateCandle.time));
      const existingCandle = existingDataMap.get(updateTime);
      if (existingCandle) {
        existingCandle.high = Math.max(existingCandle.high, updateCandle.high);
        existingCandle.low = Math.min(existingCandle.low, updateCandle.low);
        existingCandle.close = updateCandle.close;
        existingCandle.volume = updateCandle.volume || existingCandle.volume || 0;
      } else {
        const normalized = { ...updateCandle, time: updateTime as any };
        this.fullDataset.push(normalized);
        this.fullDatasetAll.push(normalized);
        existingDataMap.set(updateTime, normalized);
      }
    });

    this.fullDataset = this.deduplicateChartData(this.fullDataset);
    this.fullDatasetAll = this.deduplicateChartData(this.fullDatasetAll);
  }

  /**
   * Build `fullDataset` from everything we received on the socket (`fullDatasetAll`).
   * We do not trim by the selected timeframe — that only affects the viewport via `setTimeRange` /
   * `setVisibleTimeRangeForSelection` so all bars remain in the series.
   */
  private applyTimeRangeToDataset(_range: string): void {
    // Do not drop socket datapoints by timestamp.
    // The selected range affects the visible window (timeScale), not what we store/render.
    const data = this.fullDatasetAll;
    if (!data || data.length === 0) {
      this.fullDataset = [];
      return;
    }

    this.fullDataset = this.deduplicateChartData(data);
  }

  /** Expected trading days per range for daily candles (Yahoo Finance–style). Used to validate dataset. */
  private static readonly EXPECTED_DAILY_BARS: Record<string, { min: number; max: number; label: string }> = {
    '1d': { min: 1, max: 2, label: '1D' },
    '5d': { min: 4, max: 8, label: '5D' },
    '1m': { min: 18, max: 35, label: '1M' },
    '6m': { min: 100, max: 150, label: '6M' },
    'ytd': { min: 1, max: 365, label: 'YTD' },
    '1y': { min: 200, max: 320, label: '1Y (~252 trading days)' },
    '5y': { min: 1000, max: 1400, label: '5Y (~1260 trading days)' }
  };

  private validateDatasetBarCount(range: string, barCount: number): void {
    const expected = TradingViewChartService.EXPECTED_DAILY_BARS[range];
    if (!expected || barCount === 0) return;
    if (barCount < expected.min || barCount > expected.max) {
      console.warn(
        `[CHART] Dataset bar count may be off for ${expected.label}: got ${barCount} bars (expected roughly ${expected.min}-${expected.max}). Validate against a source like Yahoo Finance if needed.`
      );
    }
  }

  /**
   * Handle real-time chart updates (single candle or array of candles)
   * Updates the last candle if time matches, or appends new candle(s)
   */
  updateChartDataFromSocket(rawPayload: any): void {
    const payloadSymbol = (rawPayload?.symbol || '').toString().toUpperCase();
    if (payloadSymbol && this.currentSymbol && payloadSymbol !== this.currentSymbol) {
      console.warn('[CHART] Ignoring chart:update for different symbol:', payloadSymbol, 'current:', this.currentSymbol);
      return;
    }

    let updateData = this.normalizeSocketChartData(rawPayload);
    if (!updateData || updateData.length === 0) {
      console.warn('[CHART] No valid update data in socket payload');
      return;
    }

    updateData = this.deduplicateChartData(updateData);

    // A 5-bar live snapshot must never replace loaded YTD/1Y history.
    if (this.fullDatasetAll.length >= 20 && updateData.length < Math.max(10, Math.floor(this.fullDatasetAll.length * 0.5))) {
      this.mergeCandlesIntoDataset(updateData);
      this.updateMainSeriesData(false);
      this.updateTickerDetails(this.fullDataset);
      this.renderIndicators();
      return;
    }

    if (this.fullDataset.length === 0) {
      // If no existing data, treat update as initial data
      this.setChartDataFromSocket(rawPayload);
      return;
    }

    this.mergeCandlesIntoDataset(updateData);

    console.log('[CHART] Chart data updated via socket:', this.fullDataset.length, 'points');
    console.log('[CHART] First data point:', this.fullDataset[0]);
    console.log('[CHART] Last data point:', this.fullDataset[this.fullDataset.length - 1]);

    // Preserve the user's current pan/zoom — setData resets the timescale.
    this.updateMainSeriesData(false);

    // Update ticker details with latest data
    this.updateTickerDetails(this.fullDataset);

    // Re-render indicators with updated data
    this.renderIndicators();
  }

  /**
   * Merge older historical candles into the current full dataset.
   * Returns number of newly-added bars after deduplication by timestamp.
   */
  prependHistoricalDataFromSocket(rawPayload: any): number {
    let olderData = this.normalizeSocketChartData(rawPayload);
    if (!olderData || olderData.length === 0) return 0;

    olderData = this.deduplicateChartData(olderData);

    const beforeCount = this.fullDatasetAll.length;
    const mergedByTime = new Map<number, ChartData>();

    this.fullDatasetAll.forEach((candle) => {
      mergedByTime.set(Math.floor(Number(candle.time)), candle);
    });
    olderData.forEach((candle) => {
      mergedByTime.set(Math.floor(Number(candle.time)), candle);
    });

    this.fullDatasetAll = this.deduplicateChartData(Array.from(mergedByTime.values()));

    this.applyTimeRangeToDataset(this.currentInterval || this.state.selectedTimePeriod || TradingViewChartService.DEFAULT_RANGE);
    const grewFromSparseSnapshot =
      beforeCount > 0 &&
      beforeCount < 80 &&
      this.fullDatasetAll.length >= Math.max(40, beforeCount * 2);
    this.updateMainSeriesData(grewFromSparseSnapshot);
    this.renderIndicators();

    return Math.max(0, this.fullDatasetAll.length - beforeCount);
  }

  private calculateSMA(data: ChartData[], period: number): { time: Time; value: number }[] {
    const smaData: { time: Time; value: number }[] = [];
    for (let i = period - 1; i < data.length; i++) {
      const sum = data.slice(i - period + 1, i + 1).reduce((acc, curr) => acc + curr.close, 0);
      smaData.push({
        time: data[i].time,
        value: sum / period
      });
    }
    return smaData;
  }

  private calculateEMA(data: ChartData[], period: number): { time: Time; value: number }[] {
    if (data.length < period) return [];

    const emaData: { time: Time; value: number }[] = [];
    const multiplier = 2 / (period + 1);

    // Start with SMA for the first value
    const firstSlice = data.slice(0, period);
    const firstSMA = firstSlice.reduce((acc, item) => acc + item.close, 0) / period;

    emaData.push({
      time: data[period - 1].time,
      value: firstSMA
    });

    // Calculate EMA for the rest
    for (let i = period; i < data.length; i++) {
      const previousEMA = emaData[emaData.length - 1].value;
      const currentPrice = data[i].close;
      const emaValue = (currentPrice * multiplier) + (previousEMA * (1 - multiplier));

      emaData.push({
        time: data[i].time,
        value: emaValue
      });
    }

    return emaData;
  }

  private calculateATR(data: ChartData[], period: number): { time: Time; value: number }[] {
    if (data.length < period + 1) return [];

    const atrData: { time: Time; value: number }[] = [];
    const atrPercentages: number[] = [];

    // Calculate IBD's ATR for each period
    for (let i = 1; i < data.length; i++) {
      const current = data[i];
      const previous = data[i - 1];

      // IBD's ATR calculation: (High - Low) / Previous Close
      // But with gap handling for better representation
      let trueRange: number;

      // Check for significant gaps (more than 5% from previous close)
      const gapUpThreshold = previous.close * 1.05; // 5% above previous close
      const gapDownThreshold = previous.close * 0.95; // 5% below previous close

      if (current.low > gapUpThreshold) {
        // Significant gap up: use previous close instead of intraday low
        trueRange = current.high - previous.close;
      } else if (current.high < gapDownThreshold) {
        // Significant gap down: use previous close instead of intraday high
        trueRange = previous.close - current.low;
      } else {
        // Normal trading: use high - low
        trueRange = current.high - current.low;
      }

      // Convert to percentage of previous close (IBD's method)
      const atrPercentage = (trueRange / previous.close) * 100;
      atrPercentages.push(atrPercentage);
    }

    // Calculate 21-day moving average of ATR percentages
    if (atrPercentages.length >= period) {
      // Initial ATR (simple average of first 'period' values)
      let sum = 0;
      for (let i = 0; i < period; i++) {
        sum += atrPercentages[i];
      }
      const initialATR = sum / period;

      atrData.push({
        time: data[period].time, // Start from period+1 since we need previous close
        value: initialATR
      });

      // Calculate subsequent ATR values using simple moving average
      // (IBD uses moving average, not Wilder's smoothing)
      for (let i = period; i < atrPercentages.length; i++) {
        let sum = 0;
        for (let j = i - period + 1; j <= i; j++) {
          sum += atrPercentages[j];
        }
        const currentATR = sum / period;

        atrData.push({
          time: data[i + 1].time, // +1 because atrPercentages starts from index 1
          value: currentATR
        });
      }
    }

    return atrData;
  }

  private applyATRBasedCandlestickColoring(data: ChartData[], atrData: { time: Time; value: number }[], period: number): void {
    if (!this.mainSeries || atrData.length === 0) return;

    // Create a map of ATR values by time for quick lookup
    const atrMap = new Map<string, number>();
    atrData.forEach(atr => {
      atrMap.set(atr.time.toString(), atr.value);
    });

    // Process candlestick data and optionally highlight days exceeding ATR
    const coloredCandlestickData = data.map((candle, index) => {
      // Skip first few candles that don't have ATR data
      if (index === 0) return candle;

      const previous = data[index - 1];
      const atrValue = atrMap.get(candle.time.toString());

      if (!atrValue || !previous) return candle;

      // Calculate the actual daily price change percentage (close vs previous close)
      const dailyChangePercent = Math.abs(((candle.close - previous.close) / previous.close) * 100);

      // If the daily price change exceeds the ATR threshold, lightly highlight with a border
      if (dailyChangePercent > atrValue) {
        return {
          ...candle,
          borderColor: '#FFB300'   // Amber border highlight, keep normal body/wick colors
        };
      }

      return candle;
    });

    // Update the main series with colored data
    this.mainSeries.setData(coloredCandlestickData);
  }

  private resetCandlestickColors(): void {
    if (!this.mainSeries || this.fullDataset.length === 0) return;

    // Reset candlestick data to original colors (remove custom color properties)
    const resetData = this.fullDataset.map(candle => {
      // Create a new object without color properties to reset to default colors
      const { color, wickColor, borderColor, ...resetCandle } = candle as any;
      return resetCandle;
    });

    // Update the main series with reset data
    this.mainSeries.setData(resetData);
  }

  private updateTickerDetails(data: ChartData[], symbolOverride?: string): void {
    if (!data || data.length === 0) {
      console.warn('[CHART] updateTickerDetails: No data available');
      return;
    }

    // Get the latest data point
    const latest = data[data.length - 1];

    // Get previous data point for change calculation (if available)
    const previous = data.length >= 2 ? data[data.length - 2] : null;

    // Calculate change and percentage change
    let change = 0;
    let changePercent = 0;
    let previousClose = latest.close;

    if (previous) {
      previousClose = previous.close;
      change = latest.close - previous.close;
      changePercent = previous.close !== 0 ? (change / previous.close) * 100 : 0;
    }

    // Calculate average volume over the last 30 trading days (or available data)
    const volumeData = data.slice(-30).filter(d => d.volume && d.volume > 0);
    let averageVolume: number | undefined;
    let volumeChange: number | undefined;
    let volumeChangePercent: number | undefined;

    if (volumeData.length >= 10) { // Only calculate if we have enough data
      const totalVolume = volumeData.reduce((sum, d) => sum + (d.volume || 0), 0);
      averageVolume = totalVolume / volumeData.length;

      if (latest.volume && averageVolume > 0) {
        volumeChange = latest.volume - averageVolume;
        volumeChangePercent = (volumeChange / averageVolume) * 100;
      }
    }

    // Ensure we have a symbol - prioritize override
    const symbol = symbolOverride || this.currentSymbol || '';
    const companyName = this.getCompanyName(symbol);

    const lastUpdated = this.getDateFromChartTime(latest.time) ?? new Date();

    // Create ticker details object
    const tickerDetails: TickerDetails = {
      // Use fallback if symbol is empty, but getCompanyName handles empty logic too
      symbol: symbol,
      companyName: companyName,
      open: latest.open,
      high: latest.high,
      low: latest.low,
      close: latest.close,
      volume: latest.volume || 0,
      previousClose: previousClose,
      change: change,
      changePercent: changePercent,
      lastUpdated: lastUpdated,
      // Use default 0 if undefined for strict compatibility with interface
      averageVolume: averageVolume || 0,
      volumeChange: volumeChange || 0,
      volumeChangePercent: volumeChangePercent || 0
    };

    console.log('[CHART] Ticker details updated:', tickerDetails);

    // Notify the component via callback
    if (this.onTickerDetailsChangedCallback) {
      this.onTickerDetailsChangedCallback(tickerDetails);
    } else {
      console.warn('[CHART] Ticker details callback not registered');
    }
  }

  /**
   * Convert lightweight-charts `Time` into Date.
   * Socket candles typically use unix seconds (e.g. 1683676800).
   */
  private getDateFromChartTime(time: Time | undefined): Date | null {
    if (time === undefined || time === null) {
      return null;
    }

    if (typeof time === 'number') {
      const ms = time > 1e12 ? time : time * 1000;
      const date = new Date(ms);
      if (isNaN(date.getTime())) {
        return null;
      }
      // For date-only candles (00:00:00 UTC), keep the trading-session date
      // and map it to market close in configured timezone to avoid day-shift (Apr 2 -> Apr 1).
      if (
        date.getUTCHours() === 0 &&
        date.getUTCMinutes() === 0 &&
        date.getUTCSeconds() === 0
      ) {
        const marketTz = environment.defaultTimezone || 'America/New_York';
        return moment.tz({
          year: date.getUTCFullYear(),
          month: date.getUTCMonth(),
          day: date.getUTCDate(),
          hour: 16,
          minute: 0,
          second: 0
        }, marketTz).toDate();
      }
      return date;
    }

    if (typeof time === 'string') {
      const date = new Date(time);
      return isNaN(date.getTime()) ? null : date;
    }

    if (typeof time === 'object' && 'year' in time && 'month' in time && 'day' in time) {
      const y = Number((time as any).year);
      const m = Number((time as any).month);
      const d = Number((time as any).day);
      if (!Number.isFinite(y) || !Number.isFinite(m) || !Number.isFinite(d)) {
        return null;
      }
      const date = new Date(Date.UTC(y, m - 1, d));
      return isNaN(date.getTime()) ? null : date;
    }

    return null;
  }

  private getCompanyName(symbol: string): string {
    if (!symbol || symbol === 'UNKNOWN') {
      return ''; // Return empty string instead of "UNKNOWN Corporation"
    }

    // Common company names for popular tickers
    const companyNames: { [key: string]: string } = {
      'AAPL': 'Apple Inc.',
      'MSFT': 'Microsoft Corporation',
      'GOOGL': 'Alphabet Inc.',
      'GOOG': 'Alphabet Inc.',
      'AMZN': 'Amazon.com Inc.',
      'TSLA': 'Tesla Inc.',
      'META': 'Meta Platforms Inc.',
      'NVDA': 'NVIDIA Corporation',
      'NFLX': 'Netflix Inc.',
      'AMD': 'Advanced Micro Devices Inc.',
      'INTC': 'Intel Corporation',
      'CRM': 'Salesforce Inc.',
      'ORCL': 'Oracle Corporation',
      'ADBE': 'Adobe Inc.',
      'PYPL': 'PayPal Holdings Inc.',
      'DIS': 'The Walt Disney Company',
      'BA': 'The Boeing Company',
      'KO': 'The Coca-Cola Company',
      'PEP': 'PepsiCo Inc.',
      'WMT': 'Walmart Inc.',
      'JNJ': 'Johnson & Johnson',
      'PG': 'The Procter & Gamble Company',
      'V': 'Visa Inc.',
      'MA': 'Mastercard Incorporated',
      'UNH': 'UnitedHealth Group Incorporated',
      'HD': 'The Home Depot Inc.',
      'BAC': 'Bank of America Corporation',
      'JPM': 'JPMorgan Chase & Co.',
      'XOM': 'Exxon Mobil Corporation',
      'CVX': 'Chevron Corporation',
      'SPY': 'SPDR S&P 500 ETF Trust',
      'QQQ': 'Invesco QQQ Trust',
      'IWM': 'iShares Russell 2000 ETF',
      'DIA': 'SPDR Dow Jones Industrial Average ETF Trust',
      'GLD': 'SPDR Gold Shares',
      'SLV': 'iShares Silver Trust',
      'USO': 'United States Oil Fund, LP',
      'TLT': 'iShares 20+ Year Treasury Bond ETF'
    };

    const normalizedSymbol = symbol.toUpperCase();

    // Return the mapped name, or just the symbol if not found
    // Don't add "Corporation" suffix blindly as it's often incorrect (e.g. for ETFs, Crypto, Indices)
    return companyNames[normalizedSymbol] || normalizedSymbol;
  }

  // Load calendar events
  async loadCalendarEvents(symbol: string): Promise<void> {
    try {

      // Use MarketDataService to load calendar events with caching
      const markers = await this.marketDataService.loadCalendarEvents(symbol);

      if (markers && markers.length > 0) {
        this.updateMarkers(markers);
      } else {
      }
    } catch (error: any) {
      // Suppress 404 errors
      if (error.status !== 404) {
      }
    }
  }



  private updateMarkers(markers: ChartMarker[]): void {
    this.state.markers = markers;
    this.refreshMarkersOnChart();
  }

  private refreshMarkersOnChart(): void {
    if (this.mainSeries) {
      const visibleMarkers = this.state.markers.filter(marker =>
        this.state.markerVisibility[marker.category]
      );

      // Convert our markers to TradingView SeriesMarker format
      const tvMarkers = visibleMarkers.map(marker => ({
        time: marker.time as Time,
        position: marker.position as 'aboveBar' | 'belowBar' | 'inBar',
        color: marker.color,
        shape: marker.shape as 'circle' | 'square' | 'arrowUp' | 'arrowDown',
        text: marker.text || '',
        size: marker.size || 1
      }));


      // Clear all markers first by setting empty array, then set new markers
      createSeriesMarkers(this.mainSeries, []);

      // Small delay to ensure clearing takes effect
      setTimeout(() => {
        if (this.mainSeries && tvMarkers.length > 0) {
          createSeriesMarkers(this.mainSeries, tvMarkers);
        }
      }, 50);
    }
  }

  // Helper methods to check indicator visibility (bridges old and new system)
  private isMovingAverageVisible(period: number): boolean {
    return this.state.indicators.some(indicator =>
      indicator.type === 'moving_average' &&
      indicator.settings.period === period &&
      indicator.visible
    );
  }

  private isATRVisible(period: number): boolean {
    return this.state.indicators.some(indicator =>
      indicator.type === 'atr' &&
      indicator.settings.period === period &&
      indicator.visible
    );
  }

  private isRSIVisible(period: number): boolean {
    return this.state.indicators.some(indicator =>
      indicator.type === 'rsi' &&
      indicator.settings.period === period &&
      indicator.visible
    );
  }

  private getDefaultColorForPeriod(period: number): string {
    const colors: Record<number, string> = {
      20: '#FF6B6B',
      50: '#4ECDC4',
      100: '#45B7D1',
      200: '#96CEB4'
    };
    return colors[period] || '#2962FF';
  }


  // Public methods for chart interaction (updated for new indicator system)
  toggleSMA(period: number): void {
    // For backward compatibility, find or create moving average indicator
    const existingIndicator = this.state.indicators.find(indicator =>
      indicator.type === 'moving_average' && indicator.settings.period === period
    );

    if (existingIndicator) {
      // Toggle existing indicator
      existingIndicator.visible = !existingIndicator.visible;
    } else {
      // Create new moving average indicator
      const newIndicator: Indicator = {
        id: `ema_${period}_${Date.now()}`,
        name: `EMA(${period})`,
        type: 'moving_average',
        visible: true,
        settings: { period, movingAverageType: 'exponential', source: 'close' },
        color: this.getDefaultColorForPeriod(period),
        createdAt: new Date()
      };
      this.state.indicators.push(newIndicator);
    }

    this.renderIndicators();
  }

  toggleATR(period: number): void {
    // For backward compatibility, find or create ATR indicator
    const existingIndicator = this.state.indicators.find(indicator =>
      indicator.type === 'atr' && indicator.settings.period === period
    );

    if (existingIndicator) {
      // Toggle existing indicator
      existingIndicator.visible = !existingIndicator.visible;
    } else {
      // Create new ATR indicator
      const newIndicator: Indicator = {
        id: `atr_${period}_${Date.now()}`,
        name: `ATR(${period})`,
        type: 'atr',
        visible: true,
        settings: { period, source: 'hlc3' },
        color: '#FF6D00',
        createdAt: new Date()
      };
      this.state.indicators.push(newIndicator);
    }

    if (!this.chart) return;

    if (this.isATRVisible(period)) {
      // ATR enabled: create ATR series and pane
      const atrIndicator = this.state.indicators.find(ind => ind.type === 'atr' && ind.settings.period === period);
      this.createATRSeries(period, atrIndicator?.color || '#FF6B35');

      // Apply ATR-based candlestick coloring
      if (this.fullDataset.length > 0) {
        const atrData = this.calculateATR(this.fullDataset, period);
        this.applyATRBasedCandlestickColoring(this.fullDataset, atrData, period);
      }
    } else {
      // ATR disabled: remove ATR series and pane
      const atrKey = `atr${period}`;
      this.removeATRSeries(atrKey);

      // Reset candlestick colors
      this.resetCandlestickColors();
    }
  }


  /** Stroke width for line-series indicators (1–4). Default 1 for lighter overlays vs candles. */
  private indicatorStrokeWidth(settings: IndicatorSettings | undefined, fallback: LineWidth = 1): LineWidth {
    const raw = settings?.['lineWidth'];
    const n = typeof raw === 'number' ? raw : Number(raw);
    if (!Number.isFinite(n) || n <= 0) {
      return fallback;
    }
    const clamped = Math.min(4, Math.max(1, Math.round(n)));
    return clamped as LineWidth;
  }

  private createSMASeries(id: string, color: string, period: number, lineWidth: LineWidth = 1): void {
    if (!this.chart) return;

    // Add to pane 0 (main chart) so MA overlays on candlesticks
    this.smaSeries[id] = this.chart.addSeries(LineSeries, {
      color: color,
      lineWidth,
      lineStyle: 0,
      lastValueVisible: true,
      priceLineVisible: true,
      visible: true
    }, 0);

    // Update SMA data if we have it
    if (this.fullDataset.length > 0) {
      this.updateSMAData(id);
    }
  }

  private updateSMAData(id: string): void {
    if (!this.fullDataset.length) {
      return;
    }

    const series = this.smaSeries[id];

    if (!series) {
      return;
    }

    // Find the indicator to get the moving average type
    const indicator = this.state.indicators.find(ind =>
      ind.type === 'moving_average' && ind.id === id && ind.visible
    );

    if (!indicator) {
      return;
    }

    const type = String(indicator.settings.movingAverageType || 'exponential').toLowerCase();
    const period = Number(indicator.settings.period);
    if (isNaN(period) || period <= 0) return;

    // Calculate and set data
    const smaData = type === 'exponential'
      ? this.calculateEMA(this.fullDataset, period)
      : this.calculateSMA(this.fullDataset, period);

    series.setData(smaData);
  }

  private updateATRData(period: number): void {
    if (!this.fullDataset.length) {
      return;
    }

    const key = `atr${period}`;
    const series = this.atrSeries[key];

    if (!series) {
      return;
    }

    const atrData = this.calculateATR(this.fullDataset, period);
    series.setData(atrData);
  }

  private updateAllIndicatorData(): void {

    // Update all SMA series
    Object.keys(this.smaSeries).forEach(key => {
      this.updateSMAData(key);
    });

    // Update all ATR series
    Object.keys(this.atrSeries).forEach(key => {
      const period = parseInt(key.replace('atr', ''));
      this.updateATRData(period);
    });
  }

  private createATRSeries(period: number, color: string = '#FF6B35', lineWidth: LineWidth = 1): void {
    if (!this.chart) return;

    const key = `atr${period}`;
    const newPaneIndex = this.chart.panes().length;

    this.atrSeries[key] = this.chart.addSeries(LineSeries, {
      color,
      lineWidth,
      lineStyle: 0,
      lastValueVisible: true,
      priceLineVisible: true,
      visible: true,
      priceFormat: {
        type: 'price',
        precision: 2,
        minMove: 0.01,
      },
    }, newPaneIndex);

    if (this.chart.panes().length > newPaneIndex) {
      this.chart.panes()[newPaneIndex].setHeight(120);
    }

    if (this.fullDataset.length > 0) {
      const atrData = this.calculateATR(this.fullDataset, period);
      this.atrSeries[key].setData(atrData);
    }
    this.recalculatePaneLayouts();
  }

  private createRSISeries(period: number, color: string = '#9C27B0', lineWidth: LineWidth = 1): void {
    if (!this.chart) return;

    const key = `rsi${period}`;
    const newPaneIndex = this.chart.panes().length;

    const rsiIndicator = this.state.indicators.find(ind =>
      ind.type === 'rsi' && ind.settings.period === period
    );
    const overboughtLevel = rsiIndicator?.settings['overboughtLevel'] || 70;
    const oversoldLevel = rsiIndicator?.settings['oversoldLevel'] || 30;

    this.rsiSeries[key] = this.chart.addSeries(LineSeries, {
      color,
      lineWidth,
      lineStyle: 0,
      lastValueVisible: true,
      priceLineVisible: true,
      visible: true,
      priceFormat: {
        type: 'price',
        precision: 2,
        minMove: 0.01,
      },
      crosshairMarkerVisible: true,
      crosshairMarkerRadius: 3,
    }, newPaneIndex);

    // Apply native reference lines
    this.rsiSeries[key].createPriceLine({
      price: overboughtLevel,
      color: '#ef5350',
      lineWidth: 1,
      lineStyle: 2,
      axisLabelVisible: true,
      title: 'OB',
    });

    this.rsiSeries[key].createPriceLine({
      price: oversoldLevel,
      color: '#26a69a',
      lineWidth: 1,
      lineStyle: 2,
      axisLabelVisible: true,
      title: 'OS',
    });

    this.rsiSeries[key].createPriceLine({
      price: 50,
      color: '#787B86',
      lineWidth: 1,
      lineStyle: 2,
      axisLabelVisible: false,
    });

    if (this.chart.panes().length > newPaneIndex) {
      this.chart.panes()[newPaneIndex].setHeight(120);
      const priceScale = this.rsiSeries[key].priceScale();
      if (priceScale) {
        priceScale.applyOptions({
          scaleMargins: { top: 0.05, bottom: 0.05 },
          autoScale: true,
        });
      }
    }

    if (this.fullDataset.length > 0) {
      const rsiData = this.technicalIndicatorsService.calculateSmoothedRSI(this.fullDataset, period, 3);
      this.rsiSeries[key].setData(rsiData);
    }
    this.recalculatePaneLayouts();
  }

  private removeSMASeries(key: string): void {
    if (!this.chart || !this.smaSeries[key]) return;
    this.chart.removeSeries(this.smaSeries[key]);
    delete this.smaSeries[key];
  }

  private removeATRSeries(key: string): void {
    if (!this.chart || !this.atrSeries[key]) return;

    // Remove the ATR series
    this.chart.removeSeries(this.atrSeries[key]);
    delete this.atrSeries[key];

    // Note: The pane will automatically be removed when the last series in it is removed
  }

  private removeRSISeries(key: string): void {
    if (!this.chart || !this.rsiSeries[key]) return;

    // Remove the RSI series
    this.chart.removeSeries(this.rsiSeries[key]);
    delete this.rsiSeries[key];

    // Refresh layout
    this.recalculatePaneLayouts();
    // Note: The pane will automatically be removed when the last series in it is removed
  }

  // ===== Bollinger Bands =====

  private createBollingerSeries(period: number, stdDev: number, color: string, lineWidth: LineWidth = 1): void {
    if (!this.chart) return;

    const key = `bb${period}_${stdDev}`;

    const upperSeries = this.chart.addSeries(LineSeries, {
      color: color,
      lineWidth,
      lineStyle: 0,
      lastValueVisible: false,
      priceLineVisible: false,
      visible: true,
      crosshairMarkerVisible: false,
    });

    const middleSeries = this.chart.addSeries(LineSeries, {
      color: color,
      lineWidth,
      lineStyle: 2,
      lastValueVisible: false,
      priceLineVisible: false,
      visible: true,
      crosshairMarkerVisible: false,
    });

    const lowerSeries = this.chart.addSeries(LineSeries, {
      color: color,
      lineWidth,
      lineStyle: 0,
      lastValueVisible: false,
      priceLineVisible: false,
      visible: true,
      crosshairMarkerVisible: false,
    });

    this.bollingerSeries[key] = { upper: upperSeries, middle: middleSeries, lower: lowerSeries };

    if (this.fullDataset.length > 0) {
      this.updateBollingerData(period, stdDev);
    }
  }

  private updateBollingerData(period: number, stdDev: number): void {
    const key = `bb${period}_${stdDev}`;
    const series = this.bollingerSeries[key];
    if (!series || !this.fullDataset.length) return;

    const bbData = this.technicalIndicatorsService.calculateBollingerBands(this.fullDataset, period, stdDev);

    series.upper.setData(bbData.map(d => ({ time: d.time, value: d.upper })));
    series.middle.setData(bbData.map(d => ({ time: d.time, value: d.middle })));
    series.lower.setData(bbData.map(d => ({ time: d.time, value: d.lower })));
  }

  private removeBollingerSeries(key: string): void {
    if (!this.chart || !this.bollingerSeries[key]) return;

    this.chart.removeSeries(this.bollingerSeries[key].upper);
    this.chart.removeSeries(this.bollingerSeries[key].middle);
    this.chart.removeSeries(this.bollingerSeries[key].lower);
    delete this.bollingerSeries[key];
  }

  private isBollingerVisible(period: number, stdDev: number): boolean {
    return this.state.indicators.some(indicator =>
      indicator.type === 'bollinger_bands' &&
      indicator.settings.period === period &&
      (indicator.settings['standardDeviations'] || 2) === stdDev &&
      indicator.visible
    );
  }

  // ===== MACD =====

  private createMACDSeries(
    fastPeriod: number,
    slowPeriod: number,
    signalPeriod: number,
    color: string,
    macdLineWidth: LineWidth = 1
  ): void {
    if (!this.chart) return;

    const key = `macd${fastPeriod}_${slowPeriod}_${signalPeriod}`;
    const newPaneIndex = this.chart.panes().length;

    const macdLine = this.chart.addSeries(LineSeries, {
      color: color,
      lineWidth: macdLineWidth,
      lineStyle: 0,
      lastValueVisible: true,
      priceLineVisible: false,
      visible: true,
      crosshairMarkerVisible: true,
      crosshairMarkerRadius: 4,
      priceFormat: {
        type: 'price',
        precision: 2,
        minMove: 0.01,
      },
    }, newPaneIndex);

    const signalLine = this.chart.addSeries(LineSeries, {
      color: color,
      lineWidth: 1,
      lineStyle: 0,
      lastValueVisible: false,
      priceLineVisible: false,
      visible: true,
      crosshairMarkerVisible: false,
      priceFormat: {
        type: 'price',
        precision: 2,
        minMove: 0.01,
      },
    }, newPaneIndex);

    const histogram = this.chart.addSeries(HistogramSeries, {
      lastValueVisible: false,
      priceLineVisible: false,
      visible: true,
      priceFormat: {
        type: 'price',
        precision: 2,
        minMove: 0.01,
      },
    }, newPaneIndex);

    if (this.chart.panes().length > newPaneIndex) {
      this.chart.panes()[newPaneIndex].setHeight(120);
    }

    this.macdSeries[key] = { macdLine, signalLine, histogram };

    if (this.fullDataset.length > 0) {
      this.updateMACDData(fastPeriod, slowPeriod, signalPeriod);
    }
  }

  private updateMACDData(fastPeriod: number, slowPeriod: number, signalPeriod: number): void {
    const key = `macd${fastPeriod}_${slowPeriod}_${signalPeriod}`;
    const series = this.macdSeries[key];
    if (!series || !this.fullDataset.length) return;

    const macdData = this.technicalIndicatorsService.calculateMACD(this.fullDataset, fastPeriod, slowPeriod, signalPeriod);

    series.macdLine.setData(macdData.map(d => ({ time: d.time, value: d.macd })));
    series.signalLine.setData(macdData.map(d => ({ time: d.time, value: d.signal })));
    series.histogram.setData(macdData.map(d => ({
      time: d.time,
      value: d.histogram,
      color: d.histogram >= 0 ? '#26a69a' : '#ef5350'
    })));
  }

  private removeMACDSeries(key: string): void {
    if (!this.chart || !this.macdSeries[key]) return;

    this.chart.removeSeries(this.macdSeries[key].macdLine);
    this.chart.removeSeries(this.macdSeries[key].signalLine);
    this.chart.removeSeries(this.macdSeries[key].histogram);
    delete this.macdSeries[key];
    this.recalculatePaneLayouts();
  }

  private isMACDVisible(fastPeriod: number, slowPeriod: number, signalPeriod: number): boolean {
    return this.state.indicators.some(indicator =>
      indicator.type === 'macd' &&
      indicator.settings['fastPeriod'] === fastPeriod &&
      indicator.settings['slowPeriod'] === slowPeriod &&
      indicator.settings['signalPeriod'] === signalPeriod &&
      indicator.visible
    );
  }

  // ===== Stochastic Oscillator =====

  private createStochSeries(
    kPeriod: number,
    dPeriod: number,
    smooth: number,
    color: string,
    kLineWidth: LineWidth = 1
  ): void {
    if (!this.chart) return;

    const key = `stoch${kPeriod}_${dPeriod}_${smooth}`;
    const newPaneIndex = this.chart.panes().length;

    const kLine = this.chart.addSeries(LineSeries, {
      color: color,
      lineWidth: kLineWidth,
      lineStyle: 0,
      lastValueVisible: true,
      priceLineVisible: false,
      visible: true,
      crosshairMarkerVisible: true,
      crosshairMarkerRadius: 4,
      priceFormat: {
        type: 'price',
        precision: 1,
        minMove: 0.1,
      },
    }, newPaneIndex);

    const dLine = this.chart.addSeries(LineSeries, {
      color: color,
      lineWidth: 1,
      lineStyle: 2,
      lastValueVisible: false,
      priceLineVisible: false,
      visible: true,
      crosshairMarkerVisible: false,
      priceFormat: {
        type: 'price',
        precision: 1,
        minMove: 0.1,
      },
    }, newPaneIndex);

    const overboughtLine = this.chart.addSeries(LineSeries, {
      color: 'rgba(255, 68, 68, 0.4)',
      lineWidth: 1,
      lineStyle: 2,
      lastValueVisible: false,
      priceLineVisible: false,
      crosshairMarkerVisible: false,
    }, newPaneIndex);

    const oversoldLine = this.chart.addSeries(LineSeries, {
      color: 'rgba(68, 255, 68, 0.4)',
      lineWidth: 1,
      lineStyle: 2,
      lastValueVisible: false,
      priceLineVisible: false,
      crosshairMarkerVisible: false,
    }, newPaneIndex);

    if (this.chart.panes().length > newPaneIndex) {
      this.chart.panes()[newPaneIndex].setHeight(120);
    }

    this.stochSeries[key] = { kLine, dLine, overbought: overboughtLine, oversold: oversoldLine };

    if (this.fullDataset.length > 0) {
      this.updateStochData(kPeriod, dPeriod, smooth);
    }
  }

  private updateStochData(kPeriod: number, dPeriod: number, smooth: number): void {
    const key = `stoch${kPeriod}_${dPeriod}_${smooth}`;
    const series = this.stochSeries[key];
    if (!series || !this.fullDataset.length) return;

    const stochData = this.technicalIndicatorsService.calculateStochastic(this.fullDataset, kPeriod, dPeriod, smooth);

    series.kLine.setData(stochData.map(d => ({ time: d.time, value: d.k })));
    series.dLine.setData(stochData.map(d => ({ time: d.time, value: d.d })));

    // Set reference line data across the full time range
    if (stochData.length > 0) {
      const refLineData = stochData.map(d => ({ time: d.time, value: 0 }));
      series.overbought.setData(refLineData.map(d => ({ ...d, value: 80 })));
      series.oversold.setData(refLineData.map(d => ({ ...d, value: 20 })));
    }
  }

  private removeStochSeries(key: string): void {
    if (!this.chart || !this.stochSeries[key]) return;

    this.chart.removeSeries(this.stochSeries[key].kLine);
    this.chart.removeSeries(this.stochSeries[key].dLine);
    this.chart.removeSeries(this.stochSeries[key].overbought);
    this.chart.removeSeries(this.stochSeries[key].oversold);
    delete this.stochSeries[key];
    this.recalculatePaneLayouts();
  }

  private isStochVisible(kPeriod: number, dPeriod: number, smooth: number): boolean {
    return this.state.indicators.some(indicator =>
      indicator.type === 'stochastic' &&
      indicator.settings['kPeriod'] === kPeriod &&
      indicator.settings['dPeriod'] === dPeriod &&
      (indicator.settings['smooth'] || 3) === smooth &&
      indicator.visible
    );
  }

  toggleMarkerVisibility(category: ChartMarker['category']): void {
    this.state.markerVisibility[category] = !this.state.markerVisibility[category];
    this.refreshMarkersOnChart();
  }

  refreshCalendarEvents(): void {
    if (this.currentSymbol) {
      this.loadCalendarEvents(this.currentSymbol);
    }
  }

  // Change series type
  changeSeriesType(seriesType: SeriesType): void {
    this.state.seriesType = seriesType;
    this.createMainSeries(seriesType);

    // Re-apply data to the new series type
    if (this.fullDataset.length > 0) {
      this.updateMainSeriesData();
    } else {
    }
  }

  getCurrentSeriesType(): SeriesType {
    return this.state.seriesType;
  }

  // Chart Types Management
  /**
   * Fetch chart types from API and update the chart types list
   * Only returns dynamic data from API, no fallback
   */
  fetchChartTypes(): Observable<ChartType[]> {
    return new Observable(observer => {
      this.chartTypeService.getChartTypesList().subscribe({
        next: (response: any) => {
          try {
            // Map API response to ChartType interface
            // API should return array of chart types with structure:
            // { id, type/value, label, icon, description, isFavorite }
            const chartTypes: ChartType[] = this.mapApiResponseToChartTypes(response);
            // Only use dynamic data from API
            this.chartTypesSubject.next(chartTypes);
            this.chartTypesLoaded = true;
            observer.next(chartTypes);
            observer.complete();
          } catch (error) {
            // Return empty array - only show dynamic data
            this.chartTypesSubject.next([]);
            observer.next([]);
            observer.complete();
          }
        },
        error: (error) => {
          // Return empty array - only show dynamic data
          this.chartTypesSubject.next([]);
          observer.next([]);
          observer.complete();
        }
      });
    });
  }

  /**
   * Map API response to ChartType array
   * Handles favorite status from API response
   * Expected structure: { data: { chartTypes: [...] } }
   */
  private mapApiResponseToChartTypes(response: any): ChartType[] {
    // Handle different possible response structures
    let chartTypesData: any[] = [];

    // Check for nested structure: response.data.chartTypes
    if (response?.data?.chartTypes && Array.isArray(response.data.chartTypes)) {
      chartTypesData = response.data.chartTypes;
    } else if (Array.isArray(response)) {
      chartTypesData = response;
    } else if (response?.data && Array.isArray(response.data)) {
      chartTypesData = response.data;
    } else if (response?.chartTypes && Array.isArray(response.chartTypes)) {
      chartTypesData = response.chartTypes;
    } else {
      // If response structure is unexpected, return empty array
      return [];
    }

    // Map to ChartType interface with favorite status from API
    return chartTypesData.map((item: any) => ({
      id: item._id || item.id || item.chartTypeId || '',
      value: this.mapStringToSeriesType(item.value || item.type || item.chartType || 'candlestick'),
      label: item.name || item.label || item.title || 'Chart Type',
      icon: item.icon || 'chart',
      description: item.description || item.desc || '',
      // Get favorite status from API response
      isFavorite: item.isFavorite !== undefined
        ? Boolean(item.isFavorite)
        : (item.favorite !== undefined ? Boolean(item.favorite) : false)
    }));
  }

  /**
   * Map string to SeriesType
   */
  private mapStringToSeriesType(type: string): SeriesType {
    const validTypes: SeriesType[] = ['candlestick', 'line', 'area', 'bar', 'baseline', 'heikin-ashi'];
    const normalizedType = type.toLowerCase().replace(/[_-]/g, '-');
    return validTypes.includes(normalizedType as SeriesType) ? (normalizedType as SeriesType) : 'candlestick';
  }

  /**
   * Normalize API chart style string to SeriesType (lowercase, candles -> candlestick)
   * so Chart Style menu active state matches.
   */
  private normalizeChartStyleToSeriesType(value: string): SeriesType {
    const s = String(value).toLowerCase().trim();
    if (s === 'candles') return 'candlestick';
    const valid: SeriesType[] = ['candlestick', 'line', 'area', 'bar', 'baseline', 'heikin-ashi'];
    return valid.includes(s as SeriesType) ? (s as SeriesType) : 'candlestick';
  }


  /**
   * Get current chart types
   */
  getChartTypes(): ChartType[] {
    return this.chartTypesSubject.value;
  }

  /**
   * Get chart types as observable
   */
  getChartTypesObservable(): Observable<ChartType[]> {
    return this.chartTypes$;
  }

  /**
   * Check if chart types are loaded
   */
  areChartTypesLoaded(): boolean {
    return this.chartTypesLoaded;
  }

  /**
   * Toggle favorite status for a chart type
   * After toggling, refresh chart types from API to get updated favorite status
   */
  toggleChartTypeFavorite(chartTypeId: string): Observable<any> {
    return new Observable(observer => {
      this.chartTypeService.toggleFavoriteChartType(chartTypeId).subscribe({
        next: (response: any) => {
          // Refresh chart types from API to get updated favorite status
          this.chartTypeService.getChartTypesList().subscribe({
            next: (refreshResponse: any) => {
              try {
                const chartTypes: ChartType[] = this.mapApiResponseToChartTypes(refreshResponse);
                this.chartTypesSubject.next(chartTypes);
                observer.next(response);
                observer.complete();
              } catch (error) {
                // Fallback: update local state
                const currentTypes = this.chartTypesSubject.value;
                const updatedTypes = currentTypes.map(type => {
                  if (type.id === chartTypeId) {
                    return { ...type, isFavorite: !type.isFavorite };
                  }
                  return type;
                });
                this.chartTypesSubject.next(updatedTypes);
                observer.next(response);
                observer.complete();
              }
            },
            error: (refreshError) => {
              // Fallback: update local state
              const currentTypes = this.chartTypesSubject.value;
              const updatedTypes = currentTypes.map(type => {
                if (type.id === chartTypeId) {
                  return { ...type, isFavorite: !type.isFavorite };
                }
                return type;
              });
              this.chartTypesSubject.next(updatedTypes);
              observer.next(response);
              observer.complete();
            }
          });
        },
        error: (error) => {
          observer.error(error);
        }
      });
    });
  }

  /**
   * Get favorite chart types
   */
  getFavoriteChartTypes(): ChartType[] {
    return this.chartTypesSubject.value.filter(type => type.isFavorite);
  }

  /**
   * Check if a chart type is favorite
   */
  isChartTypeFavorite(chartTypeValue: SeriesType): boolean {
    const chartType = this.chartTypesSubject.value.find(type => type.value === chartTypeValue);
    return chartType?.isFavorite || false;
  }

  /**
   * Get chart type by value
   */
  getChartTypeByValue(value: SeriesType): ChartType | undefined {
    return this.chartTypesSubject.value.find(type => type.value === value);
  }

  // Chart Indicators Management
  /**
   * Fetch chart indicators from API and update the indicators list
   * Only returns dynamic data from API, no fallback
   */
  fetchChartIndicators(): Observable<ChartIndicator[]> {
    return new Observable(observer => {
      this.chartTypeService.getChartIndicatorsList().subscribe({
        next: (response: any) => {
          try {
            // Map API response to ChartIndicator interface
            const chartIndicators: ChartIndicator[] = this.mapApiResponseToChartIndicators(response);
            // Only use dynamic data from API
            this.chartIndicatorsSubject.next(chartIndicators);
            this.chartIndicatorsLoaded = true;
            observer.next(chartIndicators);
            observer.complete();
          } catch (error) {
            // Return empty array - only show dynamic data
            this.chartIndicatorsSubject.next([]);
            observer.next([]);
            observer.complete();
          }
        },
        error: (error) => {
          // Return empty array - only show dynamic data
          this.chartIndicatorsSubject.next([]);
          observer.next([]);
          observer.complete();
        }
      });
    });
  }

  /**
   * Map API response to ChartIndicator array
   * Handles favorite status from API response
   * Expected structure: { data: { chartIndicators: [...] } }
   */
  private mapApiResponseToChartIndicators(response: any): ChartIndicator[] {
    // Handle different possible response structures
    let indicatorsData: any[] = [];

    // Check for nested structure: response.data.chartIndicators
    if (response?.data?.chartIndicators && Array.isArray(response.data.chartIndicators)) {
      indicatorsData = response.data.chartIndicators;
    } else if (response?.data?.indicators && Array.isArray(response.data.indicators)) {
      indicatorsData = response.data.indicators;
    } else if (response?.chartIndicators && Array.isArray(response.chartIndicators)) {
      indicatorsData = response.chartIndicators;
    } else if (response?.indicators && Array.isArray(response.indicators)) {
      indicatorsData = response.indicators;
    } else if (response?.data && Array.isArray(response.data)) {
      indicatorsData = response.data;
    } else if (Array.isArray(response)) {
      indicatorsData = response;
    } else {
      // If response structure is unexpected, return empty array
      return [];
    }

    // Map to ChartIndicator interface with favorite status from API
    // API structure: { _id, value, name, description, isFavorite }
    const mappedIndicators = indicatorsData.map((item: any) => {
      // API uses 'value' field for indicator type, not 'type'
      const indicatorType = item.value || item.type || item.indicatorType || 'moving_average';
      const isFavorite = item.isFavorite !== undefined
        ? Boolean(item.isFavorite)
        : (item.favorite !== undefined ? Boolean(item.favorite) : false);

      return {
        id: item._id || item.id || item.indicatorId || '',
        type: this.mapStringToIndicatorType(indicatorType),
        name: item.name || item.displayName || item.title || 'Indicator',
        displayName: item.name || item.displayName || item.title || 'Indicator',
        description: item.description || item.desc || '',
        icon: item.icon || 'bar-chart-3',
        // Get favorite status from API response - ensure it's properly read
        isFavorite: isFavorite
      };
    });

    return mappedIndicators;
  }

  /**
   * Map string to IndicatorType
   */
  private mapStringToIndicatorType(type: string): IndicatorType {
    const validTypes: IndicatorType[] = ['moving_average', 'atr', 'rsi', 'macd', 'bollinger_bands', 'stochastic'];
    const normalizedType = type.toLowerCase().replace(/[_-]/g, '_');
    return validTypes.includes(normalizedType as IndicatorType) ? (normalizedType as IndicatorType) : 'moving_average';
  }

  /**
   * Get current chart indicators
   */
  getChartIndicators(): ChartIndicator[] {
    return this.chartIndicatorsSubject.value;
  }

  /**
   * Get chart indicators as observable
   */
  getChartIndicatorsObservable(): Observable<ChartIndicator[]> {
    return this.chartIndicators$;
  }

  /**
   * Check if chart indicators are loaded
   */
  areChartIndicatorsLoaded(): boolean {
    return this.chartIndicatorsLoaded;
  }

  /**
   * Toggle favorite status for a chart indicator
   * After toggling, refresh chart indicators from API to get updated favorite status
   */
  toggleChartIndicatorFavorite(chartIndicatorId: string): Observable<any> {
    return new Observable(observer => {
      this.chartTypeService.toggleFavoriteChartIndicator(chartIndicatorId).subscribe({
        next: (response: any) => {
          // Refresh chart indicators from API to get updated favorite status
          this.chartTypeService.getChartIndicatorsList().subscribe({
            next: (refreshResponse: any) => {
              try {
                const chartIndicators: ChartIndicator[] = this.mapApiResponseToChartIndicators(refreshResponse);
                this.chartIndicatorsSubject.next(chartIndicators);
                observer.next(response);
                observer.complete();
              } catch (error) {
                // Fallback: update local state
                const currentIndicators = this.chartIndicatorsSubject.value;
                const updatedIndicators = currentIndicators.map(indicator => {
                  if (indicator.id === chartIndicatorId) {
                    return { ...indicator, isFavorite: !indicator.isFavorite };
                  }
                  return indicator;
                });
                this.chartIndicatorsSubject.next(updatedIndicators);
                observer.next(response);
                observer.complete();
              }
            },
            error: (refreshError) => {
              // Fallback: update local state
              const currentIndicators = this.chartIndicatorsSubject.value;
              const updatedIndicators = currentIndicators.map(indicator => {
                if (indicator.id === chartIndicatorId) {
                  return { ...indicator, isFavorite: !indicator.isFavorite };
                }
                return indicator;
              });
              this.chartIndicatorsSubject.next(updatedIndicators);
              observer.next(response);
              observer.complete();
            }
          });
        },
        error: (error) => {
          observer.error(error);
        }
      });
    });
  }

  /**
   * Get favorite chart indicators
   */
  getFavoriteChartIndicators(): ChartIndicator[] {
    const allIndicators = this.chartIndicatorsSubject.value;
    const favorites = allIndicators.filter(indicator => indicator.isFavorite === true);
    return favorites;
  }

  /**
   * Check if a chart indicator is favorite
   */
  isChartIndicatorFavorite(indicatorType: IndicatorType): boolean {
    const indicator = this.chartIndicatorsSubject.value.find(ind => ind.type === indicatorType);
    return indicator?.isFavorite || false;
  }

  /**
   * Get chart indicator by type
   */
  getChartIndicatorByType(type: IndicatorType): ChartIndicator | undefined {
    return this.chartIndicatorsSubject.value.find(indicator => indicator.type === type);
  }

  // Chart Settings Management
  /**
   * Fetch chart settings from API
   */
  fetchChartSettings(): Observable<ChartSettings> {
    return new Observable(observer => {
      this.chartTypeService.getChartSettings().subscribe({
        next: (response: any) => {
          try {
            // Map API response to ChartSettings interface
            const settings: ChartSettings = this.mapApiResponseToChartSettings(response);
            observer.next(settings);
            observer.complete();
          } catch (error) {
            // Return empty settings on error
            observer.next({});
            observer.complete();
          }
        },
        error: (error) => {
          // Return empty settings on error
          observer.next({});
          observer.complete();
        }
      });
    });
  }

  /**
   * Map API response to ChartSettings interface
   * Handles different possible response structures
   */
  private mapApiResponseToChartSettings(response: any): ChartSettings {
    let settingsData: any = {};

    // Handle different possible response structures
    // Expected: { data: { chartSettings: { ... } }, message: 'Chart settings fetched successfully.' }
    if (response?.data?.chartSettings && typeof response.data.chartSettings === 'object') {
      settingsData = response.data.chartSettings;
    } else if (response?.data && typeof response.data === 'object') {
      settingsData = response.data;
    } else if (response?.settings && typeof response.settings === 'object') {
      settingsData = response.settings;
    } else if (typeof response === 'object' && !Array.isArray(response)) {
      settingsData = response;
    } else {
      return {};
    }

    // Map API field names to ChartSettings:
    // crossHairType, defaultInterval, defaultTimeframe, eventMarkers, gridLine, priceLine, theme, volume, defaultChartType
    const rawStyle = settingsData.defaultChartType
      ?? settingsData.style
      ?? settingsData.seriesType
      ?? settingsData.chartType
      ?? undefined;
    const style = rawStyle ? this.normalizeChartStyleToSeriesType(rawStyle) : undefined;

    // Extract drawings from get-chart-settings response (chartDrawings, drawings, data.chartDrawings)
    const chartDrawings = settingsData.chartDrawings ?? settingsData.drawings
      ?? response?.data?.chartDrawings ?? response?.data?.drawings ?? [];

    return {
      theme: settingsData.theme || undefined,
      interval: settingsData.defaultTimeframe ?? settingsData.interval ?? settingsData.timeRange ?? undefined,
      candleInterval: settingsData.defaultInterval !== undefined
        ? settingsData.defaultInterval
        : (settingsData.candleInterval ?? settingsData.interval ?? undefined),
      style,
      crosshair: settingsData.crossHairType
        ?? settingsData.crosshair
        ?? settingsData.crosshairMode
        ?? undefined,
      volume: settingsData.volume !== undefined ? Boolean(settingsData.volume) : undefined,
      gridLines: settingsData.gridLine !== undefined
        ? Boolean(settingsData.gridLine)
        : (settingsData.gridLines !== undefined ? Boolean(settingsData.gridLines) : undefined),
      priceLine: settingsData.priceLine !== undefined ? Boolean(settingsData.priceLine) : undefined,
      markers: settingsData.eventMarkers !== undefined
        ? Boolean(settingsData.eventMarkers)
        : (settingsData.markers !== undefined ? Boolean(settingsData.markers) : undefined),
      indicators: settingsData.indicators || undefined,
      chartDrawings: Array.isArray(chartDrawings) && chartDrawings.length > 0 ? chartDrawings : undefined
    };
  }

  /**
   * Update chart settings via API
   */
  updateChartSettings(settings: ChartSettings): Observable<any> {
    return new Observable(observer => {
      // Prepare settings object for API (use backend field names)
      const settingsPayload = {
        crossHairType: settings.crosshair ?? 'normal',
        defaultInterval: settings.candleInterval ?? 'auto',
        defaultTimeframe: settings.interval ?? '1D',
        eventMarkers: settings.markers ?? true,
        gridLine: settings.gridLines ?? true,
        priceLine: settings.priceLine ?? true,
        theme: settings.theme ?? 'light',
        volume: settings.volume ?? true,
        defaultChartType: settings.style ?? 'candles'
      };

      this.chartTypeService.updateChartSettings(settingsPayload).subscribe({
        next: (response: any) => {
          observer.next(response);
          observer.complete();
        },
        error: (error) => {
          observer.error(error);
        }
      });
    });
  }

  getMarkerVisibility(): Record<ChartMarker['category'], boolean> {
    return { ...this.state.markerVisibility };
  }

  setTimeRange(range: string): void {
    if (!this.chart) return;
    this.state.selectedTimePeriod = range;
    this.currentInterval = range;
    this.presetViewportApplied = false;

    // Full socket history stays in the series; only the visible window changes
    this.applyTimeRangeToDataset(range);
    this.updateMainSeriesData(true);
    setTimeout(() => this.setVisibleTimeRange(range), 0);
  }

  // Line Drawing Functionality
  toggleDrawingMode(): void {
    this.state.isDrawingMode = !this.state.isDrawingMode;

    if (this.chart) {
      // Disable/enable chart interactions when in drawing mode
      this.chart.applyOptions({
        handleScroll: !this.state.isDrawingMode,
        handleScale: !this.state.isDrawingMode,
      });
    }

  }

  setDrawingMode(enabled: boolean): void {
    this.state.isDrawingMode = enabled;

    if (this.chart) {
      // Disable/enable chart interactions when in drawing mode
      this.chart.applyOptions({
        handleScroll: !this.state.isDrawingMode,
        handleScale: !this.state.isDrawingMode,
      });
    }

  }

  // Text Annotation Functionality

  addTextAnnotation(time: Time, price: number, text: string = 'New Text', clickX?: number, clickY?: number): string {
    const id = `text_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

    const annotation: TextAnnotation = {
      id,
      x: clickX || 0, // Use actual click coordinates if provided
      y: clickY || 0, // Use actual click coordinates if provided  
      time,
      price,
      text,
      fontSize: 14,
      fontColor: '#333333',
      backgroundColor: undefined, // Start with no background
      borderColor: undefined, // Start with no border
      borderWidth: 0,
      visible: true,
      editable: true,
      name: `Text ${this.state.textAnnotations.length + 1}`,
      style: 'normal'
    };

    this.state.textAnnotations.push(annotation);
    this.renderTextAnnotation(annotation);

    // Optional: Auto-exit text mode after placing text (uncomment if desired)
    // this.state.isTextMode = false;

    return id;
  }

  addTextAnnotationFromApi(spec: { time?: number; price?: number; text: string; id?: string; style?: { color?: string; fontSize?: number } }): string {
    const { time: timeSec, price } = this.getDefaultTimePriceForText(spec.time, spec.price);
    const time = this.normalizeApiTime(timeSec) as Time;
    const id = spec.id || `text_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    const annotation: TextAnnotation = {
      id,
      x: 0,
      y: 0,
      time,
      price,
      text: spec.text,
      fontSize: spec.style?.fontSize ?? 14,
      fontColor: spec.style?.color ?? '#333333',
      backgroundColor: undefined,
      borderColor: undefined,
      borderWidth: 0,
      visible: true,
      editable: true,
      name: `Text ${this.state.textAnnotations.length + 1}`,
      style: 'normal'
    };
    this.state.textAnnotations.push(annotation);
    this.renderTextAnnotation(annotation);
    return id;
  }

  /** Default time/price when API sends text with no point/points (e.g. data.points: []). */
  private getDefaultTimePriceForText(timeSec?: number, priceVal?: number): { time: number; price: number } {
    if (this.fullDataset && this.fullDataset.length > 0) {
      const first = this.fullDataset[0];
      const last = this.fullDataset[this.fullDataset.length - 1];
      const t = timeSec ?? Number(first.time);
      const prices = this.fullDataset.map(d => (d as any).close ?? (d as any).value ?? (d as any).high);
      const mid = prices.length ? (Math.min(...prices) + Math.max(...prices)) / 2 : Number((last as any).close ?? (last as any).value ?? 0);
      const p = priceVal ?? mid;
      return { time: typeof t === 'number' ? t : Number((first as any).time), price: p };
    }
    return { time: timeSec ?? 0, price: priceVal ?? 0 };
  }

  addPenDrawingFromApi(spec: { points: Array<{ time: number; price: number }>; id?: string; style?: { color?: string; width?: number } }): string {
    if (!spec.points || spec.points.length < 2) return '';
    const id = spec.id || `pen-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    const points = spec.points.map(p => ({ time: this.normalizeApiTime(p.time) as Time, price: p.price }));
    const drawing: PenDrawing = {
      id,
      points,
      color: spec.style?.color ?? this.state.penColor,
      lineWidth: spec.style?.width ?? this.state.penSize,
      visible: true,
      name: `Drawing ${this.state.penDrawings.length + 1}`,
      createdAt: new Date()
    };
    this.state.penDrawings.push(drawing);
    this.renderPenDrawing(drawing);
    if (this.onLinesChangedCallback) this.onLinesChangedCallback();
    return id;
  }

  private normalizeApiTime(time: number): number {
    return time > 10000000000 ? Math.floor(time / 1000) : time;
  }

  private timeToApiMs(time: Time): number {
    const t = typeof time === 'number' ? time : (time as any)?.timestamp ?? 0;
    return t <= 10000000000 ? t * 1000 : t;
  }

  loadDrawingsFromApi(drawings: ApiDrawing[], clearExisting: boolean = true): void {
    if (!drawings || !Array.isArray(drawings)) return;
    if (clearExisting) {
      this.clearAllHorizontalLines();
      this.clearAllTextAnnotations();
      this.clearAllPenDrawings();
      this.clearAllArrowDrawings();
    }
    for (const d of drawings) {
      try {
        const tool = (d.tool || '').toLowerCase();
        if (tool === 'line' && d.points && d.points.length >= 1) {
          this.addHorizontalLineFromApi({
            id: d.id,
            price: d.points[0].price,
            name: undefined,
            style: { color: d.style?.color, lineWidth: d.style?.width },
          });
        } else if (tool === 'pen' && d.points && d.points.length >= 2) {
          this.addPenDrawingFromApi({ id: d.id, points: d.points, style: { color: d.style?.color, width: d.style?.width } });
        } else if (tool === 'text' && d.text != null) {
          const point = d.point ?? (d.points && d.points.length > 0 ? d.points[0] : undefined);
          this.addTextAnnotationFromApi({
            id: d.id,
            time: point?.time,
            price: point?.price,
            text: d.text,
            style: { color: d.style?.color, fontSize: d.style?.fontSize }
          });
        } else if (tool === 'arrow' && d.points && d.points.length >= 2) {
          const start = d.points[0];
          const end = d.points[d.points.length - 1];
          if (start && end) {
            this.state.arrowDrawings.push({
              id: d.id || `arrow-${Date.now()}-${Math.random().toString(36).substr(2, 6)}`,
              startTime: this.normalizeApiTime(start.time) as Time,
              startPrice: start.price,
              endTime: this.normalizeApiTime(end.time) as Time,
              endPrice: end.price,
              color: d.style?.color ?? this.arrowDefaultColor,
              lineWidth: d.style?.width ?? this.arrowDefaultWidth,
              visible: true,
              name: `Arrow ${this.state.arrowDrawings.length + 1}`,
              createdAt: new Date()
            });
          }
        }
      } catch (err) {
        console.warn('[Chart] Failed to load drawing from API:', d, err);
      }
    }
    // Ensure all drawings are visible even if loaded before chart/series were ready.
    this.renderAllHorizontalLines();
    this.renderAllTextAnnotations();
    this.redrawPenDrawingsOnCanvas();
    // Prevent initial "wrong-position then jump" flash: keep arrow layer hidden until first stable redraw.
    this.setArrowDrawingsVisibility(false);
    setTimeout(() => {
      this.redrawAllArrowDrawings();
      this.setArrowDrawingsVisibility(true);
    }, 180);
    // Keep sync redraws while chart layout/time scale continues settling after refresh.
    [400, 800, 1500].forEach(delay => setTimeout(() => this.redrawAllArrowDrawings(), delay));
  }

  private addHorizontalLineFromApi(spec: { id?: string; price: number; name?: string; style?: { color?: string; lineWidth?: number } }): string {
    const lineId = spec.id || `line_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    const lineName = spec.name || `Line #${this.lineCounter}`;
    if (!spec.id) this.lineCounter++;
    const color = spec.style?.color ?? '#2962FF';
    const lineWidth = spec.style?.lineWidth ?? 2;

    // Store in state even if series is not ready yet.
    const existing = this.state.lines.find(l => l.id === lineId);
    if (!existing) {
      this.state.lines.push({
        id: lineId,
        price: spec.price,
        color,
        lineWidth,
        lineStyle: 0,
        name: lineName,
      });
    }

    // Render immediately if series exists.
    if (this.mainSeries) {
      try {
        const priceLine = this.mainSeries.createPriceLine({
          price: spec.price,
          color,
          lineWidth: lineWidth as any,
          lineStyle: 0 as any,
          axisLabelVisible: true,
          title: lineName,
        });
        this.horizontalLines.set(lineId, priceLine);
      } catch { /* ignore */ }
    }

    if (this.onLinesChangedCallback) this.onLinesChangedCallback();
    return lineId;
  }

  private renderAllHorizontalLines(): void {
    if (!this.mainSeries) return;
    // Remove any previously rendered priceLines and recreate from state.
    for (const [, priceLine] of this.horizontalLines) {
      try { this.mainSeries.removePriceLine(priceLine); } catch { /* ignore */ }
    }
    this.horizontalLines.clear();
    for (const line of this.state.lines) {
      try {
        const priceLine = this.mainSeries.createPriceLine({
          price: line.price,
          color: line.color,
          lineWidth: (line.lineWidth ?? 2) as any,
          lineStyle: (line.lineStyle ?? 0) as any,
          axisLabelVisible: true,
          title: line.name,
        });
        this.horizontalLines.set(line.id, priceLine);
      } catch { /* ignore */ }
    }
  }

  private renderAllTextAnnotations(): void {
    if (!this.chart) return;
    // Clear DOM and recreate from state.
    for (const ann of this.state.textAnnotations) {
      this.removeTextAnnotationElement(ann.id);
    }
    for (const ann of this.state.textAnnotations) {
      if (ann.visible) this.renderTextAnnotation(ann);
    }
  }

  /** Load drawings from API/DB/socket format. Resilient to payload shapes from socket (drawings, data.points, etc). */
  loadDrawingsFromChartDrawingApi(drawings: ChartDrawingApi[] | any[], clearExisting: boolean = true): void {
    if (!drawings || !Array.isArray(drawings)) return;
    const flat: ApiDrawing[] = drawings.map((d: any) => {
      if (!d || typeof d !== 'object') return null;
      const tool = (d.tool || d.type || '').toLowerCase();
      if (!tool) return null;
      const data = d.data ?? d;
      let points = data?.points ?? d.points;
      if (Array.isArray(points)) {
        points = points.map((p: any) => {
          if (!p || typeof p !== 'object') return null;
          const time = p.time ?? p.t ?? p.timestamp;
          const price = p.price ?? p.p ?? p.value;
          if (time == null || price == null || isNaN(Number(price))) return null;
          return { time: Number(time), price: Number(price) };
        }).filter(Boolean);
      }
      const point = data?.point ?? d.point ?? (Array.isArray(points) && points.length > 0 ? points[0] : undefined);
      const text = data?.text ?? d.text;
      const style = d.style ?? d.options ?? (d.color || d.width ? { color: d.color, width: d.width } : undefined);
      const result: ApiDrawing = {
        id: d.id ?? d._id,
        tool,
        symbol: d.symbol,
        interval: d.interval ?? d.timeRange,
        points: Array.isArray(points) && points.length > 0 ? points : undefined,
        point,
        text,
        style
      };
      return result;
    }).filter((x: ApiDrawing | null): x is ApiDrawing => {
      if (!x || !x.tool) return false;
      if (x.tool === 'pen') return !!(x.points && x.points.length >= 2);
      if (x.tool === 'line') return !!(x.points && x.points.length >= 1);
      if (x.tool === 'arrow') return !!(x.points && x.points.length >= 2);
      if (x.tool === 'text') return x.text != null;
      return false;
    });
    this.loadDrawingsFromApi(flat, clearExisting);
  }

  /** Export current drawings for update-chart-drawings API (ChartDrawingApi[]). */
  getDrawingsForApi(symbol: string, timeRange: string, interval: string): ChartDrawingApi[] {
    const result: ChartDrawingApi[] = [];
    const now = new Date().toISOString();
    let firstTime = 0;
    let lastTime = 1;
    if (this.fullDataset && this.fullDataset.length >= 2) {
      firstTime = Number(this.fullDataset[0].time);
      lastTime = Number(this.fullDataset[this.fullDataset.length - 1].time);
    }
    const toMs = (t: number) => (t <= 10000000000 ? t * 1000 : t);
    const timeToSeconds = (time: Time): number => {
      if (typeof time === 'number') return time;
      const b = time as { year?: number; month?: number; day?: number };
      if (b?.year != null && b?.month != null && b?.day != null) {
        return Math.floor(new Date(b.year, b.month - 1, b.day).getTime() / 1000);
      }
      return (time as any)?.timestamp ?? 0;
    };

    this.state.lines.forEach(line => {
      result.push({
        id: line.id,
        tool: 'line',
        symbol,
        timeRange,
        interval,
        data: {
          points: [
            { time: toMs(firstTime), price: line.price },
            { time: toMs(lastTime), price: line.price }
          ]
        },
        style: { color: line.color, width: line.lineWidth },
        createdAt: now,
        updatedAt: now
      });
    });

    this.state.textAnnotations.forEach(ann => {
      const t = timeToSeconds(ann.time);
      result.push({
        id: ann.id,
        tool: 'text',
        symbol,
        timeRange,
        interval,
        data: {
          text: ann.text,
          points: [{ time: toMs(t), price: ann.price }]
        },
        style: { color: ann.fontColor, fontSize: ann.fontSize },
        createdAt: now,
        updatedAt: now
      });
    });

    this.state.penDrawings.forEach(d => {
      result.push({
        id: d.id,
        tool: 'pen',
        symbol,
        timeRange,
        interval,
        data: {
          points: d.points.map(p => {
            const t = typeof p.time === 'number' ? p.time : (p.time as any)?.timestamp ?? 0;
            return { time: toMs(t), price: p.price };
          })
        },
        style: { color: d.color, width: d.lineWidth },
        createdAt: d.createdAt?.toISOString?.() ?? now,
        updatedAt: now
      });
    });

    this.state.arrowDrawings.forEach(a => {
      result.push({
        id: a.id,
        tool: 'arrow',
        symbol,
        timeRange,
        interval,
        data: {
          points: [
            { time: toMs(timeToSeconds(a.startTime)), price: a.startPrice },
            { time: toMs(timeToSeconds(a.endTime)), price: a.endPrice }
          ]
        },
        style: { color: a.color, width: a.lineWidth },
        createdAt: a.createdAt?.toISOString?.() ?? now,
        updatedAt: now
      });
    });

    return result;
  }

  private isDataUrlImage(text: string): boolean {
    const t = text.trim();
    return /^data:image\/(png|jpeg|jpe|gif|webp|bmp);base64,/i.test(t);
  }

  private setTextAnnotationVisualContent(annotation: TextAnnotation, textElement: HTMLElement): void {
    if (this.isDataUrlImage(annotation.text)) {
      textElement.innerHTML = '';
      textElement.style.whiteSpace = 'normal';
      const img = document.createElement('img');
      img.className = 'text-annotation-image';
      img.src = annotation.text;
      img.alt = 'Pasted image';
      img.draggable = false;
      img.style.maxWidth = 'min(320px, 80vw)';
      img.style.maxHeight = '240px';
      img.style.objectFit = 'contain';
      img.style.display = 'block';
      textElement.appendChild(img);
    } else {
      textElement.innerHTML = '';
      textElement.style.whiteSpace = 'nowrap';
      textElement.innerText = annotation.text;
    }
  }

  private renderTextAnnotation(annotation: TextAnnotation): void {
    if (!this.chart) return;

    // Create a DOM element for the text annotation
    const chartElement = this.chart.chartElement();
    const textElement = document.createElement('div');
    textElement.id = `text-annotation-${annotation.id}`;
    textElement.className = 'text-annotation';
    textElement.style.position = 'absolute';
    textElement.style.zIndex = '10';
    textElement.style.fontSize = `${annotation.fontSize}px`;
    textElement.style.color = annotation.fontColor;
    textElement.style.backgroundColor = annotation.backgroundColor || 'transparent';
    textElement.style.borderColor = annotation.borderColor || 'transparent';
    textElement.style.borderWidth = `${annotation.borderWidth || 0}px`;
    textElement.style.borderStyle = 'solid';
    textElement.style.padding = '4px 8px';
    textElement.style.borderRadius = '4px';
    textElement.style.cursor = 'move';
    textElement.style.fontWeight = annotation.style === 'bold' ? 'bold' : 'normal';
    textElement.style.fontStyle = annotation.style === 'italic' ? 'italic' : 'normal';
    this.setTextAnnotationVisualContent(annotation, textElement);

    // Position the text annotation
    this.positionTextAnnotation(annotation, textElement);

    // Add click handler for selection/editing (with small delay to prevent auto-selection)
    let isReady = false;
    setTimeout(() => { isReady = true; }, 100); // Prevent immediate selection

    textElement.addEventListener('click', (e) => {
      e.stopPropagation();
      if (isReady) {
        this.selectTextAnnotation(annotation.id);
      }
    });

    // Add double-click handler for inline editing
    textElement.addEventListener('dblclick', (e) => {
      e.stopPropagation();
      if (isReady) {
        this.startInlineTextEdit(annotation.id, textElement);
      }
    });

    // Add drag functionality
    this.addTextDragHandlers(textElement, annotation);

    chartElement.appendChild(textElement);

    // Set up position update listeners if this is the first text annotation
    if (this.state.textAnnotations.length === 1) {
      this.setupTextAnnotationPositionUpdates();
    }

  }

  private positionTextAnnotation(annotation: TextAnnotation, element: HTMLElement): void {
    if (!this.chart) return;

    // Always calculate position from time and price to ensure text sticks to chart coordinates
    const timeScale = this.chart.timeScale();
    const x = timeScale.timeToCoordinate(annotation.time);


    if (x !== null) {
      // Calculate Y position based on price using the main series
      let y = 50; // Default fallback position

      try {
        // Try to get the price coordinate from the main series
        // First try to use the stored mainSeries reference
        let targetSeries = this.mainSeries;

        // If mainSeries is not available, we can't calculate price coordinates
        if (!targetSeries) {
        }

        if (targetSeries) {
          const priceCoordinate = targetSeries.priceToCoordinate(annotation.price);
          if (priceCoordinate !== null) {
            y = priceCoordinate;
          }
        } else {
        }
      } catch (error) {
        // Use a percentage of chart height as fallback
        const chartHeight = this.chart.chartElement().clientHeight;
        y = chartHeight * 0.3; // Position at 30% from top as fallback
      }

      element.style.left = `${x + 5}px`; // Small offset to avoid cursor overlap
      element.style.top = `${y - 10}px`; // Small offset to position above price level

      // Update stored coordinates for reference
      annotation.x = x;
      annotation.y = y;
    } else {
    }
  }

  private updateAllTextAnnotationPositions(): void {
    if (!this.chart) return;


    this.state.textAnnotations.forEach(annotation => {
      const element = document.getElementById(`text-annotation-${annotation.id}`);
      if (element) {
        this.positionTextAnnotation(annotation, element);
      } else {
      }
    });
  }

  private addTextDragHandlers(element: HTMLElement, annotation: TextAnnotation): void {
    let isDragging = false;
    let startX = 0;
    let startY = 0;
    let startLeft = 0;
    let startTop = 0;

    element.addEventListener('mousedown', (e) => {
      e.preventDefault();
      e.stopPropagation();

      isDragging = true;
      startX = e.clientX;
      startY = e.clientY;
      startLeft = parseInt(element.style.left) || 0;
      startTop = parseInt(element.style.top) || 0;

      element.style.cursor = 'grabbing';
      document.body.style.cursor = 'grabbing';
    });

    document.addEventListener('mousemove', (e) => {
      if (!isDragging) return;

      e.preventDefault();
      const deltaX = e.clientX - startX;
      const deltaY = e.clientY - startY;

      const newLeft = startLeft + deltaX;
      const newTop = startTop + deltaY;

      // Keep within chart bounds
      const chartRect = this.chart!.chartElement().getBoundingClientRect();
      const elementRect = element.getBoundingClientRect();

      const maxLeft = chartRect.width - elementRect.width;
      const maxTop = chartRect.height - elementRect.height;

      const clampedLeft = Math.max(0, Math.min(newLeft, maxLeft));
      const clampedTop = Math.max(0, Math.min(newTop, maxTop));

      element.style.left = `${clampedLeft}px`;
      element.style.top = `${clampedTop}px`;

      // Update annotation coordinates
      annotation.x = clampedLeft;
      annotation.y = clampedTop;
    });

    document.addEventListener('mouseup', () => {
      if (isDragging) {
        isDragging = false;
        element.style.cursor = 'move';
        document.body.style.cursor = 'default';

        // Update time and price based on new position
        this.updateTextAnnotationPosition(annotation);
        if (this.onLinesChangedCallback) this.onLinesChangedCallback();
      }
    });
  }

  private updateTextAnnotationPosition(annotation: TextAnnotation): void {
    if (!this.chart) return;

    // Convert pixel coordinates back to time/price
    const timeScale = this.chart.timeScale();
    const time = timeScale.coordinateToTime(annotation.x);

    if (time) {
      annotation.time = time;

      // Calculate price from Y coordinate
      try {
        const mainSeries = this.mainSeries; // Use stored main series reference
        if (mainSeries) {
          const price = mainSeries.coordinateToPrice(annotation.y);
          if (price !== null) {
            annotation.price = price;
          }
        }
      } catch (error) {
      }

    }
  }

  private removeTextAnnotationElement(annotationId: string): void {
    const element = document.getElementById(`text-annotation-${annotationId}`);
    if (element) {
      element.remove();
    }
  }

  private editTextAnnotation(annotationId: string): void {
    // Use the new selection-based editing instead of prompt
    this.selectTextAnnotation(annotationId);
  }

  // Text annotation selection and management
  selectTextAnnotation(annotationId: string): void {
    const annotation = this.state.textAnnotations.find(a => a.id === annotationId);
    if (!annotation) return;

    // Remove selected class from all text annotations
    this.state.textAnnotations.forEach(a => {
      const el = document.getElementById(`text-annotation-${a.id}`);
      if (el) el.classList.remove('selected');
    });

    // Get the DOM element position for toolbar placement
    const element = document.getElementById(`text-annotation-${annotationId}`);
    if (element && this.onTextSelectedCallback) {
      // Add selected class
      element.classList.add('selected');

      const rect = element.getBoundingClientRect();
      const chartRect = this.chart!.chartElement().getBoundingClientRect();

      // Calculate relative position within chart
      const x = rect.left - chartRect.left;
      const y = rect.top - chartRect.top;

      this.onTextSelectedCallback(annotation, x, y);
    }
  }

  // Method to deselect all text annotations
  deselectAllTextAnnotations(): void {
    this.state.textAnnotations.forEach(a => {
      const el = document.getElementById(`text-annotation-${a.id}`);
      if (el) el.classList.remove('selected');
    });

    // Clear inline editing state when deselecting
    this.state.isInlineEditing = false;
  }

  // Inline text editing functionality
  startInlineTextEdit(annotationId: string, textElement: HTMLElement): void {
    const annotation = this.state.textAnnotations.find(a => a.id === annotationId);
    if (!annotation) return;

    // Set inline editing state to prevent new text creation
    this.state.isInlineEditing = true;

    // Hide the text toolbar if it's showing
    if (this.onTextSelectedCallback) {
      this.onTextSelectedCallback(null);
    }

    // Store original text and element properties
    const originalText = annotation.text;
    const originalCursor = textElement.style.cursor;
    const originalUserSelect = textElement.style.userSelect;

    const chartElement = this.chart!.chartElement();

    const applyEditStyles = (el: HTMLElement): void => {
      el.style.position = 'absolute';
      el.style.left = textElement.style.left;
      el.style.top = textElement.style.top;
      el.style.fontSize = textElement.style.fontSize;
      el.style.color = textElement.style.color;
      el.style.backgroundColor = textElement.style.backgroundColor;
      el.style.borderColor = textElement.style.borderColor;
      el.style.borderWidth = textElement.style.borderWidth;
      el.style.borderStyle = 'solid';
      el.style.borderRadius = textElement.style.borderRadius;
      el.style.fontWeight = textElement.style.fontWeight;
      el.style.fontStyle = textElement.style.fontStyle;
      el.style.fontFamily = textElement.style.fontFamily || 'var(--font-family-primary)';
      el.style.padding = '4px 8px';
      el.style.margin = '0';
      el.style.outline = '2px solid var(--primary-600)';
      el.style.outlineOffset = '0px';
      el.style.zIndex = '11';
      el.style.minWidth = `${Math.max(textElement.offsetWidth, 100)}px`;
      el.style.cursor = 'text';
    };

    const normalizeSavedText = (raw: string): string => {
      const t = raw.trim();
      if (this.isDataUrlImage(t)) {
        return t;
      }
      return t;
    };

    const attachPasteImageHandler = (ta: HTMLTextAreaElement, onImage: (dataUrl: string) => void): void => {
      ta.addEventListener('paste', (e) => {
        const cd = e.clipboardData;
        if (!cd) {
          return;
        }
        for (let i = 0; i < cd.items.length; i++) {
          const item = cd.items[i];
          if (item.kind === 'file' && item.type.startsWith('image/')) {
            e.preventDefault();
            const file = item.getAsFile();
            if (!file) {
              return;
            }
            const reader = new FileReader();
            reader.onload = () => {
              const dataUrl = reader.result as string;
              ta.value = dataUrl;
              onImage(dataUrl);
            };
            reader.readAsDataURL(file);
            return;
          }
        }
      });
    };

    textElement.style.display = 'none';

    let editorRoot: HTMLElement;

    if (this.isDataUrlImage(originalText)) {
      editorRoot = document.createElement('div');
      editorRoot.className = 'text-annotation-inline-edit text-annotation-inline-edit--image';
      applyEditStyles(editorRoot);
      editorRoot.style.minWidth = `${Math.max(textElement.offsetWidth, 180)}px`;
      editorRoot.style.maxWidth = 'min(360px, 90vw)';

      let currentDataUrl = originalText;

      const preview = document.createElement('img');
      preview.className = 'text-annotation-inline-preview';
      preview.src = originalText;
      preview.alt = 'Preview';
      preview.style.maxWidth = '100%';
      preview.style.maxHeight = '200px';
      preview.style.objectFit = 'contain';
      preview.style.display = 'block';
      preview.style.marginBottom = '6px';

      const textarea = document.createElement('textarea');
      textarea.className = 'text-annotation-inline-edit-field';
      textarea.rows = 2;
      textarea.placeholder = 'Paste image (Ctrl+V / ⌘V) to replace';
      textarea.style.width = '100%';
      textarea.style.boxSizing = 'border-box';
      textarea.style.resize = 'vertical';
      textarea.style.minHeight = '48px';
      textarea.style.fontSize = '12px';
      textarea.value = '';

      attachPasteImageHandler(textarea, (dataUrl) => {
        currentDataUrl = dataUrl;
        preview.src = dataUrl;
      });

      editorRoot.appendChild(preview);
      editorRoot.appendChild(textarea);
      chartElement.appendChild(editorRoot);

      textarea.focus();

      const saveEdit = () => {
        const next = normalizeSavedText(currentDataUrl);
        if (next && next !== originalText) {
          this.updateTextAnnotation(annotationId, { text: next });
        }

        this.state.isInlineEditing = false;
        textElement.style.display = '';
        textElement.style.cursor = originalCursor;
        textElement.style.userSelect = originalUserSelect;
        editorRoot.remove();
      };

      const cancelEdit = () => {
        this.state.isInlineEditing = false;
        textElement.style.display = '';
        textElement.style.cursor = originalCursor;
        textElement.style.userSelect = originalUserSelect;
        editorRoot.remove();
      };

      textarea.addEventListener('blur', saveEdit);
      textarea.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') {
          e.preventDefault();
          cancelEdit();
        } else if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
          e.preventDefault();
          saveEdit();
        }
      });
      editorRoot.addEventListener('click', (e) => e.stopPropagation());
      editorRoot.addEventListener('dblclick', (e) => e.stopPropagation());
      return;
    }

    const textarea = document.createElement('textarea');
    textarea.className = 'text-annotation-inline-edit text-annotation-inline-edit-field';
    textarea.value = originalText;
    applyEditStyles(textarea);
    textarea.style.minHeight = `${Math.max(32, textElement.offsetHeight || 32)}px`;
    textarea.style.resize = 'both';
    textarea.style.boxSizing = 'border-box';

    chartElement.appendChild(textarea);

    textarea.focus();
    textarea.select();

    attachPasteImageHandler(textarea, (dataUrl) => {
      /* value already set; optional: could update live */
    });

    const saveEdit = () => {
      const newText = normalizeSavedText(textarea.value);
      if (newText && newText !== originalText) {
        this.updateTextAnnotation(annotationId, { text: newText });
      }

      this.state.isInlineEditing = false;
      textElement.style.display = '';
      textElement.style.cursor = originalCursor;
      textElement.style.userSelect = originalUserSelect;
      textarea.remove();
    };

    const cancelEdit = () => {
      this.state.isInlineEditing = false;
      textElement.style.display = '';
      textElement.style.cursor = originalCursor;
      textElement.style.userSelect = originalUserSelect;
      textarea.remove();
    };

    textarea.addEventListener('blur', saveEdit);
    textarea.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        cancelEdit();
      } else if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
        e.preventDefault();
        saveEdit();
      }
    });

    textarea.addEventListener('click', (e) => e.stopPropagation());
    textarea.addEventListener('dblclick', (e) => e.stopPropagation());
  }

  updateTextAnnotation(annotationId: string, updates: Partial<TextAnnotation>): void {
    const annotation = this.state.textAnnotations.find(a => a.id === annotationId);
    if (!annotation) {
      return;
    }

    // Update the annotation properties
    Object.assign(annotation, updates);

    // Update the DOM element
    const element = document.getElementById(`text-annotation-${annotationId}`);
    if (element) {
      if (updates.text !== undefined) {
        this.setTextAnnotationVisualContent(annotation, element);
      }
      if (updates.fontSize !== undefined) {
        element.style.fontSize = `${annotation.fontSize}px`;
      }
      if (updates.fontColor !== undefined) {
        element.style.color = annotation.fontColor;
      }
      if (updates.style !== undefined) {
        element.style.fontWeight = annotation.style === 'bold' ? 'bold' : 'normal';
        element.style.fontStyle = annotation.style === 'italic' ? 'italic' : 'normal';
      }
      if ('backgroundColor' in updates) {
        element.style.backgroundColor = annotation.backgroundColor || 'transparent';
      }
      if ('borderColor' in updates) {
        element.style.borderColor = annotation.borderColor || 'transparent';
      }
      if (updates.borderWidth !== undefined) {
        element.style.borderWidth = `${annotation.borderWidth || 0}px`;
      }
    }

    if (this.onLinesChangedCallback) {
      this.onLinesChangedCallback();
    }
  }

  removeTextAnnotation(annotationId: string): void {
    const index = this.state.textAnnotations.findIndex(a => a.id === annotationId);
    if (index === -1) return;

    // Remove DOM element
    this.removeTextAnnotationElement(annotationId);

    // Remove from state
    this.state.textAnnotations.splice(index, 1);

    if (this.onLinesChangedCallback) {
      this.onLinesChangedCallback();
    }
  }

  addHorizontalLine(price: number, name?: string, style?: { color?: string; lineWidth?: number }, existingId?: string): string {
    if (!this.mainSeries) return '';

    const lineId = existingId || `line_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    const lineName = name || `Line #${this.lineCounter}`;
    if (!existingId) this.lineCounter++;
    const color = style?.color ?? '#2962FF';
    const lineWidth = style?.lineWidth ?? 2;

    const priceLine = this.mainSeries.createPriceLine({
      price: price,
      color: color,
      lineWidth: lineWidth as any,
      lineStyle: 0 as any,
      axisLabelVisible: true,
      title: lineName,
    });

    this.horizontalLines.set(lineId, priceLine);

    const newLine: HorizontalLine = {
      id: lineId,
      price: price,
      color: color,
      lineWidth: lineWidth,
      lineStyle: 0,
      name: lineName
    };

    this.state.lines.push(newLine);
    this.saveLinesToLocalStorage();

    if (this.onLinesChangedCallback) {
      this.onLinesChangedCallback();
    }

    return lineId;
  }

  removeHorizontalLine(lineId: string): void {
    if (this.selectedLineId === lineId) {
      this.deselectLine();
    }
    // Remove from chart
    const priceLine = this.horizontalLines.get(lineId);
    if (priceLine && this.mainSeries) {
      this.mainSeries.removePriceLine(priceLine);
      this.horizontalLines.delete(lineId);
    }

    // Remove from state
    this.state.lines = this.state.lines.filter(line => line.id !== lineId);
    this.saveLinesToLocalStorage();

    // When no lines remain, clear Line drawing mode so toolbar icon is not left active
    if (this.state.lines.length === 0) {
      this.state.isDrawingMode = false;
    }

    // Notify component of changes
    if (this.onLinesChangedCallback) {
      this.onLinesChangedCallback();
    }
  }

  updateHorizontalLineName(lineId: string, newName: string): void {
    // Update in state
    const line = this.state.lines.find(l => l.id === lineId);
    if (line) {
      line.name = newName;

      // Update the price line title
      const priceLine = this.horizontalLines.get(lineId);
      if (priceLine && this.mainSeries) {
        // Remove and recreate with new title
        this.mainSeries.removePriceLine(priceLine);
        const newPriceLine = this.mainSeries.createPriceLine({
          price: line.price,
          color: line.color,
          lineWidth: line.lineWidth as any,
          lineStyle: line.lineStyle as any,
          axisLabelVisible: true,
          title: newName,
        });
        this.horizontalLines.set(lineId, newPriceLine);
      }

      this.saveLinesToLocalStorage();

      // Notify component of changes
      if (this.onLinesChangedCallback) {
        this.onLinesChangedCallback();
      }
    }
  }

  updateHorizontalLineColor(lineId: string, newColor: string): void {
    // Update in state
    const line = this.state.lines.find(l => l.id === lineId);
    if (line) {
      line.color = newColor;

      // Update the price line color
      const priceLine = this.horizontalLines.get(lineId);
      if (priceLine && this.mainSeries) {
        // Remove and recreate with new color
        this.mainSeries.removePriceLine(priceLine);
        const newPriceLine = this.mainSeries.createPriceLine({
          price: line.price,
          color: newColor,
          lineWidth: line.lineWidth as any,
          lineStyle: line.lineStyle as any,
          axisLabelVisible: true,
          title: line.name,
        });
        this.horizontalLines.set(lineId, newPriceLine);
      }

      this.saveLinesToLocalStorage();

      // Notify component of changes
      if (this.onLinesChangedCallback) {
        this.onLinesChangedCallback();
      }
    }
  }

  updateHorizontalLinePrice(lineId: string, newPrice: number): void {
    this.updateLinePrice(lineId, newPrice);
  }

  clearAllHorizontalLines(): void {
    this.deselectLine();
    this.state.isDrawingMode = false;
    // Remove all lines from chart
    this.horizontalLines.forEach((priceLine) => {
      if (this.mainSeries) {
        this.mainSeries.removePriceLine(priceLine);
      }
    });

    // Clear references and state
    this.horizontalLines.clear();
    this.state.lines = [];
    this.saveLinesToLocalStorage();

    // Notify component of changes
    if (this.onLinesChangedCallback) {
      this.onLinesChangedCallback();
    }
  }

  // System Line Management Methods
  loadSystemLines(systemLines: SystemLine[]): void {
    // Clear existing system lines
    this.clearAllSystemLines();

    // Add new system lines
    this.state.systemLines = systemLines;

    // Create visual lines on chart
    systemLines.forEach(line => {
      if (line.isVisible && this.state.systemLineVisibility[line.type]) {
        this.createSystemLineOnChart(line);
      }
    });

    // Save to localStorage
    this.saveSystemLinesToLocalStorage();

    // Notify component of changes
    if (this.onLinesChangedCallback) {
      this.onLinesChangedCallback();
    }

  }

  private createSystemLineOnChart(line: SystemLine): void {
    if (!this.mainSeries) return;

    const priceLine = this.mainSeries.createPriceLine({
      price: line.price,
      color: line.color,
      lineWidth: line.lineWidth as any,
      lineStyle: line.lineStyle as any,
      axisLabelVisible: true,
      title: `${this.getSystemLineIcon(line.type)} ${line.name}`,
    });

    this.systemLines.set(line.id, priceLine);
  }

  private getSystemLineIcon(type: SystemLine['type']): string {
    switch (type) {
      case 'SUPPORT': return '[SUPPORT]'; // Support level
      case 'RESISTANCE': return '[RESISTANCE]'; // Resistance level
      default: return '[CHART]';
    }
  }

  toggleSystemLineVisibility(type: SystemLine['type']): void {
    this.state.systemLineVisibility[type] = !this.state.systemLineVisibility[type];

    // Update visibility of all lines of this type
    this.state.systemLines
      .filter(line => line.type === type)
      .forEach(line => {
        const priceLine = this.systemLines.get(line.id);
        if (priceLine && this.mainSeries) {
          // Remove existing line
          this.mainSeries.removePriceLine(priceLine);
          this.systemLines.delete(line.id);

          // Recreate if should be visible
          if (this.state.systemLineVisibility[type] && line.isVisible) {
            this.createSystemLineOnChart(line);
          }
        }
      });

    // Notify component of changes
    if (this.onLinesChangedCallback) {
      this.onLinesChangedCallback();
    }
  }

  toggleSystemLineIndividualVisibility(lineId: string): void {
    const line = this.state.systemLines.find(l => l.id === lineId);
    if (!line) return;

    line.isVisible = !line.isVisible;

    const priceLine = this.systemLines.get(lineId);
    if (priceLine && this.mainSeries) {
      // Remove existing line
      this.mainSeries.removePriceLine(priceLine);
      this.systemLines.delete(lineId);

      // Recreate if should be visible
      if (line.isVisible && this.state.systemLineVisibility[line.type]) {
        this.createSystemLineOnChart(line);
      }
    }

    // Save changes
    this.saveSystemLinesToLocalStorage();

    // Notify component of changes
    if (this.onLinesChangedCallback) {
      this.onLinesChangedCallback();
    }
  }

  clearAllSystemLines(): void {
    // Remove all system lines from chart
    this.systemLines.forEach((priceLine, lineId) => {
      if (this.mainSeries) {
        this.mainSeries.removePriceLine(priceLine);
      }
    });

    // Clear references and state
    this.systemLines.clear();
    this.state.systemLines = [];
    this.saveSystemLinesToLocalStorage();

    // Notify component of changes
    if (this.onLinesChangedCallback) {
      this.onLinesChangedCallback();
    }

  }

  getSystemLines(): SystemLine[] {
    return [...this.state.systemLines];
  }

  getSystemLineVisibility(): Record<SystemLine['type'], boolean> {
    return { ...this.state.systemLineVisibility };
  }

  private saveSystemLinesToLocalStorage(): void {
    if (this.currentSymbol) {
      const key = `system_lines_${this.currentSymbol}`;
      localStorage.setItem(key, JSON.stringify(this.state.systemLines));

      const visibilityKey = `system_line_visibility_${this.currentSymbol}`;
      localStorage.setItem(visibilityKey, JSON.stringify(this.state.systemLineVisibility));
    }
  }

  private loadSystemLinesFromLocalStorage(): void {
    if (this.currentSymbol) {
      // Load system lines
      const key = `system_lines_${this.currentSymbol}`;
      const savedLines = localStorage.getItem(key);

      if (savedLines) {
        try {
          const lines: SystemLine[] = JSON.parse(savedLines);
          this.state.systemLines = lines;

          // Recreate system lines on chart
          lines.forEach(line => {
            if (line.isVisible && this.state.systemLineVisibility[line.type]) {
              this.createSystemLineOnChart(line);
            }
          });

        } catch (error) {
        }
      }

      // Load visibility settings
      const visibilityKey = `system_line_visibility_${this.currentSymbol}`;
      const savedVisibility = localStorage.getItem(visibilityKey);

      if (savedVisibility) {
        try {
          const visibility = JSON.parse(savedVisibility);
          this.state.systemLineVisibility = { ...this.state.systemLineVisibility, ...visibility };
        } catch (error) {
        }
      }
    }
  }

  private saveLinesToLocalStorage(): void {
    if (this.currentSymbol) {
      const key = `horizontal_lines_${this.currentSymbol}`;
      localStorage.setItem(key, JSON.stringify(this.state.lines));
    }
  }

  private loadLinesFromLocalStorage(): void {
    if (this.currentSymbol) {
      const key = `horizontal_lines_${this.currentSymbol}`;
      const savedLines = localStorage.getItem(key);

      if (savedLines) {
        try {
          const lines: HorizontalLine[] = JSON.parse(savedLines);
          this.state.lines = lines;

          // Update line counter to continue from highest existing number
          const lineNumbers = lines
            .map(line => {
              const match = line.name.match(/Line #(\d+)/);
              return match ? parseInt(match[1]) : 0;
            })
            .filter(num => num > 0);

          if (lineNumbers.length > 0) {
            this.lineCounter = Math.max(...lineNumbers) + 1;
          }

          // Recreate price lines on chart
          lines.forEach(line => {
            if (this.mainSeries) {
              const priceLine = this.mainSeries.createPriceLine({
                price: line.price,
                color: line.color,
                lineWidth: line.lineWidth as any,
                lineStyle: line.lineStyle as any,
                axisLabelVisible: true,
                title: line.name,
              });
              this.horizontalLines.set(line.id, priceLine);
            }
          });

        } catch (error) {
        }
      }
    }
  }

  // Check if click is on a marker
  private checkMarkerClick(x: number, y: number, time: Time): boolean {
    const clickThreshold = 20; // pixels - area around marker to detect clicks


    // Find visible markers at or near the clicked time
    const visibleMarkers = this.state.markers.filter(marker =>
      this.state.markerVisibility[marker.category]
    );


    for (const marker of visibleMarkers) {
      // Check if the time matches (within reasonable range for daily data)
      const markerTime = typeof marker.time === 'string' ? parseInt(marker.time) : marker.time as number;
      const clickTime = typeof time === 'string' ? parseInt(time) : time as number;


      // For daily data, allow some tolerance (7 days = 604800 seconds)
      const timeDiff = Math.abs(markerTime - clickTime);

      if (timeDiff <= 604800) { // Within 7 days
        // Check if click is near the marker position
        const basePrice = marker.position === 'aboveBar' ? this.getHighPriceAtTime(marker.time) + 1 :
          marker.position === 'belowBar' ? this.getLowPriceAtTime(marker.time) - 1 :
            this.getClosePriceAtTime(marker.time);

        const markerY = this.mainSeries?.priceToCoordinate(basePrice);


        if (markerY !== undefined && markerY !== null) {
          const distance = Math.abs(y - markerY);

          if (distance <= clickThreshold) {
            this.showMarkerTooltip(marker, x, y);
            return true;
          }
        }
      }
    }

    return false;
  }

  // Get price data at specific time for marker positioning
  private getHighPriceAtTime(time: Time): number {
    // Try exact match first
    let data = this.fullDataset.find(d => d.time === time);

    // If no exact match, find closest time within reasonable range
    if (!data) {
      const targetTime = typeof time === 'string' ? parseInt(time) : time as number;
      let closestData: ChartData | undefined = undefined;
      let minDiff = Infinity;

      for (const d of this.fullDataset) {
        const dataTime = typeof d.time === 'string' ? parseInt(d.time) : d.time as number;
        const diff = Math.abs(dataTime - targetTime);
        if (diff < minDiff && diff <= 604800) { // Within 7 days
          minDiff = diff;
          closestData = d;
        }
      }
      data = closestData;
    }

    return data?.high || 200; // Default fallback price
  }

  private getLowPriceAtTime(time: Time): number {
    // Try exact match first
    let data = this.fullDataset.find(d => d.time === time);

    // If no exact match, find closest time within reasonable range
    if (!data) {
      const targetTime = typeof time === 'string' ? parseInt(time) : time as number;
      let closestData: ChartData | undefined = undefined;
      let minDiff = Infinity;

      for (const d of this.fullDataset) {
        const dataTime = typeof d.time === 'string' ? parseInt(d.time) : d.time as number;
        const diff = Math.abs(dataTime - targetTime);
        if (diff < minDiff && diff <= 604800) { // Within 7 days
          minDiff = diff;
          closestData = d;
        }
      }
      data = closestData;
    }

    return data?.low || 200; // Default fallback price
  }

  private getClosePriceAtTime(time: Time): number {
    // Try exact match first
    let data = this.fullDataset.find(d => d.time === time);

    // If no exact match, find closest time within reasonable range
    if (!data) {
      const targetTime = typeof time === 'string' ? parseInt(time) : time as number;
      let closestData: ChartData | undefined = undefined;
      let minDiff = Infinity;

      for (const d of this.fullDataset) {
        const dataTime = typeof d.time === 'string' ? parseInt(d.time) : d.time as number;
        const diff = Math.abs(dataTime - targetTime);
        if (diff < minDiff && diff <= 604800) { // Within 7 days
          minDiff = diff;
          closestData = d;
        }
      }
      data = closestData;
    }

    return data?.close || 200; // Default fallback price
  }

  // Show marker tooltip
  private showMarkerTooltip(marker: ChartMarker, x: number, y: number): void {
    if (this.onMarkerTooltipChangedCallback) {
      const tooltipData: MarkerTooltipData = {
        visible: true,
        x: x,
        y: y,
        marker: marker
      };
      this.onMarkerTooltipChangedCallback(tooltipData);
    }
  }

  // Hide marker tooltip
  hideMarkerTooltip(): void {
    if (this.onMarkerTooltipChangedCallback) {
      this.onMarkerTooltipChangedCallback(null);
    }
  }

  private cancelTooltipHide(): void {
    if (this.tooltipHideTimeout != null) {
      clearTimeout(this.tooltipHideTimeout);
      this.tooltipHideTimeout = null;
    }
  }

  private scheduleTooltipHide(): void {
    if (this.tooltipHideTimeout != null) return;
    this.tooltipHideTimeout = setTimeout(() => {
      this.tooltipHideTimeout = null;
      if (this.onTooltipChangedCallback) {
        this.onTooltipChangedCallback(null);
      }
      if (this.onATRTooltipChangedCallback) {
        this.onATRTooltipChangedCallback(null);
      }
    }, TradingViewChartService.TOOLTIP_HIDE_DELAY_MS);
  }

  // Handle chart click events for line drawing
  private setupChartClickHandler(): void {
    if (!this.chart) return;

    this.chart.subscribeClick((param) => {
      if (!this.chart) return;
      if (param.point) {
        const chartEl = this.chart.chartElement();
        const chartH = chartEl.clientHeight || 0;
        const x = param.point.x;
        const yClamped = chartH > 0 ? Math.max(0, Math.min(chartH, param.point.y)) : param.point.y;

        // Use library price when available; fall back to our own coordinate helper
        // so clicks in the right offset / price-scale area still resolve.
        let price = this.mainSeries?.coordinateToPrice(yClamped) as number | null | undefined;
        let time = param.time as Time | undefined;

        // When the library couldn't resolve time/price (right-side empty area), use our fallback
        if (time === undefined || price === undefined || price === null) {
          const fallback = this.pixelToChartCoordinates(x, yClamped);
          if (fallback) {
            if (time === undefined) time = fallback.time;
            if (price === undefined || price === null) price = fallback.price;
          }
        }

        // First priority: Handle measuring mode clicks
        if (this.state.isMeasuringMode && time && price !== undefined && price !== null) {
          const priceValue = typeof price === 'number' ? price : price as number;
          if (!this.measureStartPoint) {
            this.clearAllMeasurements();

            this.measureStartPoint = {
              time,
              price: priceValue,
              x: param.point.x,
              y: param.point.y
            };
          } else {
            this.completeMeasurement(time, priceValue, param.point.x, param.point.y);
          }
          return;
        }

        // Arrow mode is handled by its own DOM click listener in initializeArrowTool
        if (this.state.isArrowMode) {
          return;
        }

        // Second, check if we clicked on a marker
        if (time && this.checkMarkerClick(x, yClamped, time)) {
          return;
        }

        if (price !== undefined && price !== null) {
          // Check if we clicked on an existing line (prioritize selection)
          const threshold = 10;
          let closestLine: HorizontalLine | null = null;
          let minDistance = Infinity;

          for (const line of this.state.lines) {
            const lineY = this.mainSeries?.priceToCoordinate(line.price);

            if (lineY !== undefined && lineY !== null) {
              const distance = Math.abs(yClamped - lineY);
              if (distance < threshold && distance < minDistance) {
                minDistance = distance;
                closestLine = line;
              }
            }
          }

          if (closestLine !== null) {
            if (this.selectedLineId === closestLine.id) {
              return;
            }
            this.selectLine(closestLine.id, param.point.x, param.point.y);
            const chartElement = this.chart!.chartElement();
            chartElement.style.cursor = 'ns-resize';
            return;
          }

          // Handle text mode
          if (this.state.isTextMode && !this.state.isInlineEditing && !this.selectedLineId && time !== undefined) {
            this.addTextAnnotation(time as Time, price as number, 'New Text', param.point?.x, param.point?.y);
            return;
          }

          // Drawing mode — place a horizontal line
          if (this.state.isDrawingMode && !this.selectedLineId) {
            this.addHorizontalLine(price as number);
            return;
          }

          if (this.selectedLineId) {
            this.deselectLine();
            return;
          }

          this.deselectLine();
        } else {
          this.deselectLine();
        }
      }
    });

    // Add mouse and touch event handlers for dragging
    const chartElement = this.chart.chartElement();

    chartElement.addEventListener('mousedown', (event) => {
      // Get mouse coordinates and price for line detection (clamp Y so upper/lower chart areas work)
      const rect = chartElement.getBoundingClientRect();
      const x = event.clientX - rect.left;
      const yRaw = event.clientY - rect.top;
      const y = rect.height > 0 ? Math.max(0, Math.min(rect.height, yRaw)) : yRaw;
      const price = this.mainSeries?.coordinateToPrice(y);


      // Check for line selection first (regardless of current selection state)
      if (price !== undefined && price !== null && event.button === 0) {
        const threshold = 10;
        let closestLine: HorizontalLine | null = null;
        let minDistance = Infinity;

        for (const line of this.state.lines) {
          const lineY = this.mainSeries?.priceToCoordinate(line.price);

          if (lineY !== undefined && lineY !== null) {
            const distance = Math.abs(y - lineY);
            if (distance < threshold && distance < minDistance) {
              minDistance = distance;
              closestLine = line;
            }
          }
        }

        if (closestLine !== null) {
          // If we already have this line selected, prepare for potential dragging
          if (this.selectedLineId === closestLine.id) {
            this.mouseDownOnLine = true;
            this.dragStartY = y;
            chartElement.style.cursor = 'ns-resize';
            event.preventDefault();
            event.stopPropagation();
            return;
          }

          // Select the line
          this.selectLine(closestLine.id, x, y);
          chartElement.style.cursor = 'ns-resize';
          event.preventDefault();
          event.stopPropagation();
          return;
        }
      }


    });

    chartElement.addEventListener('mousemove', (event) => {
      const rect = chartElement.getBoundingClientRect();
      const yRaw = event.clientY - rect.top;
      const y = rect.height > 0 ? Math.max(0, Math.min(rect.height, yRaw)) : yRaw;

      // Check if we should start dragging (mouse is down on line and moved enough)
      if (this.mouseDownOnLine && !this.isDragging && this.selectedLineId) {
        const movementDistance = Math.abs(y - this.dragStartY);
        if (movementDistance > this.dragThreshold) {
          this.isDragging = true;
          chartElement.style.userSelect = 'none';
        }
      }

      if (this.isDragging && this.selectedLineId) {
        const newPrice = this.mainSeries?.coordinateToPrice(y);

        if (newPrice !== undefined && newPrice !== null && newPrice > 0) {
          this.updateLinePrice(this.selectedLineId, newPrice);
        }
        event.preventDefault();
        event.stopPropagation();
      } else {
        // Check if hovering over lines to show resize cursor
        const threshold = 10;
        let hoveringOverLine = false;

        // First priority: Check if hovering over the selected line (more forgiving threshold)
        if (this.selectedLineId) {
          const selectedLine = this.state.lines.find(l => l.id === this.selectedLineId);
          if (selectedLine) {
            const selectedLineY = this.mainSeries?.priceToCoordinate(selectedLine.price);
            if (selectedLineY !== undefined && selectedLineY !== null && Math.abs(y - selectedLineY) < 25) {
              chartElement.style.cursor = 'ns-resize';
              hoveringOverLine = true;
            }
          }
        }

        // Second priority: Check if hovering over ANY other line (strict threshold)
        if (!hoveringOverLine) {
          for (const line of this.state.lines) {
            const lineY = this.mainSeries?.priceToCoordinate(line.price);
            if (lineY !== undefined && lineY !== null && Math.abs(y - lineY) < threshold) {
              chartElement.style.cursor = 'ns-resize';
              hoveringOverLine = true;
              break;
            }
          }
        }

        if (!hoveringOverLine) {
          chartElement.style.cursor = 'default';
        }
      }
    });

    chartElement.addEventListener('mouseup', (event) => {
      if (this.isDragging) {
        this.isDragging = false;
        chartElement.style.cursor = 'default';
        chartElement.style.userSelect = '';
        event.preventDefault();
        event.stopPropagation();
      }

      // Reset mouse down state
      this.mouseDownOnLine = false;
    });

    chartElement.addEventListener('mouseleave', () => {
      if (this.isDragging) {
        this.isDragging = false;
        chartElement.style.cursor = 'default';
        chartElement.style.userSelect = '';
      }

      // Reset mouse down state when leaving chart
      this.mouseDownOnLine = false;
    });

    // Add touch event handlers for mobile support
    chartElement.addEventListener('touchstart', (event) => {
      if (event.touches.length === 1) { // Single finger touch
        const touch = event.touches[0];
        const rect = chartElement.getBoundingClientRect();
        const x = touch.clientX - rect.left;
        const yRaw = touch.clientY - rect.top;
        const y = rect.height > 0 ? Math.max(0, Math.min(rect.height, yRaw)) : yRaw;
        const price = this.mainSeries?.coordinateToPrice(y);


        // Clear any existing long press timer
        if (this.longPressTimer) {
          clearTimeout(this.longPressTimer);
          this.longPressTimer = null;
        }

        // Check for line selection (same logic as mousedown)
        if (price !== undefined && price !== null) {
          const threshold = 10;
          let closestLine: HorizontalLine | null = null;
          let minDistance = Infinity;

          for (const line of this.state.lines) {
            const lineY = this.mainSeries?.priceToCoordinate(line.price);
            if (lineY !== undefined && lineY !== null) {
              const distance = Math.abs(y - lineY);
              if (distance < threshold && distance < minDistance) {
                minDistance = distance;
                closestLine = line;
              }
            }
          }

          if (closestLine !== null) {
            // If we already have this line selected, start dragging immediately
            if (this.selectedLineId === closestLine.id) {
              this.isDragging = true;
              this.dragStartY = y;
              event.preventDefault();
              event.stopPropagation();
              return;
            }

            // Select the line and set up long press for toolbar
            this.selectLine(closestLine.id, x, y);

            // Set up long press timer for additional toolbar access
            this.longPressStarted = false;
            this.longPressTimer = setTimeout(() => {
              if (!this.isDragging && !this.longPressStarted) {
                this.longPressStarted = true;
                // Trigger haptic feedback if available
                if (navigator.vibrate) {
                  navigator.vibrate(50);
                }
                // Force toolbar to show again (in case it was dismissed)
                if (this.onLineSelectedCallback && closestLine) {
                  this.onLineSelectedCallback(closestLine, x, y);
                }
              }
            }, 500); // 500ms long press

            event.preventDefault();
            event.stopPropagation();
            return;
          }
        }
      }
    });

    chartElement.addEventListener('touchmove', (event) => {
      // Cancel long press if user moves finger
      if (this.longPressTimer && !this.isDragging) {
        clearTimeout(this.longPressTimer);
        this.longPressTimer = null;
      }

      if (this.isDragging && this.selectedLineId && event.touches.length === 1) {
        const touch = event.touches[0];
        const rect = chartElement.getBoundingClientRect();
        const yRaw = touch.clientY - rect.top;
        const y = rect.height > 0 ? Math.max(0, Math.min(rect.height, yRaw)) : yRaw;
        const newPrice = this.mainSeries?.coordinateToPrice(y);

        if (newPrice !== undefined && newPrice !== null && newPrice > 0) {
          this.updateLinePrice(this.selectedLineId, newPrice);
        }
        event.preventDefault();
        event.stopPropagation();
      }
    });

    chartElement.addEventListener('touchend', (event) => {
      // Clear long press timer
      if (this.longPressTimer) {
        clearTimeout(this.longPressTimer);
        this.longPressTimer = null;
      }

      if (this.isDragging) {
        this.isDragging = false;
        event.preventDefault();
        event.stopPropagation();
      }

      this.longPressStarted = false;
    });

    // Prevent context menu on chart to avoid interference
    chartElement.addEventListener('contextmenu', (event) => {
      event.preventDefault();
    });

    // Subscribe to crosshair move for tooltip (delayed hide to prevent flicker when cursor is near edges)
    this.chart.subscribeCrosshairMove((param) => {
      if (!this.chart) return;
      const point = param.point;
      const chartEl = this.chart.chartElement();
      const chartW = chartEl.clientWidth || 0;
      const chartH = chartEl.clientHeight || 0;
      const margin = 25; // Allow mouse in upper/lower margins and still show tooltip
      const outOfBounds = !point || !param.time ||
        point.x < -margin || point.y < -margin ||
        point.x > chartW + margin || point.y > chartH + margin;

      if (outOfBounds) {
        this.scheduleTooltipHide();
        return;
      }

      const pointSafe = point!;
      const time = param.time!;

      // Get data from candlestick series
      const candlestickData = param.seriesData.get(this.mainSeries!);
      const volumeData = param.seriesData.get(this.volumeSeries!);

      // Cancel any pending hide so tooltip stays visible
      this.cancelTooltipHide();

      // Handle main chart tooltip
      if (candlestickData && (candlestickData as any).open !== undefined) {
        // Try to read moving average values at this point (if series exist)
        const movingAverages: { [key: string]: { value: number; color: string; name: string } } = {};
        
        this.state.indicators
          .filter(ind => ind.type === 'moving_average' && ind.visible)
          .forEach(ind => {
            const period = ind.settings.period;
            const key = ind.id;
            const series = this.smaSeries[key];
            if (series) {
              const data = param.seriesData.get(series);
              if (data && (data as any).value !== undefined) {
                movingAverages[key] = {
                  value: (data as any).value,
                  color: ind.color || '#ffffff',
                  name: ind.name || `MA(${period})`
                };
              }
            }
          });

        const tooltipData: TooltipData = {
          visible: true,
          x: pointSafe.x,
          y: pointSafe.y,
          data: {
            open: (candlestickData as any).open,
            high: (candlestickData as any).high,
            low: (candlestickData as any).low,
            close: (candlestickData as any).close,
          },
          volumeData: volumeData ? { value: (volumeData as any).value } : undefined,
          movingAverages: Object.keys(movingAverages).length > 0 ? movingAverages : undefined,
          time
        };

        if (this.onTooltipChangedCallback) {
          this.onTooltipChangedCallback(tooltipData);
        }
      } else {
        this.scheduleTooltipHide();
        return;
      }

      // Handle ATR tooltip (check if ATR is visible and we have ATR data)
      if (this.isATRVisible(21) && this.atrSeries['atr21']) {
        const atrData = param.seriesData.get(this.atrSeries['atr21']);

        if (atrData && (atrData as any).value !== undefined) {
          const atrTooltipData: ATRTooltipData = {
            visible: true,
            x: pointSafe.x,
            y: pointSafe.y,
            atrValue: (atrData as any).value,
            time,
            period: 21
          };

          if (this.onATRTooltipChangedCallback) {
            this.onATRTooltipChangedCallback(atrTooltipData);
          }
        } else {
          if (this.onATRTooltipChangedCallback) {
            this.onATRTooltipChangedCallback(null);
          }
        }
      } else {
        if (this.onATRTooltipChangedCallback) {
          this.onATRTooltipChangedCallback(null);
        }
      }
    });
  }

  // Getters for component access
  public getDrawingMode(): boolean {
    return this.state.isDrawingMode;
  }

  public getTextMode(): boolean {
    return this.state.isTextMode;
  }

  public getHorizontalLines(): HorizontalLine[] {
    return [...this.state.lines];
  }

  public getTextAnnotations(): TextAnnotation[] {
    return [...this.state.textAnnotations];
  }

  // ===== PEN DRAWING METHODS =====
  public getPenMode(): boolean {
    return this.state.isPenMode;
  }

  public getPenDrawings(): PenDrawing[] {
    return [...this.state.penDrawings];
  }

  public setPenMode(enabled: boolean): void {
    this.state.isPenMode = enabled;
    if (enabled) {
      this.initializePenCanvas();
    } else {
      this.cleanupPenCanvas();
      // Ensure persisted pen drawings remain visible even when pen tool is inactive.
      // (Some flows only draw onto the interactive canvas; force SVG overlay redraw here.)
      if (this.state.penDrawings.length > 0) {
        this.redrawAllPenDrawings();
      }
    }
  }

  public setPenSize(size: number): void {
    this.state.penSize = size;
  }

  public setPenColor(color: string): void {
    this.state.penColor = color;
  }

  public clearAllPenDrawings(): void {
    this.deselectPenDrawing();
    this.state.penDrawings = [];
    this.state.isPenMode = false;
    this.clearPenDrawingsFromDOM();
    // Clear canvas too (drawings persist on canvas when pen is deactivated)
    this.redrawPenDrawingsOnCanvas();
    if (this.onLinesChangedCallback) {
      this.onLinesChangedCallback();
    }
  }

  selectPenDrawing(drawingId: string, clientX?: number, clientY?: number): void {
    const drawing = this.state.penDrawings.find(d => d.id === drawingId);
    if (!drawing) return;

    // Update selection
    this.selectedPenDrawingId = drawingId;

    // Re-render all pen drawings to update visual selection
    this.redrawAllPenDrawings();

    // Calculate position for toolbar
    let x = clientX || 0;
    let y = clientY || 0;

    // If no client coordinates provided, use center of drawing
    if (!clientX || !clientY) {
      const firstPoint = drawing.points[0];
      if (firstPoint) {
        const pixelCoords = this.chartToPixelCoordinates(firstPoint.time, firstPoint.price);
        if (pixelCoords) {
          const chartElement = this.chart?.chartElement();
          if (chartElement) {
            const rect = chartElement.getBoundingClientRect();
            x = rect.left + pixelCoords.x;
            y = rect.top + pixelCoords.y;
          }
        }
      }
    }

    // Trigger selection callback
    if (this.onPenDrawingSelectedCallback) {
      this.onPenDrawingSelectedCallback(drawing, x, y);
    }
  }

  public deselectPenDrawing(): void {
    this.selectedPenDrawingId = null;
    this.redrawAllPenDrawings();

    if (this.onPenDrawingSelectedCallback) {
      this.onPenDrawingSelectedCallback(null);
    }
  }

  getSelectedPenDrawing(): PenDrawing | null {
    if (!this.selectedPenDrawingId) return null;
    return this.state.penDrawings.find(d => d.id === this.selectedPenDrawingId) || null;
  }

  public updatePenDrawing(drawingId: string, updates: Partial<PenDrawing>): void {
    const drawing = this.state.penDrawings.find(d => d.id === drawingId);
    if (!drawing) return;

    // Update the drawing properties
    Object.assign(drawing, updates);

    // Re-render all drawings to show changes
    this.redrawAllPenDrawings();

    if (this.onLinesChangedCallback) {
      this.onLinesChangedCallback();
    }
  }

  public removePenDrawing(drawingId: string): void {
    this.state.penDrawings = this.state.penDrawings.filter(d => d.id !== drawingId);
    this.clearPenDrawingsFromDOM();
    this.redrawAllPenDrawings();

    if (this.selectedPenDrawingId === drawingId) {
      this.selectedPenDrawingId = null;
    }

    if (this.onLinesChangedCallback) {
      this.onLinesChangedCallback();
    }
  }

  // Add the missing public methods
  toggleTextMode(): void {
    this.state.isTextMode = !this.state.isTextMode;

    // Clear inline editing state when turning off text mode
    if (!this.state.isTextMode) {
      this.state.isInlineEditing = false;
    }

    if (this.chart) {
      // Disable/enable chart interactions when in text mode
      this.chart.applyOptions({
        handleScroll: !this.state.isTextMode,
        handleScale: !this.state.isTextMode,
      });
    }

  }

  public setTextMode(enabled: boolean): void {
    this.state.isTextMode = enabled;

    // Clear inline editing state when turning off text mode
    if (!this.state.isTextMode) {
      this.state.isInlineEditing = false;
    }

    if (this.chart) {
      // Disable/enable chart interactions when in text mode
      this.chart.applyOptions({
        handleScroll: !this.state.isTextMode,
        handleScale: !this.state.isTextMode,
      });
    }

  }

  public clearAllTextAnnotations(): void {
    // Ensure any selected annotation/editing toolbar is dismissed before removal.
    this.deselectAllTextAnnotations();
    if (this.onTextSelectedCallback) {
      this.onTextSelectedCallback(null);
    }

    // Remove all text annotation DOM elements
    this.state.textAnnotations.forEach(annotation => {
      this.removeTextAnnotationElement(annotation.id);
    });

    this.state.textAnnotations = [];

    // Clear inline editing state if it was active
    this.state.isInlineEditing = false;

    // Keep tool state aligned with UI after bulk clear.
    this.state.isTextMode = false;
    if (this.chart) {
      this.chart.applyOptions({
        handleScroll: true,
        handleScale: true,
      });
    }

    if (this.onLinesChangedCallback) {
      this.onLinesChangedCallback();
    }
  }

  // Check if currently in inline editing mode
  isInlineEditing(): boolean {
    return this.state.isInlineEditing;
  }

  // Chart Settings Methods
  public setCrosshairMode(mode: CrosshairMode): void {
    this.state.crosshairMode = mode;
    if (this.chart) {
      let lightweightMode: LightweightCrosshairMode;
      switch (mode) {
        case 'normal':
          lightweightMode = LightweightCrosshairMode.Normal;
          break;
        case 'magnet':
          lightweightMode = LightweightCrosshairMode.Magnet;
          break;
        case 'hidden':
          lightweightMode = LightweightCrosshairMode.Hidden;
          break;
        case 'magnetOHLC':
          lightweightMode = LightweightCrosshairMode.MagnetOHLC;
          break;
        default:
          lightweightMode = LightweightCrosshairMode.Normal;
      }

      this.chart.applyOptions({
        crosshair: {
          mode: lightweightMode
        }
      });
    }
  }

  public setGridLinesVisible(visible: boolean): void {
    this.state.showGridLines = visible;
    if (this.chart) {
      this.chart.applyOptions({
        grid: {
          vertLines: {
            visible: visible
          },
          horzLines: {
            visible: visible
          }
        }
      });
    }
  }

  public setVolumeVisible(visible: boolean): void {
    this.volumeVisible = visible;
    if (this.volumeSeries) {
      this.volumeSeries.applyOptions({
        visible: visible
      });
    }
    this.applyVolumeScaleOptions();
  }

  public setPriceLineVisible(visible: boolean): void {
    if (this.mainSeries) {
      this.mainSeries.applyOptions({
        priceLineVisible: visible // Only hide the horizontal line, keep the price label
      });
    }
  }

  public applyTheme(theme: 'light' | 'dark'): void {
    if (!this.chart) return;

    const chartOptions = {
      layout: {
        background: {
          color: theme === 'light' ? '#ffffff' : '#1a1a1a'
        },
        textColor: theme === 'light' ? '#333333' : '#d1d4dc'
      },
      grid: {
        vertLines: {
          color: theme === 'light' ? '#e1e3e6' : '#2b2b43'
        },
        horzLines: {
          color: theme === 'light' ? '#e1e3e6' : '#2b2b43'
        }
      },
      timeScale: {
        borderColor: theme === 'light' ? '#e1e3e6' : '#2b2b43',
        timeVisible: true,
        secondsVisible: false,
        rightOffset: 14,
        barSpacing: TradingViewChartService.DEFAULT_BAR_SPACING,
        fixRightEdge: false,
        shiftVisibleRangeOnNewBar: false
      },
      rightPriceScale: {
        borderColor: theme === 'light' ? '#e1e3e6' : '#2b2b43'
      }
    };

    this.chart.applyOptions(chartOptions);

    // Update main series colors (if it's a candlestick series)
    if (this.mainSeries && (this.state.seriesType === 'candlestick' || this.state.seriesType === 'bar')) {
      this.mainSeries.applyOptions({
        upColor: theme === 'light' ? '#26a69a' : '#26a69a',
        downColor: theme === 'light' ? '#ef5350' : '#ef5350',
        borderUpColor: theme === 'light' ? '#26a69a' : '#26a69a',
        borderDownColor: theme === 'light' ? '#ef5350' : '#ef5350',
        wickUpColor: theme === 'light' ? '#26a69a' : '#26a69a',
        wickDownColor: theme === 'light' ? '#ef5350' : '#ef5350'
      });
    }

    // Update volume series colors
    if (this.volumeSeries) {
      this.volumeSeries.applyOptions({
        color: theme === 'light' ? '#26a69a' : '#26a69a'
      });
    }
  }

  // Sync indicators from IndicatorService to chart rendering
  public syncIndicators(indicators: Indicator[]): void {
    this.state.indicators = this.dedupeIndicatorsByCanonicalKey(indicators || []);
    // console.log('[INDICATORS] syncIndicators called with', indicators.length, 'indicators:', indicators.map(i => `${i.type}(visible=${i.visible})`).join(', '));

    if (this.chart) {
      if (this.fullDataset.length > 0) {
        this.renderIndicators();
      } else {
        // console.log('[INDICATORS] No candle data yet; indicators will render when socket data arrives.');
      }
    } else {
      // console.log('[INDICATORS] Skipping render - chart not ready');
    }
  }

  private getIndicatorCanonicalKey(indicator: Indicator): string {
    const settings = indicator.settings || {};
    switch (indicator.type) {
      case 'moving_average': {
        const period = settings['period'];
        const maType = settings['movingAverageType'] || 'exponential';
        return `moving_average:${period}:${maType}`;
      }
      case 'atr': {
        return `atr:${settings['period']}`;
      }
      case 'rsi': {
        return `rsi:${settings['period']}:${settings['source'] || 'close'}:${settings['overboughtLevel'] ?? 70}:${settings['oversoldLevel'] ?? 30}`;
      }
      case 'macd': {
        return `macd:${settings['fastPeriod']}:${settings['slowPeriod']}:${settings['signalPeriod']}:${settings['source'] || 'close'}`;
      }
      case 'bollinger_bands': {
        return `bollinger_bands:${settings['period']}:${settings['standardDeviations']}:${settings['source'] || 'close'}`;
      }
      case 'stochastic': {
        return `stochastic:${settings['kPeriod']}:${settings['dPeriod']}:${settings['smooth']}`;
      }
      default:
        return `${indicator.type}:${indicator.name}`;
    }
  }

  private dedupeIndicatorsByCanonicalKey(indicators: Indicator[]): Indicator[] {
    if (!Array.isArray(indicators) || indicators.length === 0) return [];
    const seen = new Set<string>();
    const uniqueReversed: Indicator[] = [];

    for (let i = indicators.length - 1; i >= 0; i--) {
      const ind = indicators[i];
      if (!ind) continue;
      const key = this.getIndicatorCanonicalKey(ind);
      if (seen.has(key)) continue;
      seen.add(key);
      uniqueReversed.push(ind);
    }

    return uniqueReversed.reverse();
  }

  private renderIndicators(): void {
    if (!this.chart) {
      // console.warn('[INDICATORS] renderIndicators: no chart');
      return;
    }

    if (this.fullDataset.length === 0) {
      // console.warn('[INDICATORS] renderIndicators: no data');
      return;
    }

    const visibleIndicators = this.state.indicators.filter(ind => ind.visible);
    // console.log('[INDICATORS] renderIndicators: rendering', visibleIndicators.length, 'visible indicators, panes:', this.chart.panes().length, 'data points:', this.fullDataset.length);

    // --- Remove inactive series FIRST (before creating new ones) to free panes ---

    try {
      Object.keys(this.smaSeries).forEach(key => {
        const indicatorExists = this.state.indicators.some(ind =>
          ind.type === 'moving_average' && ind.id === key
        );
        if (!indicatorExists) {
          this.removeSMASeries(key);
        } else {
          const isVisible = this.state.indicators.some(ind =>
            ind.type === 'moving_average' && ind.id === key && ind.visible
          );
          if (!isVisible && this.smaSeries[key]) {
            this.smaSeries[key].applyOptions({ visible: false });
          }
        }
      });
    } catch (e) { console.error('[INDICATORS] Error hiding SMA:', e); }

    try {
      [...Object.keys(this.atrSeries)].forEach(key => {
        const period = parseInt(key.replace('atr', ''));
        if (!this.isATRVisible(period)) {
          this.removeATRSeries(key);
        }
      });
    } catch (e) { console.error('[INDICATORS] Error removing ATR:', e); }

    try {
      [...Object.keys(this.rsiSeries)].forEach(key => {
        const period = parseInt(key.replace('rsi', ''));
        if (!this.isRSIVisible(period)) {
          this.removeRSISeries(key);
        }
      });
    } catch (e) { console.error('[INDICATORS] Error removing RSI:', e); }

    try {
      [...Object.keys(this.bollingerSeries)].forEach(key => {
        const parts = key.replace('bb', '').split('_');
        const period = parseInt(parts[0]);
        const stdDev = parseInt(parts[1]);
        if (!this.isBollingerVisible(period, stdDev)) {
          this.removeBollingerSeries(key);
        }
      });
    } catch (e) { console.error('[INDICATORS] Error removing BB:', e); }

    try {
      [...Object.keys(this.macdSeries)].forEach(key => {
        const parts = key.replace('macd', '').split('_');
        const fp = parseInt(parts[0]);
        const sp = parseInt(parts[1]);
        const sigp = parseInt(parts[2]);
        if (!this.isMACDVisible(fp, sp, sigp)) {
          this.removeMACDSeries(key);
        }
      });
    } catch (e) { console.error('[INDICATORS] Error removing MACD:', e); }

    try {
      [...Object.keys(this.stochSeries)].forEach(key => {
        const parts = key.replace('stoch', '').split('_');
        const kp = parseInt(parts[0]);
        const dp = parseInt(parts[1]);
        const sm = parseInt(parts[2]);
        if (!this.isStochVisible(kp, dp, sm)) {
          this.removeStochSeries(key);
        }
      });
    } catch (e) { console.error('[INDICATORS] Error removing Stoch:', e); }

    // --- Now render active indicators ---

    // Moving Average (overlay on main chart)
    try {
      this.state.indicators
        .filter(indicator => indicator.type === 'moving_average' && indicator.visible)
        .forEach(indicator => {
          const period = Number(indicator.settings.period);
          if (isNaN(period) || period <= 0) return;

          const type = String(indicator.settings.movingAverageType || 'exponential').toLowerCase();
          const key = indicator.id;
          const lw = this.indicatorStrokeWidth(indicator.settings, 1);

          if (!this.smaSeries[key]) {
            this.createSMASeries(key, indicator.color || '#2962FF', period, lw);
          }
          if (this.smaSeries[key]) {
            this.smaSeries[key].applyOptions({ visible: true, color: indicator.color || '#2962FF', lineWidth: lw });
            const smaData = type === 'exponential'
              ? this.calculateEMA(this.fullDataset, period)
              : this.calculateSMA(this.fullDataset, period);
            this.smaSeries[key].setData(smaData);
            //console.log('[INDICATORS] MA rendered:', key, 'period:', period, 'type:', type, 'points:', smaData.length);
          }
        });
    } catch (e) { console.error('[INDICATORS] Error rendering MA:', e); }

    // Bollinger Bands (overlay on main chart)
    try {
      this.state.indicators
        .filter(indicator => indicator.type === 'bollinger_bands' && indicator.visible)
        .forEach(indicator => {
          const period = indicator.settings.period || 20;
          const stdDev = indicator.settings['standardDeviations'] || 2;
          const key = `bb${period}_${stdDev}`;

          const bbColor = indicator.color || '#FF5722';
          const bbLw = this.indicatorStrokeWidth(indicator.settings, 1);
          if (!this.bollingerSeries[key]) {
            this.createBollingerSeries(period, stdDev, bbColor, bbLw);
            // console.log('[INDICATORS] BB created:', key);
          }
          if (this.bollingerSeries[key]) {
            const bb = this.bollingerSeries[key];
            bb.upper.applyOptions({ visible: true, color: bbColor, lineWidth: bbLw });
            bb.middle.applyOptions({ visible: true, color: bbColor, lineWidth: bbLw });
            bb.lower.applyOptions({ visible: true, color: bbColor, lineWidth: bbLw });
            this.updateBollingerData(period, stdDev);
            // console.log('[INDICATORS] BB rendered:', key);
          }
        });
    } catch (e) { console.error('[INDICATORS] Error rendering BB:', e); }

    // ATR (separate pane)
    try {
      this.state.indicators
        .filter(indicator => indicator.type === 'atr' && indicator.visible)
        .forEach(indicator => {
          const period = indicator.settings.period!;
          const key = `atr${period}`;
          const atrLw = this.indicatorStrokeWidth(indicator.settings, 1);

          if (!this.atrSeries[key]) {
            this.createATRSeries(period, indicator.color || '#FF6B35', atrLw);
            // console.log('[INDICATORS] ATR created:', key, 'panes now:', this.chart!.panes().length);
          }
          if (this.atrSeries[key]) {
            this.atrSeries[key].applyOptions({ visible: true, color: indicator.color || '#FF6B35', lineWidth: atrLw });
            const atrData = this.calculateATR(this.fullDataset, period);
            this.atrSeries[key].setData(atrData);
            this.applyATRBasedCandlestickColoring(this.fullDataset, atrData, period);
            // console.log('[INDICATORS] ATR rendered:', key, 'points:', atrData.length);
          }
        });
    } catch (e) { console.error('[INDICATORS] Error rendering ATR:', e); }

    // RSI (separate pane)
    try {
      this.state.indicators
        .filter(indicator => indicator.type === 'rsi' && indicator.visible)
        .forEach(indicator => {
          const period = indicator.settings.period!;
          const key = `rsi${period}`;
          const rsiLw = this.indicatorStrokeWidth(indicator.settings, 1);

          if (!this.rsiSeries[key]) {
            this.createRSISeries(period, indicator.color || '#9C27B0', rsiLw);
            // console.log('[INDICATORS] RSI created:', key, 'panes now:', this.chart!.panes().length);
          }
          if (this.rsiSeries[key]) {
            this.rsiSeries[key].applyOptions({ visible: true, color: indicator.color || '#9C27B0', lineWidth: rsiLw });
            // Use standard RSI (Wilder) so values match Yahoo/other charting sites
            const rsiData = this.technicalIndicatorsService.calculateRSI(this.fullDataset, period);
            this.rsiSeries[key].setData(rsiData);
            // console.log('[INDICATORS] RSI rendered:', key, 'points:', rsiData.length);
          }
        });
    } catch (e) { console.error('[INDICATORS] Error rendering RSI:', e); }

    // MACD (separate pane)
    try {
      this.state.indicators
        .filter(indicator => indicator.type === 'macd' && indicator.visible)
        .forEach(indicator => {
          const fastPeriod = indicator.settings['fastPeriod'] || 12;
          const slowPeriod = indicator.settings['slowPeriod'] || 26;
          const signalPeriod = indicator.settings['signalPeriod'] || 9;
          const key = `macd${fastPeriod}_${slowPeriod}_${signalPeriod}`;
          const macdLw = this.indicatorStrokeWidth(indicator.settings, 1);

          if (!this.macdSeries[key]) {
            this.createMACDSeries(fastPeriod, slowPeriod, signalPeriod, indicator.color, macdLw);
            // console.log('[INDICATORS] MACD created:', key, 'panes now:', this.chart!.panes().length);
          }
          if (this.macdSeries[key]) {
            const macd = this.macdSeries[key];
            const color = indicator.color || '#4CAF50';
            macd.macdLine.applyOptions({ visible: true, color, lineWidth: macdLw });
            macd.signalLine.applyOptions({ visible: true, color });
            macd.histogram.applyOptions({ visible: true });
            this.updateMACDData(fastPeriod, slowPeriod, signalPeriod);
            // console.log('[INDICATORS] MACD rendered:', key);
          }
        });
    } catch (e) { console.error('[INDICATORS] Error rendering MACD:', e); }

    // Stochastic (separate pane)
    try {
      this.state.indicators
        .filter(indicator => indicator.type === 'stochastic' && indicator.visible)
        .forEach(indicator => {
          const kPeriod = indicator.settings['kPeriod'] || 14;
          const dPeriod = indicator.settings['dPeriod'] || 3;
          const smooth = indicator.settings['smooth'] || 3;
          const key = `stoch${kPeriod}_${dPeriod}_${smooth}`;
          const stochLw = this.indicatorStrokeWidth(indicator.settings, 1);

          if (!this.stochSeries[key]) {
            this.createStochSeries(kPeriod, dPeriod, smooth, indicator.color, stochLw);
            // console.log('[INDICATORS] Stochastic created:', key, 'panes now:', this.chart!.panes().length);
          }
          if (this.stochSeries[key]) {
            const stoch = this.stochSeries[key];
            const color = indicator.color || '#795548';
            stoch.kLine.applyOptions({ visible: true, color, lineWidth: stochLw });
            stoch.dLine.applyOptions({ visible: true, color });
            stoch.overbought.applyOptions({ visible: true });
            stoch.oversold.applyOptions({ visible: true });
            this.updateStochData(kPeriod, dPeriod, smooth);
            // console.log('[INDICATORS] Stochastic rendered:', key);
          }
        });
    } catch (e) { console.error('[INDICATORS] Error rendering Stochastic:', e); }
  }

  // Cleanup
  public destroy(): void {
    this.cancelTooltipHide();
    if (this.chart) {
      this.chart.remove();
      this.chart = null;
      this.penCanvas = null;
      this.penContext = null;
      this.penDrawingsContainer = null;
      // Arrow tool cleanup on chart teardown
      if (this.arrowResizeHandler) {
        window.removeEventListener('resize', this.arrowResizeHandler);
        this.arrowResizeHandler = undefined;
      }
      this.arrowVisibleRangeHandler = undefined;
      this.arrowDrawingsContainer = null;
      this.arrowCanvas = null;
      this.arrowContext = null;
      this.arrowPreviewEndPoint = null;
      if (this.arrowSelectionBoundElement && this.arrowSelectionClickHandler) {
        this.arrowSelectionBoundElement.removeEventListener('click', this.arrowSelectionClickHandler, true);
      }
      this.arrowSelectionClickHandler = undefined;
      this.arrowSelectionBoundElement = null;
      this.selectedArrowDrawingId = null;
      if (this.arrowWindowMouseMoveHandler) {
        window.removeEventListener('mousemove', this.arrowWindowMouseMoveHandler, true);
        this.arrowWindowMouseMoveHandler = undefined;
      }
      if (this.arrowWindowMouseUpHandler) {
        window.removeEventListener('mouseup', this.arrowWindowMouseUpHandler, true);
        this.arrowWindowMouseUpHandler = undefined;
      }
      this.resetArrowDragState();
      this.arrowStartPoint = null;
      this.mainSeries = null;
      this.volumeSeries = null;
      this.smaSeries = {};
      this.atrSeries = {};
      this.rsiSeries = {};
      this.rsiReferenceLines = {};
      this.bollingerSeries = {};
      this.macdSeries = {};
      this.stochSeries = {};
    }
  }

  // Add method to toggle volume visibility
  toggleVolumeVisibility(visible: boolean): void {
    this.setVolumeVisible(visible);
  }

  // Add method to resize chart for responsiveness
  public resizeChart(width: number, height: number): void {
    if (this.chart) {
      this.chart.applyOptions({
        width: width,
        height: height
      });
      this.applyVolumeScaleOptions();

      // Update pen canvas size if it exists
      if (this.penCanvas) {
        const dpr = window.devicePixelRatio || 1;
        this.penCanvas.width = width * dpr;
        this.penCanvas.height = height * dpr;
        this.penCanvas.style.width = width + 'px';
        this.penCanvas.style.height = height + 'px';

        // Re-scale context after resize
        if (this.penContext) {
          this.penContext.scale(dpr, dpr);
          this.penContext.lineCap = 'round';
          this.penContext.lineJoin = 'round';
          this.penContext.imageSmoothingEnabled = true;
          if (this.penContext.imageSmoothingQuality) {
            this.penContext.imageSmoothingQuality = 'high';
          }
        }
      }

      // For 1y, re-apply bar spacing so full year still fits in one screen after resize
      if (this.currentInterval === '1y' && this.fullDataset.length > 0) {
        this.applyRightOffset();
      }

      // Re-render pen drawings after resize
      this.redrawAllPenDrawings();
    }
  }

  // Kept for backward compatibility with existing callers, but the chart must
  // never auto-fit the full dataset — always respect the selected time range.
  fitChart(): void {
    if (!this.chart) return;
    const rangeForViewport = this.currentInterval
      || this.state.selectedTimePeriod
      || TradingViewChartService.DEFAULT_RANGE;
    this.setVisibleTimeRange(rangeForViewport);
  }

  /**
   * Keep initial refresh framing (autoScale=true), but once user starts interacting
   * with the mouse, unlock to manual price scale so vertical up/down movement works.
   */
  private attachMousePriceScaleUnlock(chartElement: HTMLElement): void {
    const unlockPriceScale = () => {
      if (!this.chart || this.state.isDrawingMode || this.state.isTextMode) {
        return;
      }
      try {
        this.chart.priceScale('right').applyOptions({
          autoScale: false,
          scaleMargins: {
            top: 0.1,
            bottom: 0.1,
          },
        });
      } catch (_) {
        // No-op: keep chart interaction uninterrupted.
      }
    };

    chartElement.addEventListener('mousedown', (event: MouseEvent) => {
      if (event.button !== 0) return;
      unlockPriceScale();
    }, { passive: true });
  }

  /**
   * Run when series data is applied (refresh / new range). Keeps the right price scale on
   * library auto-scale with margins so candles stay centered without locking `setVisibleRange`,
   * which previously overwrote vertical position while panning or moving the chart up/down.
   */
  private fitPriceScaleToData(): void {
    if (!this.chart || !this.fullDataset || this.fullDataset.length === 0) {
      return;
    }
    try {
      this.chart.priceScale('right').applyOptions({
        autoScale: true,
        scaleMargins: {
          top: 0.1,
          bottom: 0.1
        }
      });
      this.applyVolumeScaleOptions();
    } catch (error) {
      console.error('[CHART] Error applying default price scale:', error);
    }
  }


  /**
   * Intelligently sets the visible range to ensure chart is readable.
   * - If data fits on screen with good spacing, show all data.
   * - If data is too large (like 1Y minute data), show only the most recent readable chunk.
   */
  private setOptimizedVisibleRange(): void {
    if (!this.chart || this.fullDataset.length === 0) return;

    const timeScale = this.chart.timeScale();
    const chartWidth = timeScale.width();
    const dataCount = this.fullDataset.length;

    // Minimum readable bar width in pixels
    // On mobile we tolerate tighter bars (4px), on desktop we want more space (6px)
    const isMobile = chartWidth < 768;
    const minBarWidth = isMobile ? 4 : 6;

    // Ideal bar width we aim for (comfortable reading)
    const targetBarWidth = isMobile ? 6 : 10;

    // How many bars can we comfortably fit?
    const capacity = Math.floor(chartWidth / targetBarWidth);

    if (dataCount <= capacity) {
      // Case 1: Data is small enough to fit comfortably.
      // We can just fit content, but let's ensure it doesn't stretch too wide if there's very little data
      // fitContent() usually does a good job
      console.log('[CHART] Small dataset, fitting content');
      timeScale.fitContent();
    } else {
      // Case 2: Data is too large to fit all at once (e.g. 1 Year of minute data).
      // We should show the most recent 'capacity' bars.
      console.log('[CHART] Large dataset, showing recent', capacity, 'bars');

      // Calculate logical range
      // We want to show [last - capacity, last]
      // Add a small right offset (empty space) for better UX
      const rightOffset = 5;

      timeScale.setVisibleLogicalRange({
        from: dataCount - capacity,
        to: dataCount + rightOffset
      });

      // Also ensure we are scrolled to the right
      timeScale.scrollToPosition(rightOffset, false);
    }
  }
  // Apply only a right-edge padding. Never set `barSpacing` here — lightweight-charts derives
  // `barSpacing` from the currently selected visible range, and overriding it re-fits the chart,
  // which previously collapsed 1Y / YTD / ALL views to only ~1 month of bars.
  private applyRightOffset(): void {
    if (!this.chart) return;
    try {
      this.chart.applyOptions({
        timeScale: {
          rightOffset: 6,
          fixRightEdge: false,
          shiftVisibleRangeOnNewBar: false
        }
      });
    } catch (_) { /* noop */ }
  }

  private selectLine(lineId: string, x: number, y: number): void {
    // First deselect any previously selected line
    if (this.selectedLineId && this.selectedLineId !== lineId) {
      this.updateLineVisualState(this.selectedLineId, false);
    }

    this.selectedLineId = lineId;
    this.updateLineVisualState(lineId, true);

    if (this.onLineSelectedCallback) {
      const line = this.state.lines.find(l => l.id === lineId);
      if (line) {
        this.onLineSelectedCallback(line, x, y);
      }
    }
  }

  public deselectLine(): void {
    if (this.selectedLineId) {
      this.updateLineVisualState(this.selectedLineId, false);
      this.selectedLineId = null;
    }

    if (this.onLineSelectedCallback) {
      this.onLineSelectedCallback(null);
    }
  }

  private updateLineVisualState(lineId: string, isSelected: boolean): void {
    const line = this.state.lines.find(l => l.id === lineId);
    const priceLine = this.horizontalLines.get(lineId);

    if (line && priceLine && this.mainSeries) {
      // Remove the old line
      this.mainSeries.removePriceLine(priceLine);

      // Create new line with enhanced visual state for selection
      const newPriceLine = this.mainSeries.createPriceLine({
        price: line.price,
        color: isSelected ? '#FF6B35' : line.color, // Bright orange for selected lines
        lineWidth: (isSelected ? 4 : line.lineWidth) as any, // Much thicker for selected lines
        lineStyle: (isSelected ? 2 : line.lineStyle) as any, // Dashed style for selected lines (2 = dashed)
        axisLabelVisible: true,
        title: isSelected ? `[SELECTED] ${line.name}` : line.name, // Add selected indicator for selected lines
      });

      this.horizontalLines.set(lineId, newPriceLine);
    }
  }

  private updateLinePrice(lineId: string, newPrice: number): void {
    const line = this.state.lines.find(l => l.id === lineId);
    if (line) {
      line.price = newPrice;

      // Update the price line on the chart
      const priceLine = this.horizontalLines.get(lineId);
      if (priceLine && this.mainSeries) {
        // Remove and recreate with new price, maintaining selection state
        this.mainSeries.removePriceLine(priceLine);
        const isSelected = this.selectedLineId === lineId;
        const newPriceLine = this.mainSeries.createPriceLine({
          price: newPrice,
          color: isSelected ? '#FF6B35' : line.color, // Bright orange for selected lines
          lineWidth: (isSelected ? 4 : line.lineWidth) as any, // Much thicker for selected lines
          lineStyle: (isSelected ? 2 : line.lineStyle) as any, // Dashed style for selected lines
          axisLabelVisible: true,
          title: isSelected ? `[SELECTED] ${line.name}` : line.name, // Add selected indicator for selected lines
        });
        this.horizontalLines.set(lineId, newPriceLine);
      }

      this.saveLinesToLocalStorage();

      // Notify component of changes
      if (this.onLinesChangedCallback) {
        this.onLinesChangedCallback();
      }
    }
  }



  // Get information about current dataset
  public getDatasetInfo(): { symbol: string; dataPoints: number; startDate?: Date; endDate?: Date } {
    if (this.fullDataset.length === 0) {
      return { symbol: this.currentSymbol, dataPoints: 0 };
    }

    const startTime = this.fullDataset[0].time;
    const endTime = this.fullDataset[this.fullDataset.length - 1].time;

    // Convert timestamps to dates - handle both string and number timestamps
    const startTimestamp = typeof startTime === 'string' ? parseInt(startTime) : startTime as number;
    const endTimestamp = typeof endTime === 'string' ? parseInt(endTime) : endTime as number;

    const startDate = new Date(startTimestamp * 1000);
    const endDate = new Date(endTimestamp * 1000);

    return {
      symbol: this.currentSymbol,
      dataPoints: this.fullDataset.length,
      startDate,
      endDate
    };
  }

  public getOldestLoadedCandleTimeSec(): number | null {
    if (!this.fullDatasetAll || this.fullDatasetAll.length === 0) return null;
    const oldest = Number(this.fullDatasetAll[0].time);
    return Number.isFinite(oldest) && oldest > 0 ? Math.floor(oldest) : null;
  }

  // ===== PEN DRAWING IMPLEMENTATION =====
  private initializePenCanvas(): void {
    if (!this.chart) return;

    const chartElement = this.chart.chartElement();
    if (!chartElement) return;

    chartElement.style.position = 'relative';

    // Create pen drawings container FIRST (so it's below canvas for drawing, but drawings display via z-index)
    if (!this.penDrawingsContainer) {
      this.penDrawingsContainer = document.createElement('div');
      this.penDrawingsContainer.style.cssText = 'position:absolute;inset:0;width:100%;height:100%;z-index:10000;pointer-events:none;visibility:visible;';
      chartElement.appendChild(this.penDrawingsContainer);
    }

    // Reuse existing canvas when reactivating pen mode (drawings persist)
    if (this.penCanvas && this.penCanvas.parentElement) {
      this.penCanvas.style.pointerEvents = 'auto';
      this.penCanvas.style.zIndex = '9999';
      // Resize canvas if chart dimensions changed
      const rect = chartElement.getBoundingClientRect();
      const dpr = window.devicePixelRatio || 1;
      this.penCanvas.width = rect.width * dpr;
      this.penCanvas.height = rect.height * dpr;
      this.penCanvas.style.width = rect.width + 'px';
      this.penCanvas.style.height = rect.height + 'px';
      this.penContext = this.penCanvas.getContext('2d');
      if (this.penContext) {
        this.penContext.setTransform(dpr, 0, 0, dpr, 0, 0);
        this.redrawPenDrawingsOnCanvas();
      }
      // Ensure SVG layer and canvas both show existing pen drawings when pen tool is active
      if (this.state.penDrawings.length > 0) {
        this.redrawAllPenDrawings();
      }
      return;
    }

    // Create canvas overlay for pen drawing (below container by DOM order; canvas z-index lower so drawings show on top)
    this.penCanvas = document.createElement('canvas');
    this.penCanvas.style.position = 'absolute';
    this.penCanvas.style.top = '0';
    this.penCanvas.style.left = '0';
    this.penCanvas.style.pointerEvents = 'auto';
    this.penCanvas.style.zIndex = '9999';

    // Set canvas size to match chart with high-DPI support
    const rect = chartElement.getBoundingClientRect();
    const dpr = window.devicePixelRatio || 1;
    this.penCanvas.width = rect.width * dpr;
    this.penCanvas.height = rect.height * dpr;
    this.penCanvas.style.width = rect.width + 'px';
    this.penCanvas.style.height = rect.height + 'px';

    chartElement.appendChild(this.penCanvas);

    this.penContext = this.penCanvas.getContext('2d');
    if (this.penContext) {
      // Scale context for high-DPI displays
      const dpr = window.devicePixelRatio || 1;
      this.penContext.scale(dpr, dpr);

      this.penContext.lineCap = 'round';
      this.penContext.lineJoin = 'round';
      this.penContext.imageSmoothingEnabled = true;
      // Set high-quality smoothing if available
      if (this.penContext.imageSmoothingQuality) {
        this.penContext.imageSmoothingQuality = 'high';
      }
      // Draw existing pen drawings on the new canvas so they are visible when pen tool is active
      this.redrawPenDrawingsOnCanvas();
    }

    // Ensure SVG layer shows existing pen drawings (container has higher z-index than canvas)
    if (this.state.penDrawings.length > 0) {
      this.redrawAllPenDrawings();
    }

    // Add mouse event listeners for pen drawing
    this.addPenEventListeners();

    // Subscribe to chart events that require re-rendering pen drawings
    this.subscribeToPenDrawingEvents();
  }

  private cleanupPenCanvas(): void {
    // Keep the canvas in the DOM so drawings remain visible - just disable interaction
    if (this.penCanvas) {
      this.penCanvas.style.pointerEvents = 'none';
    }
    this.isDrawing = false;
    this.currentDrawing = null;
    // Keep penCanvas and penContext so redrawPenDrawingsOnCanvas still works on zoom/pan
  }

  private addPenEventListeners(): void {
    if (!this.penCanvas) return;

    // Mouse events
    this.penCanvas.addEventListener('mousedown', this.handlePenStart.bind(this), { passive: false });
    this.penCanvas.addEventListener('mousemove', this.handlePenMove.bind(this), { passive: false });
    this.penCanvas.addEventListener('mouseup', this.handlePenEnd.bind(this), { passive: false });
    this.penCanvas.addEventListener('mouseleave', this.handlePenEnd.bind(this), { passive: false });
    this.penCanvas.addEventListener('click', this.handlePenClick.bind(this), { passive: false });

    // Touch events for mobile
    this.penCanvas.addEventListener('touchstart', this.handlePenTouchStart.bind(this), { passive: false });
    this.penCanvas.addEventListener('touchmove', this.handlePenTouchMove.bind(this), { passive: false });
    this.penCanvas.addEventListener('touchend', this.handlePenEnd.bind(this), { passive: false });
  }

  private subscribeToPenDrawingEvents(): void {
    if (!this.chart) return;

    // Subscribe to time scale changes (zoom/pan)
    this.chart.timeScale().subscribeVisibleTimeRangeChange(() => {
      // Re-render pen drawings when time range changes (includes canvas sync)
      this.redrawAllPenDrawings();
    });

    // Note: Price scale change subscription is not available in this version
    // Pen drawings will be redrawn on resize and time scale changes
  }

  private handlePenStart(event: MouseEvent): void {
    if (!this.state.isPenMode || !this.penContext || this.isDrawing) return;

    event.preventDefault();
    event.stopPropagation();

    const rect = this.penCanvas!.getBoundingClientRect();
    const x = event.clientX - rect.left;
    const y = event.clientY - rect.top;

    this.startPenDrawing(x, y);
  }

  private handlePenTouchStart(event: TouchEvent): void {
    if (!this.state.isPenMode || !this.penContext || event.touches.length !== 1) return;

    event.preventDefault();
    event.stopPropagation();

    const rect = this.penCanvas!.getBoundingClientRect();
    const touch = event.touches[0];
    const x = touch.clientX - rect.left;
    const y = touch.clientY - rect.top;

    this.startPenDrawing(x, y);
  }

  private handlePenMove(event: MouseEvent): void {
    if (!this.state.isPenMode || !this.isDrawing || !this.penContext) return;

    event.preventDefault();
    event.stopPropagation();

    const rect = this.penCanvas!.getBoundingClientRect();
    const x = event.clientX - rect.left;
    const y = event.clientY - rect.top;

    this.continuePenDrawing(x, y);
  }

  private handlePenTouchMove(event: TouchEvent): void {
    if (!this.state.isPenMode || !this.isDrawing || !this.penContext || event.touches.length !== 1) return;

    event.preventDefault();
    event.stopPropagation();

    const rect = this.penCanvas!.getBoundingClientRect();
    const touch = event.touches[0];
    const x = touch.clientX - rect.left;
    const y = touch.clientY - rect.top;

    this.continuePenDrawing(x, y);
  }

  private handlePenEnd(event: Event): void {
    if (!this.state.isPenMode || !this.isDrawing) return;

    event.preventDefault();
    event.stopPropagation();

    this.finishPenDrawing();
  }

  private handlePenClick(event: MouseEvent): void {
    // Only handle clicks for selection when not in drawing mode or when no drawing is in progress
    if (!this.state.isPenMode || this.isDrawing) return;

    event.preventDefault();
    event.stopPropagation();

    const rect = this.penCanvas!.getBoundingClientRect();
    const x = event.clientX - rect.left;
    const y = event.clientY - rect.top;

    // Check if click is on an existing pen drawing
    const clickedDrawing = this.findPenDrawingAtPoint(x, y);
    if (clickedDrawing) {
      this.selectPenDrawing(clickedDrawing.id, event.clientX, event.clientY);
    } else {
      // Deselect if clicking on empty space
      this.deselectPenDrawing();
    }
  }

  private findPenDrawingAtPoint(x: number, y: number): PenDrawing | null {
    // Check each pen drawing to see if the click point is within its bounds
    for (const drawing of this.state.penDrawings) {
      if (this.isPointOnPenDrawing(drawing, x, y)) {
        return drawing;
      }
    }
    return null;
  }

  private isPointOnPenDrawing(drawing: PenDrawing, x: number, y: number): boolean {
    // Convert chart coordinates to pixel coordinates for hit testing
    const pixelPoints = drawing.points.map(point => this.chartToPixelCoordinates(point.time, point.price));

    if (pixelPoints.length < 2) return false;

    // Check if point is within a certain distance of the pen drawing path
    const hitThreshold = Math.max(drawing.lineWidth * 2, 8); // Minimum 8px hit area

    for (let i = 0; i < pixelPoints.length - 1; i++) {
      const p1 = pixelPoints[i];
      const p2 = pixelPoints[i + 1];

      if (!p1 || !p2) continue;

      // Calculate distance from point to line segment
      const distance = this.distanceToLineSegment(x, y, p1.x, p1.y, p2.x, p2.y);
      if (distance <= hitThreshold) {
        return true;
      }
    }

    return false;
  }

  private distanceToLineSegment(px: number, py: number, x1: number, y1: number, x2: number, y2: number): number {
    const A = px - x1;
    const B = py - y1;
    const C = x2 - x1;
    const D = y2 - y1;

    const dot = A * C + B * D;
    const lenSq = C * C + D * D;

    if (lenSq === 0) {
      // Line segment is actually a point
      return Math.sqrt(A * A + B * B);
    }

    let param = dot / lenSq;

    let xx, yy;

    if (param < 0) {
      xx = x1;
      yy = y1;
    } else if (param > 1) {
      xx = x2;
      yy = y2;
    } else {
      xx = x1 + param * C;
      yy = y1 + param * D;
    }

    const dx = px - xx;
    const dy = py - yy;
    return Math.sqrt(dx * dx + dy * dy);
  }

  private startPenDrawing(x: number, y: number): void {
    this.isDrawing = true;

    // Convert pixel coordinates to chart coordinates
    const chartCoords = this.pixelToChartCoordinates(x, y);
    if (!chartCoords) {
      this.isDrawing = false;
      return;
    }

    // Create new pen drawing with chart coordinates
    this.currentDrawing = {
      id: `pen-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      points: [chartCoords],
      color: this.state.penColor,
      lineWidth: this.state.penSize,
      visible: true,
      name: `Drawing ${this.state.penDrawings.length + 1}`,
      createdAt: new Date()
    };

    // Set pen context properties for temporary drawing
    if (this.penContext) {
      this.penContext.strokeStyle = this.state.penColor;
      this.penContext.lineWidth = this.state.penSize;
      this.penContext.beginPath();
      this.penContext.moveTo(x, y);
    }
  }

  private continuePenDrawing(x: number, y: number): void {
    if (!this.currentDrawing || !this.penContext) return;

    // Convert pixel coordinates to chart coordinates
    const chartCoords = this.pixelToChartCoordinates(x, y);
    if (!chartCoords) return;

    // Avoid oversampling: skip points that are visually too close to the previous point.
    const points = this.currentDrawing.points;
    const lastPoint = points[points.length - 1];
    const lastPixelCoords = this.chartToPixelCoordinates(lastPoint.time, lastPoint.price);
    if (lastPixelCoords) {
      const dx = x - lastPixelCoords.x;
      const dy = y - lastPixelCoords.y;
      const minDistance = TradingViewChartService.PEN_MIN_POINT_DISTANCE_PX;
      if ((dx * dx + dy * dy) < (minDistance * minDistance)) {
        return;
      }
    }

    // Add chart coordinates to current drawing.
    this.currentDrawing.points.push(chartCoords);

    // Redraw persisted drawings + current stroke using a unified smoothing routine
    // so live preview and saved stroke geometry stay visually consistent.
    if (this.penCanvas) {
      this.redrawPenDrawingsOnCanvas();

      const previewPixelPoints: Array<{ x: number, y: number }> = [];
      for (const point of this.currentDrawing.points) {
        const pixel = this.chartToPixelCoordinates(point.time, point.price);
        if (pixel) {
          previewPixelPoints.push(pixel);
        }
      }
      this.drawSmoothStrokeOnCanvas(previewPixelPoints, this.currentDrawing.color, this.currentDrawing.lineWidth);
    }
  }

  private finishPenDrawing(): void {
    if (!this.currentDrawing) return;

    // Only save drawing if it has multiple points
    if (this.currentDrawing.points.length > 1) {
      this.state.penDrawings.push(this.currentDrawing);
      // Render SVG for persistence and selection
      this.renderPenDrawing(this.currentDrawing);
      if (this.onLinesChangedCallback) {
        this.onLinesChangedCallback();
      }
    }

    // Clear canvas and redraw all persisted strokes so drawing is visible
    if (this.penContext && this.penCanvas) {
      this.redrawPenDrawingsOnCanvas();
    }

    this.isDrawing = false;
    this.currentDrawing = null;
  }

  /** Draw all pen drawings on the canvas (for visibility when pen mode is active). */
  private redrawPenDrawingsOnCanvas(): void {
    if (!this.penContext || !this.penCanvas) return;
    const canvasWidth = this.penCanvas.clientWidth || this.penCanvas.width;
    const canvasHeight = this.penCanvas.clientHeight || this.penCanvas.height;
    this.penContext.clearRect(0, 0, canvasWidth, canvasHeight);
    for (const drawing of this.state.penDrawings) {
      if (!drawing.visible || drawing.points.length < 2) continue;
      const pixelPoints: Array<{ x: number, y: number }> = [];
      for (const pt of drawing.points) {
        const coords = this.chartToPixelCoordinates(pt.time, pt.price);
        if (coords) pixelPoints.push(coords);
      }
      this.drawSmoothStrokeOnCanvas(pixelPoints, drawing.color, drawing.lineWidth);
    }
  }

  private drawSmoothStrokeOnCanvas(
    pixelPoints: Array<{ x: number, y: number }>,
    color: string,
    lineWidth: number
  ): void {
    if (!this.penContext || pixelPoints.length < 2) return;

    this.penContext.strokeStyle = color;
    this.penContext.lineWidth = lineWidth;
    this.penContext.lineCap = 'round';
    this.penContext.lineJoin = 'round';
    this.penContext.beginPath();
    this.penContext.moveTo(pixelPoints[0].x, pixelPoints[0].y);

    if (pixelPoints.length === 2) {
      this.penContext.lineTo(pixelPoints[1].x, pixelPoints[1].y);
      this.penContext.stroke();
      return;
    }

    for (let i = 1; i < pixelPoints.length - 1; i++) {
      const current = pixelPoints[i];
      const next = pixelPoints[i + 1];
      const endX = (current.x + next.x) / 2;
      const endY = (current.y + next.y) / 2;
      this.penContext.quadraticCurveTo(current.x, current.y, endX, endY);
    }

    const last = pixelPoints[pixelPoints.length - 1];
    this.penContext.lineTo(last.x, last.y);
    this.penContext.stroke();
  }

  private renderPenDrawing(drawing: PenDrawing): void {
    if (!this.chart) return;

    const chartElement = this.chart.chartElement();
    if (!chartElement) return;

    // Ensure dedicated overlay container exists (so pen drawings always display above chart)
    if (!this.penDrawingsContainer || !this.penDrawingsContainer.parentElement) {
      this.penDrawingsContainer = document.createElement('div');
      this.penDrawingsContainer.style.cssText = 'position:absolute;top:0;left:0;right:0;bottom:0;z-index:10000;pointer-events:none;visibility:visible;';
      chartElement.style.position = 'relative';
      chartElement.appendChild(this.penDrawingsContainer);
    }

    // Convert chart coordinates to pixel coordinates
    const pixelPoints: Array<{ x: number, y: number }> = [];
    for (const point of drawing.points) {
      const pixelCoords = this.chartToPixelCoordinates(point.time, point.price);
      if (pixelCoords) {
        pixelPoints.push(pixelCoords);
      }
    }

    // Skip rendering if we don't have enough valid points
    if (pixelPoints.length < 2) return;

    // Calculate bounding box for the drawing
    const bounds = this.calculateDrawingBounds(pixelPoints, drawing.lineWidth);

    // Create SVG element for the drawing with tight bounds (z-index above pen canvas so drawings are visible)
    const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    svg.style.position = 'absolute';
    svg.style.top = `${bounds.top}px`;
    svg.style.left = `${bounds.left}px`;
    svg.style.width = `${bounds.width}px`;
    svg.style.height = `${bounds.height}px`;
    svg.style.pointerEvents = 'none'; // SVG itself doesn't capture events
    svg.setAttribute('id', `pen-drawing-${drawing.id}`);

    // Create path element using pixel coordinates (adjusted for SVG bounds)
    const path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
    const pathData = this.createPathFromPixelPoints(pixelPoints, bounds);
    path.setAttribute('d', pathData);
    path.setAttribute('stroke', drawing.color);
    path.setAttribute('stroke-width', drawing.lineWidth.toString());
    path.setAttribute('fill', 'none');
    path.setAttribute('stroke-linecap', 'round');
    path.setAttribute('stroke-linejoin', 'round');
    path.style.pointerEvents = 'auto'; // Only the path captures events
    path.style.cursor = 'pointer';

    // Add stroke tolerance for easier clicking
    const tolerance = Math.max(10, drawing.lineWidth + 6);
    path.setAttribute('stroke-width', tolerance.toString());
    path.setAttribute('stroke-opacity', '0'); // Make the tolerance invisible

    // Create visible path on top
    const visiblePath = document.createElementNS('http://www.w3.org/2000/svg', 'path');
    visiblePath.setAttribute('d', pathData);
    visiblePath.setAttribute('stroke', drawing.color);
    visiblePath.setAttribute('stroke-width', drawing.lineWidth.toString());
    visiblePath.setAttribute('fill', 'none');
    visiblePath.setAttribute('stroke-linecap', 'round');
    visiblePath.setAttribute('stroke-linejoin', 'round');
    visiblePath.style.pointerEvents = 'none'; // Visual only, no events

    svg.appendChild(path);
    svg.appendChild(visiblePath);

    // Add click handler for selection
    path.addEventListener('click', (event) => {
      event.preventDefault();
      event.stopPropagation();
      this.selectPenDrawing(drawing.id, event.clientX, event.clientY);
    });

    // Add visual feedback for selection
    if (this.selectedPenDrawingId === drawing.id) {
      visiblePath.setAttribute('stroke', '#FF6B35'); // Orange selection color
      visiblePath.setAttribute('stroke-width', (drawing.lineWidth + 2).toString());
      svg.style.filter = 'drop-shadow(0 0 3px rgba(255, 107, 53, 0.5))';
    }

    this.penDrawingsContainer!.appendChild(svg);
  }

  private createPathFromPixelPoints(points: Array<{ x: number, y: number }>, bounds?: { left: number, top: number, width: number, height: number }): string {
    if (points.length === 0) return '';

    // Adjust coordinates relative to SVG bounds if provided
    const adjustPoint = bounds ?
      (point: { x: number, y: number }) => ({ x: point.x - bounds.left, y: point.y - bounds.top }) :
      (point: { x: number, y: number }) => point;

    const adjustedPoints = points.map(adjustPoint);

    // For single point, just draw a small circle
    if (adjustedPoints.length === 1) {
      const p = adjustedPoints[0];
      return `M ${p.x - 1} ${p.y} A 1 1 0 1 1 ${p.x + 1} ${p.y} A 1 1 0 1 1 ${p.x - 1} ${p.y}`;
    }

    // For two points, draw a straight line
    if (adjustedPoints.length === 2) {
      return `M ${adjustedPoints[0].x} ${adjustedPoints[0].y} L ${adjustedPoints[1].x} ${adjustedPoints[1].y}`;
    }

    // For multiple points, create smooth curves using quadratic Bezier
    return this.createSmoothPath(adjustedPoints);
  }

  private createSmoothPath(points: Array<{ x: number, y: number }>): string {
    if (points.length < 3) {
      // Fallback for insufficient points
      let pathData = `M ${points[0].x} ${points[0].y}`;
      for (let i = 1; i < points.length; i++) {
        pathData += ` L ${points[i].x} ${points[i].y}`;
      }
      return pathData;
    }

    let pathData = `M ${points[0].x} ${points[0].y}`;

    // Create smooth curves using quadratic Bezier curves
    for (let i = 1; i < points.length - 1; i++) {
      const current = points[i];
      const next = points[i + 1];

      // Calculate control point for smooth curve
      const controlX = current.x;
      const controlY = current.y;

      // Calculate end point (midpoint between current and next for smoother curves)
      const endX = (current.x + next.x) / 2;
      const endY = (current.y + next.y) / 2;

      pathData += ` Q ${controlX} ${controlY} ${endX} ${endY}`;
    }

    // Add final line to last point
    const lastPoint = points[points.length - 1];
    pathData += ` T ${lastPoint.x} ${lastPoint.y}`;

    return pathData;
  }

  private calculateDrawingBounds(points: Array<{ x: number, y: number }>, lineWidth: number): { left: number, top: number, width: number, height: number } {
    if (points.length === 0) {
      return { left: 0, top: 0, width: 0, height: 0 };
    }

    // Find min/max coordinates
    let minX = points[0].x;
    let maxX = points[0].x;
    let minY = points[0].y;
    let maxY = points[0].y;

    for (const point of points) {
      minX = Math.min(minX, point.x);
      maxX = Math.max(maxX, point.x);
      minY = Math.min(minY, point.y);
      maxY = Math.max(maxY, point.y);
    }

    // Add padding for line width and selection tolerance
    const padding = Math.max(20, lineWidth + 10);

    return {
      left: Math.max(0, minX - padding),
      top: Math.max(0, minY - padding),
      width: (maxX - minX) + (2 * padding),
      height: (maxY - minY) + (2 * padding)
    };
  }

  private clearPenDrawingsFromDOM(): void {
    // If the chart re-rendered and detached our container, recreate it on demand.
    if (this.penDrawingsContainer && !this.penDrawingsContainer.parentElement) {
      this.penDrawingsContainer = null;
    }
    if (!this.penDrawingsContainer) return;
    this.penDrawingsContainer.innerHTML = '';
  }

  private redrawAllPenDrawings(): void {
    this.clearPenDrawingsFromDOM();
    this.state.penDrawings.forEach(drawing => {
      if (drawing.visible) {
        this.renderPenDrawing(drawing);
      }
    });
    // Keep the temporary pen canvas in sync (color/width updates only touched SVG before).
    this.redrawPenDrawingsOnCanvas();
  }

  /** Redraw all pen drawings (e.g. after loading from API when time scale is ready). */
  public redrawPenDrawings(): void {
    this.redrawAllPenDrawings();
  }

  // Convert pixel coordinates to chart coordinates (time/price)
  private pixelToChartCoordinates(x: number, y: number): { time: Time, price: number } | null {
    if (!this.chart || !this.mainSeries) return null;

    try {
      let price: number | null = this.mainSeries.coordinateToPrice(y) as number | null;

      // Fallback for price when y is outside the plot area
      if (price === null) {
        if (!this.fullDataset?.length) return null;
        let minP = this.fullDataset[0].low;
        let maxP = this.fullDataset[0].high;
        for (const bar of this.fullDataset) {
          minP = Math.min(minP, bar.low);
          maxP = Math.max(maxP, bar.high);
        }
        const height = this.chart.chartElement()?.clientHeight ?? 400;
        if (height > 0) {
          const t = Math.max(0, Math.min(1, 1 - y / height));
          price = minP + t * (maxP - minP);
        }
      }

      // Use continuous time along the visible bar axis (not coordinateToTime bar snapping).
      const anchors = this.getPenTimeAxisAnchors();
      let timeSec = this.interpolateTimeSecFromPixelX(x, anchors);
      if (timeSec === null) {
        timeSec = this.interpolateTimeSecFromVisibleRange(x);
      }

      if (timeSec === null || price === null) return null;
      return { time: timeSec as Time, price };
    } catch (error) {
      return null;
    }
  }

  /**
   * Visible bars as x/time anchors so pen strokes interpolate smoothly on 1D and intraday views
   * instead of snapping to discrete bar timestamps.
   */
  private getPenTimeAxisAnchors(): Array<{ x: number; timeSec: number }> {
    if (!this.chart || !this.fullDataset?.length) return [];

    const timeScale = this.chart.timeScale();
    const anchors: Array<{ x: number; timeSec: number }> = [];
    const logicalRange = timeScale.getVisibleLogicalRange();

    if (logicalRange) {
      const fromIdx = Math.max(0, Math.floor(logicalRange.from));
      const toIdx = Math.min(this.fullDataset.length - 1, Math.ceil(logicalRange.to));
      for (let i = fromIdx; i <= toIdx; i++) {
        const bar = this.fullDataset[i];
        const timeSec = this.timeToSeconds(bar.time);
        const barX = timeScale.timeToCoordinate(bar.time as Time);
        if (barX !== null && Number.isFinite(barX) && !isNaN(timeSec)) {
          anchors.push({ x: barX, timeSec });
        }
      }
    }

    if (anchors.length < 2) {
      const step = Math.max(1, Math.floor(this.fullDataset.length / 250));
      for (let i = 0; i < this.fullDataset.length; i += step) {
        const bar = this.fullDataset[i];
        const timeSec = this.timeToSeconds(bar.time);
        const barX = timeScale.timeToCoordinate(bar.time as Time);
        if (barX !== null && Number.isFinite(barX) && !isNaN(timeSec)) {
          anchors.push({ x: barX, timeSec });
        }
      }
      const lastBar = this.fullDataset[this.fullDataset.length - 1];
      const lastSec = this.timeToSeconds(lastBar.time);
      const lastX = timeScale.timeToCoordinate(lastBar.time as Time);
      if (lastX !== null && anchors[anchors.length - 1]?.timeSec !== lastSec) {
        anchors.push({ x: lastX, timeSec: lastSec });
      }
    }

    anchors.sort((a, b) => a.x - b.x);
    const deduped: Array<{ x: number; timeSec: number }> = [];
    for (const anchor of anchors) {
      if (deduped.length === 0 || Math.abs(deduped[deduped.length - 1].x - anchor.x) > 0.01) {
        deduped.push(anchor);
      }
    }
    return deduped;
  }

  private interpolateTimeSecFromPixelX(
    x: number,
    anchors: Array<{ x: number; timeSec: number }>
  ): number | null {
    if (anchors.length === 0) return null;
    if (anchors.length === 1) return anchors[0].timeSec;

    if (x <= anchors[0].x) {
      const a = anchors[0];
      const b = anchors[1];
      const dx = b.x - a.x;
      return dx !== 0 ? a.timeSec + ((x - a.x) / dx) * (b.timeSec - a.timeSec) : a.timeSec;
    }

    const lastIdx = anchors.length - 1;
    if (x >= anchors[lastIdx].x) {
      const a = anchors[lastIdx - 1];
      const b = anchors[lastIdx];
      const dx = b.x - a.x;
      return dx !== 0 ? a.timeSec + ((x - a.x) / dx) * (b.timeSec - a.timeSec) : b.timeSec;
    }

    for (let i = 0; i < anchors.length - 1; i++) {
      const a = anchors[i];
      const b = anchors[i + 1];
      if (x >= a.x && x <= b.x) {
        const dx = b.x - a.x;
        return dx !== 0 ? a.timeSec + ((x - a.x) / dx) * (b.timeSec - a.timeSec) : a.timeSec;
      }
    }

    return anchors[lastIdx].timeSec;
  }

  private interpolatePixelXFromTimeSec(
    timeSec: number,
    anchors: Array<{ x: number; timeSec: number }>
  ): number | null {
    if (anchors.length === 0) return null;
    if (anchors.length === 1) return anchors[0].x;

    if (timeSec <= anchors[0].timeSec) {
      const a = anchors[0];
      const b = anchors[1];
      const dt = b.timeSec - a.timeSec;
      return dt !== 0 ? a.x + ((timeSec - a.timeSec) / dt) * (b.x - a.x) : a.x;
    }

    const lastIdx = anchors.length - 1;
    if (timeSec >= anchors[lastIdx].timeSec) {
      const a = anchors[lastIdx - 1];
      const b = anchors[lastIdx];
      const dt = b.timeSec - a.timeSec;
      return dt !== 0 ? a.x + ((timeSec - a.timeSec) / dt) * (b.x - a.x) : b.x;
    }

    for (let i = 0; i < anchors.length - 1; i++) {
      const a = anchors[i];
      const b = anchors[i + 1];
      if (timeSec >= a.timeSec && timeSec <= b.timeSec) {
        const dt = b.timeSec - a.timeSec;
        return dt !== 0 ? a.x + ((timeSec - a.timeSec) / dt) * (b.x - a.x) : a.x;
      }
    }

    return anchors[lastIdx].x;
  }

  private interpolateTimeSecFromVisibleRange(x: number): number | null {
    if (!this.chart || !this.fullDataset?.length) return null;

    const timeScale = this.chart.timeScale();
    const visibleRange = timeScale.getVisibleRange();
    if (!visibleRange) {
      return this.timeToSeconds(this.fullDataset[this.fullDataset.length - 1].time);
    }

    const firstVisibleSec = this.timeToSeconds(visibleRange.from as Time);
    const lastVisibleSec = this.timeToSeconds(visibleRange.to as Time);
    const lastBarX = timeScale.timeToCoordinate(visibleRange.to as Time);
    const firstBarX = timeScale.timeToCoordinate(visibleRange.from as Time);

    if (firstBarX !== null && lastBarX !== null && lastBarX !== firstBarX) {
      const t = (x - firstBarX) / (lastBarX - firstBarX);
      return firstVisibleSec + t * (lastVisibleSec - firstVisibleSec);
    }

    return lastVisibleSec;
  }

  // Convert chart coordinates (time/price) to pixel coordinates
  private chartToPixelCoordinates(time: Time, price: number): { x: number, y: number } | null {
    if (!this.chart || !this.mainSeries) return null;

    try {
      const timeScale = this.chart.timeScale();
      const timeSec = this.timeToSeconds(time);
      let xVal: number | null = null;
      let yVal: number | null = this.mainSeries.priceToCoordinate(price) as number | null;

      // Prefer bar-axis interpolation so pen round-trips match pixel capture (smooth on 1D/intraday).
      if (this.fullDataset?.length && !isNaN(timeSec)) {
        const anchors = this.getPenTimeAxisAnchors();
        xVal = this.interpolatePixelXFromTimeSec(timeSec, anchors);
      }

      // BusinessDay / exact bar times: fall back to library projection.
      if (xVal === null) {
        xVal = timeScale.timeToCoordinate(time) as number | null;
      }
      if (xVal === null && this.fullDataset?.length && !isNaN(timeSec)) {
        xVal = timeScale.timeToCoordinate(timeSec as Time) as number | null;
      }
      if (xVal === null && this.fullDataset?.length && !isNaN(timeSec)) {
        xVal = this.timeToCoordinateWithInterpolation(timeSec);
      }
      if (yVal === null && this.fullDataset?.length) {
        yVal = this.priceToCoordinateWithInterpolation(price);
      }

      if (
        xVal !== null &&
        yVal !== null &&
        Number.isFinite(xVal) &&
        Number.isFinite(yVal)
      ) {
        return { x: xVal, y: yVal };
      }
      return null;
    } catch (error) {
      return null;
    }
  }

  /** Interpolate y for a price using fullDataset range (so pen points render when priceToCoordinate returns null). */
  private priceToCoordinateWithInterpolation(price: number): number | null {
    if (!this.mainSeries || !this.fullDataset?.length) return null;
    let minP = this.fullDataset[0].low;
    let maxP = this.fullDataset[0].high;
    for (const bar of this.fullDataset) {
      minP = Math.min(minP, bar.low);
      maxP = Math.max(maxP, bar.high);
    }
    const range = maxP - minP || 1;
    const clampedPrice = Math.max(minP, Math.min(maxP, price));
    let yTop = this.mainSeries.priceToCoordinate(maxP);
    let yBottom = this.mainSeries.priceToCoordinate(minP);
    if (yTop === null || yBottom === null) {
      const chartEl = this.chart?.chartElement();
      const h = chartEl?.getBoundingClientRect?.()?.height ?? 400;
      const t = (clampedPrice - minP) / range;
      return (1 - t) * h * 0.85;
    }
    const t = (clampedPrice - minP) / range;
    return yBottom + t * (yTop - yBottom);
  }

  /** Extract time as Unix seconds from chart data point (handles number, business day, or {time,timestamp}). */
  private timeToSeconds(t: Time | any): number {
    if (typeof t === 'number') return t > 10000000000 ? t / 1000 : t;
    if (t?.year != null && t?.month != null && t?.day != null) {
      return Math.floor(new Date(t.year, t.month - 1, t.day).getTime() / 1000);
    }
    const val = t?.timestamp ?? t?.time ?? 0;
    return val > 10000000000 ? val / 1000 : val;
  }

  /** Interpolate x for any time using visible bar anchors (matches pixelToChartCoordinates). */
  private timeToCoordinateWithInterpolation(timeSec: number): number | null {
    if (!this.chart || !this.fullDataset?.length || isNaN(timeSec)) return null;

    const anchors = this.getPenTimeAxisAnchors();
    const anchoredX = this.interpolatePixelXFromTimeSec(timeSec, anchors);
    if (anchoredX !== null && Number.isFinite(anchoredX)) {
      const containerW = (this.chartHostContainer ?? this.chart.chartElement())?.getBoundingClientRect?.()?.width
        ?? this.chart.timeScale().width();
      return Math.max(-containerW * 0.1, Math.min(containerW * 1.1, anchoredX));
    }

    const timeScale = this.chart.timeScale();

    // Fallback to visible range endpoints
    const visibleRange = timeScale.getVisibleRange();
    if (visibleRange) {
      const fromSec = this.timeToSeconds(visibleRange.from as Time);
      const toSec = this.timeToSeconds(visibleRange.to as Time);
      const x1 = timeScale.timeToCoordinate(visibleRange.from as Time);
      const x2 = timeScale.timeToCoordinate(visibleRange.to as Time);
      if (x1 !== null && x2 !== null && toSec !== fromSec) {
        const t = (timeSec - fromSec) / (toSec - fromSec);
        const containerW = (this.chartHostContainer ?? this.chart.chartElement())?.getBoundingClientRect?.()?.width ?? timeScale.width();
        return Math.max(-containerW * 0.1, Math.min(containerW * 1.1, x1 + t * (x2 - x1)));
      }
    }

    // Fallback to full dataset range
    const firstTime = this.timeToSeconds(this.fullDataset[0].time);
    const lastTime = this.timeToSeconds(this.fullDataset[this.fullDataset.length - 1].time);
    if (isNaN(firstTime) || isNaN(lastTime)) return null;
    const width = timeScale.width();
    const x1 = timeScale.timeToCoordinate(firstTime as Time);
    const x2 = timeScale.timeToCoordinate(lastTime as Time);
    if (x1 === null || x2 === null) {
      const chartEl = this.chart.chartElement();
      const w = chartEl?.getBoundingClientRect?.()?.width ?? width;
      const t = (timeSec - firstTime) / (lastTime - firstTime || 1);
      return Math.max(-w * 0.1, Math.min(w * 1.1, t * w));
    }
    const t = (timeSec - firstTime) / (lastTime - firstTime || 1);
    const x = x1 + t * (x2 - x1);
    return Math.max(-width * 0.1, Math.min(width * 1.1, x));
  }

  // ===== MEASURING TOOL METHODS =====
  public getMeasuringMode(): boolean {
    return this.state.isMeasuringMode;
  }

  public getMeasurements(): Measurement[] {
    return [...this.state.measurements];
  }

  public setMeasuringMode(enabled: boolean): void {
    this.state.isMeasuringMode = enabled;
    if (enabled) {
      this.initializeMeasuring();
    } else {
      this.cleanupMeasuring();
    }
  }

  private measureStartPoint: { time: Time; price: number; x: number; y: number } | null = null;
  private currentMeasurement: Measurement | null = null;
  private measureClickHandler?: (param: any) => void;
  private measureMoveHandler?: (param: any) => void;
  private measureOverlay: HTMLElement | null = null;
  private permanentMeasurementOverlays: Map<string, HTMLElement> = new Map();
  private escapeKeyHandler?: (event: KeyboardEvent) => void;

  private initializeMeasuring(): void {
    if (!this.chart) return;


    // Add ESC key handler to exit measuring mode
    this.addEscapeKeyHandler();

    // Set cursor style for measuring and ensure proper positioning context
    const chartContainer = this.chart.chartElement();
    if (chartContainer) {
      chartContainer.style.cursor = 'crosshair';
      // Set position: relative to create a positioning context for absolute children
      chartContainer.style.position = 'relative';
    }

    // Set up cursor movement tracking for live measuring box
    // First, try crosshair move events
    this.measureMoveHandler = (param: any) => {
      if (!this.state.isMeasuringMode || !this.measureStartPoint || !param.point) return;

      const time = param.time;
      const price = this.mainSeries?.coordinateToPrice(param.point.y);

      if (time && price !== undefined && price !== null) {
        const priceValue = typeof price === 'number' ? price : price as number;
        this.updateLiveMeasurement(time, priceValue, param.point.x, param.point.y);
      }
    };

    this.chart.subscribeCrosshairMove(this.measureMoveHandler);

    // Also set up direct mouse move tracking as backup
    const mouseMoveHandler = (event: MouseEvent) => {
      if (!this.state.isMeasuringMode || !this.measureStartPoint) return;

      const rect = chartContainer.getBoundingClientRect();
      const x = event.clientX - rect.left;
      const y = event.clientY - rect.top;

      // Convert pixel coordinates to chart coordinates
      const time = this.chart?.timeScale().coordinateToTime(x);
      const price = this.mainSeries?.coordinateToPrice(y);

      if (time && price !== undefined && price !== null) {
        const priceValue = typeof price === 'number' ? price : price as number;
        this.updateLiveMeasurement(time, priceValue, x, y);
      }
    };

    chartContainer.addEventListener('mousemove', mouseMoveHandler);

    // Store the handler for cleanup
    (chartContainer as any)._measureMouseMoveHandler = mouseMoveHandler;

    // Note: Measuring clicks are now handled in the main setupChartClickHandler method
    // No separate click handler needed as measuring has priority in the main handler
  }

  private updateLiveMeasurement(endTime: Time, endPrice: number, endX: number, endY: number): void {
    if (!this.measureStartPoint) return;


    // Calculate measurement data
    const measureData = this.calculateMeasurementData(
      this.measureStartPoint.time,
      this.measureStartPoint.price,
      endTime,
      endPrice
    );


    // Update or create the overlay
    this.updateMeasurementOverlay(
      this.measureStartPoint.x,
      this.measureStartPoint.y,
      endX,
      endY,
      measureData
    );
  }

  private calculateMeasurementData(startTime: Time, startPrice: number, endTime: Time, endPrice: number) {
    // Convert times to dates for calculation
    const startDate = new Date(typeof startTime === 'string' ? startTime : (startTime as number) * 1000);
    const endDate = new Date(typeof endTime === 'string' ? endTime : (endTime as number) * 1000);

    // Calculate time difference in days
    const timeDiffMs = Math.abs(endDate.getTime() - startDate.getTime());
    const days = Math.round(timeDiffMs / (1000 * 60 * 60 * 24));

    // Calculate price changes
    const priceChange = endPrice - startPrice;
    const percentChange = startPrice !== 0 ? (priceChange / startPrice) * 100 : 0;

    return {
      days,
      priceChange,
      percentChange,
      startPrice,
      endPrice,
      startDate: startDate.toLocaleDateString(),
      endDate: endDate.toLocaleDateString()
    };
  }

  private updateMeasurementOverlay(startX: number, startY: number, endX: number, endY: number, data: any): void {
    if (!this.chart) return;

    const chartContainer = this.chart.chartElement();
    if (!chartContainer) return;

    // Get the chart container's position relative to the viewport
    const containerRect = chartContainer.getBoundingClientRect();

    // Remove existing overlay
    if (this.measureOverlay) {
      this.measureOverlay.remove();
    }

    // Create overlay container - use fixed positioning relative to viewport
    this.measureOverlay = document.createElement('div');
    this.measureOverlay.style.cssText = `
      position: fixed;
      pointer-events: none;
      z-index: 999999;
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
      font-size: 12px;
    `;

    // Calculate overlay position and size relative to viewport
    const left = Math.min(startX, endX) + containerRect.left;
    const top = Math.min(startY, endY) + containerRect.top;
    const width = Math.abs(endX - startX);
    const height = Math.abs(endY - startY);

    // Determine direction and color based on price movement
    const isPositiveMove = data.priceChange >= 0;
    const horizontalDirection = endX > startX ? 'right' : 'left';
    const verticalDirection = endY < startY ? 'up' : 'down'; // Note: Y coordinates are inverted (up = smaller Y)

    // Color theme based on positive/negative movement
    const boxColor = isPositiveMove ? '#0096ff' : '#ff4444';
    const boxBgColor = isPositiveMove ? 'rgba(0, 150, 255, 0.15)' : 'rgba(255, 68, 68, 0.15)';
    const shadowColor = isPositiveMove ? 'rgba(0, 150, 255, 0.3)' : 'rgba(255, 68, 68, 0.3)';

    // Create measurement selection box
    const box = document.createElement('div');
    box.style.cssText = `
      position: absolute;
      left: 0px;
      top: 0px;
      width: ${width}px;
      height: ${height}px;
      background: ${boxBgColor};
      border: 2px solid ${boxColor};
      border-radius: 4px;
      box-sizing: border-box;
      box-shadow: 0 0 8px ${shadowColor};
    `;

    // Only show arrows if the box is large enough to be visible
    if (width > 20 && height > 20) {
      const centerX = width / 2;
      const centerY = height / 2;

      // Create horizontal arrow spanning full width
      const horizontalArrow = document.createElement('div');
      horizontalArrow.style.cssText = `
        position: absolute;
        left: 8px;
        top: ${centerY - 1}px;
        width: ${width - 16}px;
        height: 2px;
        background: ${boxColor};
        z-index: 10;
      `;

      // Create horizontal arrow head pointing in the direction of measurement
      const horizontalArrowHead = document.createElement('div');
      if (horizontalDirection === 'right') {
        horizontalArrowHead.style.cssText = `
          position: absolute;
          right: -6px;
          top: -4px;
          width: 0;
          height: 0;
          border-top: 5px solid transparent;
          border-bottom: 5px solid transparent;
          border-left: 8px solid ${boxColor};
          z-index: 10;
        `;
      } else {
        horizontalArrowHead.style.cssText = `
          position: absolute;
          left: -6px;
          top: -4px;
          width: 0;
          height: 0;
          border-top: 5px solid transparent;
          border-bottom: 5px solid transparent;
          border-right: 8px solid ${boxColor};
          z-index: 10;
        `;
      }

      horizontalArrow.appendChild(horizontalArrowHead);

      // Create vertical arrow spanning full height
      const verticalArrow = document.createElement('div');
      verticalArrow.style.cssText = `
        position: absolute;
        left: ${centerX - 1}px;
        top: 8px;
        width: 2px;
        height: ${height - 16}px;
        background: ${boxColor};
        z-index: 10;
      `;

      // Create vertical arrow head pointing in the direction of measurement
      const verticalArrowHead = document.createElement('div');
      if (verticalDirection === 'up') {
        verticalArrowHead.style.cssText = `
          position: absolute;
          left: -4px;
          top: -6px;
          width: 0;
          height: 0;
          border-left: 5px solid transparent;
          border-right: 5px solid transparent;
          border-bottom: 8px solid ${boxColor};
          z-index: 10;
        `;
      } else {
        verticalArrowHead.style.cssText = `
          position: absolute;
          left: -4px;
          bottom: -6px;
          width: 0;
          height: 0;
          border-left: 5px solid transparent;
          border-right: 5px solid transparent;
          border-top: 8px solid ${boxColor};
          z-index: 10;
        `;
      }

      verticalArrow.appendChild(verticalArrowHead);

      // Add arrows to the box
      box.appendChild(horizontalArrow);
      box.appendChild(verticalArrow);
    }

    // Position the overlay container at the calculated viewport coordinates
    this.measureOverlay.style.left = `${left}px`;
    this.measureOverlay.style.top = `${top}px`;

    // Create info panel - position it relative to the measurement box center
    const infoPanel = document.createElement('div');

    const panelWidth = 200;
    const panelHeight = 100;
    const topOffset = 35; // Distance above box - good spacing
    const bottomOffset = 15; // Distance below box - tighter spacing

    // Calculate center of the measurement box
    const centerX = Math.min(startX, endX) + Math.abs(endX - startX) / 2;
    const centerY = Math.min(startY, endY) + Math.abs(endY - startY) / 2;

    // Determine vertical direction of measurement
    const isUpwardMeasurement = data.priceChange > 0;

    // Position panel above box for upward movements, below for downward movements
    let panelX = centerX + containerRect.left - panelWidth / 2; // Center horizontally
    let panelY;

    if (isUpwardMeasurement) {
      // Position above the box for upward movements
      panelY = Math.min(startY, endY) + containerRect.top - panelHeight - topOffset;
    } else {
      // Position below the box for downward movements  
      panelY = Math.max(startY, endY) + containerRect.top + bottomOffset;
    }

    // Adjust if panel would go off-screen horizontally
    if (panelX < 10) {
      panelX = 10;
    } else if (panelX + panelWidth > window.innerWidth - 10) {
      panelX = window.innerWidth - panelWidth - 10;
    }

    // Adjust if panel would go off-screen vertically
    if (panelY < 10) {
      panelY = 10;
    } else if (panelY + panelHeight > window.innerHeight - 10) {
      panelY = window.innerHeight - panelHeight - 10;
    }

    infoPanel.style.cssText = `
      position: fixed;
      left: ${panelX}px;
      top: ${panelY}px;
      background: rgba(20, 20, 30, 0.95);
      color: white;
      padding: 12px 16px;
      border-radius: 8px;
      border: 2px solid ${boxColor};
      min-width: 180px;
      box-shadow: 0 8px 24px rgba(0, 0, 0, 0.4);
      font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
      font-size: 13px;
      font-weight: 500;
      backdrop-filter: blur(8px);
      z-index: 1000000;
    `;

    const isPositiveChange = data.priceChange >= 0;
    const changeColor = isPositiveChange ? '#00ff00' : '#ff4444';

    infoPanel.innerHTML = `
      <div style="font-weight: bold; margin-bottom: 4px; color: #ffffff;">📏 Measurement</div>
      <div style="margin-bottom: 2px;">📅 Days: <strong>${data.days}</strong></div>
      <div style="margin-bottom: 2px;">💰 Price: <strong style="color: ${changeColor}">${data.priceChange >= 0 ? '+' : ''}$${data.priceChange.toFixed(2)}</strong></div>
      <div style="margin-bottom: 2px;">📈 Change: <strong style="color: ${changeColor}">${data.percentChange >= 0 ? '+' : ''}${data.percentChange.toFixed(2)}%</strong></div>
      <div style="font-size: 10px; color: #ccc; margin-top: 4px;">
        ${data.startDate} → ${data.endDate}
      </div>
    `;

    this.measureOverlay.appendChild(box);
    this.measureOverlay.appendChild(infoPanel);
    document.body.appendChild(this.measureOverlay);
  }

  private completeMeasurement(endTime: Time, endPrice: number, endX: number, endY: number): void {
    if (!this.measureStartPoint || !this.chart) return;

    const measurement: Measurement = {
      id: `measure_${Date.now()}`,
      startTime: this.measureStartPoint.time,
      endTime,
      startPrice: this.measureStartPoint.price,
      endPrice,
      startX: this.measureStartPoint.x,
      startY: this.measureStartPoint.y,
      endX,
      endY,
      color: this.state.measureColor,
      lineWidth: 2,
      visible: true,
      name: `Measurement ${this.state.measurements.length + 1}`,
      createdAt: new Date()
    };

    this.state.measurements.push(measurement);
    this.drawMeasurement(measurement);

    // Remove the live overlay
    if (this.measureOverlay) {
      this.measureOverlay.remove();
      this.measureOverlay = null;
    }

    // Reset for next measurement
    this.measureStartPoint = null;
    this.currentMeasurement = null;


    // Notify component of changes
    if (this.onLinesChangedCallback) {
      this.onLinesChangedCallback();
    }
  }

  private drawMeasurement(measurement: Measurement): void {
    if (!this.chart || !measurement.visible) return;

    // Create permanent measurement overlay box with arrows (no line series)
    this.createPermanentMeasurementOverlay(measurement);
  }

  private createPermanentMeasurementOverlay(measurement: Measurement): void {

    if (!this.chart) {
      return;
    }

    const chartContainer = this.chart.chartElement();
    if (!chartContainer) {
      return;
    }

    // Get current container bounds
    const containerRect = chartContainer.getBoundingClientRect();

    // Convert measurement coordinates to current screen coordinates
    const startScreenX = this.chart.timeScale().timeToCoordinate(measurement.startTime) || 0;
    const startScreenY = this.mainSeries?.priceToCoordinate(measurement.startPrice) || 0;
    const endScreenX = this.chart.timeScale().timeToCoordinate(measurement.endTime) || 0;
    const endScreenY = this.mainSeries?.priceToCoordinate(measurement.endPrice) || 0;

    // Calculate overlay dimensions and position
    const left = Math.min(startScreenX, endScreenX) + containerRect.left;
    const top = Math.min(startScreenY, endScreenY) + containerRect.top;
    const width = Math.abs(endScreenX - startScreenX);
    const height = Math.abs(endScreenY - startScreenY);

    // Skip if too small to be meaningful
    if (width < 10 && height < 10) return;

    // Calculate measurement data
    const data = this.calculateMeasurementData(measurement.startTime, measurement.startPrice, measurement.endTime, measurement.endPrice);

    // Determine direction and color
    const isPositive = measurement.endPrice >= measurement.startPrice;
    const horizontalDirection = endScreenX > startScreenX ? 'right' : 'left';
    const verticalDirection = endScreenY < startScreenY ? 'up' : 'down';

    const boxColor = isPositive ? '#0096ff' : '#ff4444';
    const boxBgColor = isPositive ? 'rgba(0, 150, 255, 0.15)' : 'rgba(255, 68, 68, 0.15)';
    const shadowColor = isPositive ? 'rgba(0, 150, 255, 0.3)' : 'rgba(255, 68, 68, 0.3)';

    // Create permanent overlay container
    const permanentOverlay = document.createElement('div');
    permanentOverlay.id = `measurement-overlay-${measurement.id}`;
    permanentOverlay.style.cssText = `
      position: fixed;
      left: ${left}px;
      top: ${top}px;
      width: ${width}px;
      height: ${height}px;
      pointer-events: none;
      z-index: 999998;
    `;

    // Create the measurement box
    const box = document.createElement('div');
    box.style.cssText = `
      position: absolute;
      left: 0px;
      top: 0px;
      width: 100%;
      height: 100%;
      background: ${boxBgColor};
      border: 2px solid ${boxColor};
      border-radius: 4px;
      box-sizing: border-box;
      box-shadow: 0 0 8px ${shadowColor};
    `;

    // Add arrows if box is large enough
    if (width > 20 && height > 20) {
      const centerX = width / 2;
      const centerY = height / 2;
      const horizontalDirection = measurement.endTime > measurement.startTime ? 'right' : 'left';
      const verticalDirection = measurement.endPrice > measurement.startPrice ? 'up' : 'down';

      // Horizontal arrow spanning full width - directional based on measurement
      const horizontalArrow = document.createElement('div');
      horizontalArrow.style.cssText = `
        position: absolute;
        left: 8px;
        top: ${centerY - 1}px;
        width: ${width - 16}px;
        height: 2px;
        background: ${boxColor};
        z-index: 10;
      `;

      // Create horizontal arrow head pointing in the direction of measurement
      const horizontalArrowHead = document.createElement('div');
      if (horizontalDirection === 'right') {
        horizontalArrowHead.style.cssText = `
          position: absolute;
          right: -6px;
          top: -4px;
          width: 0;
          height: 0;
          border-top: 5px solid transparent;
          border-bottom: 5px solid transparent;
          border-left: 8px solid ${boxColor};
          z-index: 10;
        `;
      } else {
        horizontalArrowHead.style.cssText = `
          position: absolute;
          left: -6px;
          top: -4px;
          width: 0;
          height: 0;
          border-top: 5px solid transparent;
          border-bottom: 5px solid transparent;
          border-right: 8px solid ${boxColor};
          z-index: 10;
        `;
      }

      horizontalArrow.appendChild(horizontalArrowHead);

      // Vertical arrow spanning full height - directional based on measurement
      const verticalArrow = document.createElement('div');
      verticalArrow.style.cssText = `
        position: absolute;
        left: ${centerX - 1}px;
        top: 8px;
        width: 2px;
        height: ${height - 16}px;
        background: ${boxColor};
        z-index: 10;
      `;

      // Create vertical arrow head pointing in the direction of measurement
      const verticalArrowHead = document.createElement('div');
      if (verticalDirection === 'up') {
        verticalArrowHead.style.cssText = `
          position: absolute;
          left: -4px;
          top: -6px;
          width: 0;
          height: 0;
          border-left: 5px solid transparent;
          border-right: 5px solid transparent;
          border-bottom: 8px solid ${boxColor};
          z-index: 10;
        `;
      } else {
        verticalArrowHead.style.cssText = `
          position: absolute;
          left: -4px;
          bottom: -6px;
          width: 0;
          height: 0;
          border-left: 5px solid transparent;
          border-right: 5px solid transparent;
          border-top: 8px solid ${boxColor};
          z-index: 10;
        `;
      }

      verticalArrow.appendChild(verticalArrowHead);

      box.appendChild(horizontalArrow);
      box.appendChild(verticalArrow);
    }

    permanentOverlay.appendChild(box);

    // Create permanent tooltip for the measurement
    const permanentTooltip = document.createElement('div');
    const isUpwardMeasurement = measurement.endPrice >= measurement.startPrice;

    // Calculate measurement data for tooltip
    const days = Math.abs(Math.floor(((measurement.endTime as number) - (measurement.startTime as number)) / 86400));
    const priceChange = measurement.endPrice - measurement.startPrice;
    const percentChange = (priceChange / measurement.startPrice) * 100;
    const changeColor = priceChange >= 0 ? '#00ff00' : '#ff4444';

    // Position tooltip above or below based on measurement direction
    const tooltipWidth = 200;
    const tooltipHeight = 100;
    const topOffset = 35;
    const bottomOffset = 15;

    let tooltipX = left + width / 2 - tooltipWidth / 2; // Center horizontally
    let tooltipY;

    if (isUpwardMeasurement) {
      tooltipY = top - tooltipHeight - topOffset; // Above box
    } else {
      tooltipY = top + height + bottomOffset; // Below box
    }

    // Adjust if tooltip would go off-screen
    if (tooltipX < 10) tooltipX = 10;
    if (tooltipX + tooltipWidth > window.innerWidth - 10) {
      tooltipX = window.innerWidth - tooltipWidth - 10;
    }
    if (tooltipY < 10) tooltipY = 10;
    if (tooltipY + tooltipHeight > window.innerHeight - 10) {
      tooltipY = window.innerHeight - tooltipHeight - 10;
    }

    permanentTooltip.style.cssText = `
      position: fixed;
      left: ${tooltipX}px;
      top: ${tooltipY}px;
      background: rgba(20, 20, 30, 0.95);
      color: white;
      padding: 12px 16px;
      border-radius: 8px;
      font-size: 13px;
      font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
      line-height: 1.4;
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
      border: 1px solid rgba(255, 255, 255, 0.1);
      backdrop-filter: blur(8px);
      z-index: 999999;
      pointer-events: none;
    `;

    permanentTooltip.innerHTML = `
      <div style="font-weight: bold; margin-bottom: 4px; color: #ffffff;">📏 Measurement</div>
      <div style="margin-bottom: 2px;">📅 Days: <strong>${days}</strong></div>
      <div style="margin-bottom: 2px;">💰 Price: <strong style="color: ${changeColor}">${priceChange >= 0 ? '+' : ''}$${priceChange.toFixed(2)}</strong></div>
      <div>📊 Change: <strong style="color: ${changeColor}">${priceChange >= 0 ? '+' : ''}${percentChange.toFixed(2)}%</strong></div>
    `;

    permanentOverlay.appendChild(permanentTooltip);
    document.body.appendChild(permanentOverlay);

    // Store the overlay for position updates
    this.permanentMeasurementOverlays.set(measurement.id, permanentOverlay);

    // Set up chart view change listeners if this is the first measurement
    if (this.permanentMeasurementOverlays.size === 1) {
      this.setupMeasurementPositionUpdates();
    }

  }

  private addEscapeKeyHandler(): void {
    // Remove existing handler if it exists
    if (this.escapeKeyHandler) {
      document.removeEventListener('keydown', this.escapeKeyHandler);
    }

    // Create new escape key handler
    this.escapeKeyHandler = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && this.state.isMeasuringMode) {
        this.exitMeasuringMode();
      }
    };

    // Add the event listener
    document.addEventListener('keydown', this.escapeKeyHandler);
  }

  private exitMeasuringMode(): void {

    // Update the state to exit measuring mode
    this.state.isMeasuringMode = false;

    // Clean up measuring resources
    this.cleanupMeasuring();

    // Remove ESC key handler
    if (this.escapeKeyHandler) {
      document.removeEventListener('keydown', this.escapeKeyHandler);
      this.escapeKeyHandler = undefined;
    }

    // Notify the component that drawing state has changed
    if (this.onLinesChangedCallback) {
      this.onLinesChangedCallback();
    }
  }

  private setupMeasurementPositionUpdates(): void {
    if (!this.chart) return;

    // Subscribe to chart view changes to update measurement overlay positions
    this.chart.timeScale().subscribeVisibleTimeRangeChange(() => {
      this.updateAllMeasurementPositions();
    });

    // Also listen for resize events
    window.addEventListener('resize', () => {
      setTimeout(() => this.updateAllMeasurementPositions(), 100);
    });
  }

  private setupTextAnnotationPositionUpdates(): void {
    if (!this.chart || this.textAnnotationEventListenersSetup) return;


    // Subscribe to chart view changes to update text annotation positions
    this.chart.timeScale().subscribeVisibleTimeRangeChange(() => {
      this.updateAllTextAnnotationPositions();
    });

    // Also listen for resize events
    window.addEventListener('resize', () => {
      setTimeout(() => this.updateAllTextAnnotationPositions(), 100);
    });

    this.textAnnotationEventListenersSetup = true;
  }

  private updateAllMeasurementPositions(): void {
    if (!this.chart) return;

    this.state.measurements.forEach(measurement => {
      this.updateMeasurementOverlayPosition(measurement);
    });
  }

  private updateMeasurementOverlayPosition(measurement: Measurement): void {
    if (!this.chart) return;

    const overlay = this.permanentMeasurementOverlays.get(measurement.id);
    if (!overlay) return;

    const chartContainer = this.chart.chartElement();
    if (!chartContainer) return;

    // Get current container bounds
    const containerRect = chartContainer.getBoundingClientRect();

    // Convert measurement coordinates to current screen coordinates
    const startScreenX = this.chart.timeScale().timeToCoordinate(measurement.startTime);
    const startScreenY = this.mainSeries?.priceToCoordinate(measurement.startPrice);
    const endScreenX = this.chart.timeScale().timeToCoordinate(measurement.endTime);
    const endScreenY = this.mainSeries?.priceToCoordinate(measurement.endPrice);

    // If any coordinate is null, hide the overlay (measurement is off-screen)
    if (startScreenX === null || startScreenY === null || endScreenX === null || endScreenY === null) {
      overlay.style.display = 'none';
      return;
    }

    // Show the overlay if it was hidden
    overlay.style.display = 'block';

    // Calculate new overlay dimensions and position
    const left = Math.min(startScreenX!, endScreenX!) + containerRect.left;
    const top = Math.min(startScreenY!, endScreenY!) + containerRect.top;
    const width = Math.abs(endScreenX! - startScreenX!);
    const height = Math.abs(endScreenY! - startScreenY!);

    // Update overlay position and size
    overlay.style.left = `${left}px`;
    overlay.style.top = `${top}px`;
    overlay.style.width = `${width}px`;
    overlay.style.height = `${height}px`;

    // Remove all existing content and recreate it with correct dimensions
    overlay.innerHTML = '';

    // Recreate the measurement box with correct dimensions
    this.recreateMeasurementContent(overlay, measurement, width, height, left, top);
  }

  private recreateMeasurementContent(overlay: HTMLElement, measurement: Measurement, width: number, height: number, left: number, top: number): void {
    const isPositive = measurement.endPrice >= measurement.startPrice;
    const boxColor = isPositive ? '#0096ff' : '#ff4444';
    const boxBgColor = isPositive ? 'rgba(0, 150, 255, 0.15)' : 'rgba(255, 68, 68, 0.15)';
    const shadowColor = isPositive ? 'rgba(0, 150, 255, 0.3)' : 'rgba(255, 68, 68, 0.3)';

    // Create the measurement box
    const box = document.createElement('div');
    box.style.cssText = `
      position: absolute;
      left: 0px;
      top: 0px;
      width: 100%;
      height: 100%;
      background: ${boxBgColor};
      border: 2px solid ${boxColor};
      border-radius: 4px;
      box-sizing: border-box;
      box-shadow: 0 0 8px ${shadowColor};
    `;

    // Add arrows if box is large enough
    if (width > 20 && height > 20) {
      const centerX = width / 2;
      const centerY = height / 2;
      const horizontalDirection = measurement.endTime > measurement.startTime ? 'right' : 'left';
      const verticalDirection = measurement.endPrice > measurement.startPrice ? 'up' : 'down';

      // Create horizontal arrow spanning full width - directional based on measurement
      const horizontalArrow = document.createElement('div');
      horizontalArrow.style.cssText = `
        position: absolute;
        left: 8px;
        top: ${centerY - 1}px;
        width: ${width - 16}px;
        height: 2px;
        background: ${boxColor};
        z-index: 10;
      `;

      // Create horizontal arrow head pointing in the direction of measurement
      const horizontalArrowHead = document.createElement('div');
      if (horizontalDirection === 'right') {
        horizontalArrowHead.style.cssText = `
          position: absolute;
          right: -6px;
          top: -4px;
          width: 0;
          height: 0;
          border-top: 5px solid transparent;
          border-bottom: 5px solid transparent;
          border-left: 8px solid ${boxColor};
          z-index: 10;
        `;
      } else {
        horizontalArrowHead.style.cssText = `
          position: absolute;
          left: -6px;
          top: -4px;
          width: 0;
          height: 0;
          border-top: 5px solid transparent;
          border-bottom: 5px solid transparent;
          border-right: 8px solid ${boxColor};
          z-index: 10;
        `;
      }

      horizontalArrow.appendChild(horizontalArrowHead);

      // Create vertical arrow spanning full height - directional based on measurement
      const verticalArrow = document.createElement('div');
      verticalArrow.style.cssText = `
        position: absolute;
        left: ${centerX - 1}px;
        top: 8px;
        width: 2px;
        height: ${height - 16}px;
        background: ${boxColor};
        z-index: 10;
      `;

      // Create vertical arrow head pointing in the direction of measurement
      const verticalArrowHead = document.createElement('div');
      if (verticalDirection === 'up') {
        verticalArrowHead.style.cssText = `
          position: absolute;
          left: -4px;
          top: -6px;
          width: 0;
          height: 0;
          border-left: 5px solid transparent;
          border-right: 5px solid transparent;
          border-bottom: 8px solid ${boxColor};
          z-index: 10;
        `;
      } else {
        verticalArrowHead.style.cssText = `
          position: absolute;
          left: -4px;
          bottom: -6px;
          width: 0;
          height: 0;
          border-left: 5px solid transparent;
          border-right: 5px solid transparent;
          border-top: 8px solid ${boxColor};
          z-index: 10;
        `;
      }

      verticalArrow.appendChild(verticalArrowHead);

      box.appendChild(horizontalArrow);
      box.appendChild(verticalArrow);
    }

    overlay.appendChild(box);

    // Recreate permanent tooltip with correct positioning
    const permanentTooltip = document.createElement('div');
    const isUpwardMeasurement = measurement.endPrice >= measurement.startPrice;

    // Calculate measurement data for tooltip
    const days = Math.abs(Math.floor(((measurement.endTime as number) - (measurement.startTime as number)) / 86400));
    const priceChange = measurement.endPrice - measurement.startPrice;
    const percentChange = (priceChange / measurement.startPrice) * 100;
    const changeColor = priceChange >= 0 ? '#00ff00' : '#ff4444';

    // Position tooltip above or below based on measurement direction
    const tooltipWidth = 200;
    const tooltipHeight = 100;
    const topOffset = 35;
    const bottomOffset = 15;

    let tooltipX = left + width / 2 - tooltipWidth / 2; // Center horizontally
    let tooltipY;

    if (isUpwardMeasurement) {
      tooltipY = top - tooltipHeight - topOffset; // Above box
    } else {
      tooltipY = top + height + bottomOffset; // Below box
    }

    // Adjust if tooltip would go off-screen
    if (tooltipX < 10) tooltipX = 10;
    if (tooltipX + tooltipWidth > window.innerWidth - 10) {
      tooltipX = window.innerWidth - tooltipWidth - 10;
    }
    if (tooltipY < 10) tooltipY = 10;
    if (tooltipY + tooltipHeight > window.innerHeight - 10) {
      tooltipY = window.innerHeight - tooltipHeight - 10;
    }

    permanentTooltip.style.cssText = `
      position: fixed;
      left: ${tooltipX}px;
      top: ${tooltipY}px;
      background: rgba(20, 20, 30, 0.95);
      color: white;
      padding: 12px 16px;
      border-radius: 8px;
      font-size: 13px;
      font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
      line-height: 1.4;
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
      border: 1px solid rgba(255, 255, 255, 0.1);
      backdrop-filter: blur(8px);
      z-index: 999999;
      pointer-events: none;
    `;

    permanentTooltip.innerHTML = `
      <div style="font-weight: bold; margin-bottom: 4px; color: #ffffff;">📏 Measurement</div>
      <div style="margin-bottom: 2px;">📅 Days: <strong>${days}</strong></div>
      <div style="margin-bottom: 2px;">💰 Price: <strong style="color: ${changeColor}">${priceChange >= 0 ? '+' : ''}$${priceChange.toFixed(2)}</strong></div>
      <div>📊 Change: <strong style="color: ${changeColor}">${priceChange >= 0 ? '+' : ''}${percentChange.toFixed(2)}%</strong></div>
    `;

    overlay.appendChild(permanentTooltip);
  }

  private cleanupMeasuring(): void {
    if (!this.chart) return;


    // Remove ESC key handler
    if (this.escapeKeyHandler) {
      document.removeEventListener('keydown', this.escapeKeyHandler);
      this.escapeKeyHandler = undefined;
    }

    // Reset cursor
    const chartContainer = this.chart.chartElement();
    if (chartContainer) {
      chartContainer.style.cursor = 'default';

      // Remove mouse move handler if it exists
      const mouseMoveHandler = (chartContainer as any)._measureMouseMoveHandler;
      if (mouseMoveHandler) {
        chartContainer.removeEventListener('mousemove', mouseMoveHandler);
        delete (chartContainer as any)._measureMouseMoveHandler;
      }
    }

    // Remove cursor move handler
    if (this.measureMoveHandler) {
      this.chart.unsubscribeCrosshairMove(this.measureMoveHandler);
      this.measureMoveHandler = undefined;
    }

    // Remove live overlay
    if (this.measureOverlay) {
      this.measureOverlay.remove();
      this.measureOverlay = null;
    }

    // Reset state
    this.measureStartPoint = null;
    this.currentMeasurement = null;

    // Note: No need to remove click handler as measuring is handled in main click handler
  }

  public clearAllMeasurements(): void {
    // Remove all permanent measurement overlays
    this.permanentMeasurementOverlays.forEach((overlay, measurementId) => {
      overlay.remove();
    });
    this.permanentMeasurementOverlays.clear();

    // Remove any active measuring overlay and reset in-progress state.
    if (this.measureOverlay) {
      this.measureOverlay.remove();
      this.measureOverlay = null;
    }
    this.measureStartPoint = null;
    this.currentMeasurement = null;

    this.state.measurements = [];
    this.state.isMeasuringMode = false;
    if (this.onLinesChangedCallback) {
      this.onLinesChangedCallback();
    }
  }
  // ===== ARROW TOOL METHODS =====
  private arrowStartPoint: { time: Time; price: number } | null = null;
  private arrowDrawingsContainer: HTMLDivElement | null = null;
  private arrowCanvas: HTMLCanvasElement | null = null;
  private arrowContext: CanvasRenderingContext2D | null = null;
  private arrowPreviewEndPoint: { x: number; y: number } | null = null;
  private arrowEscapeHandler?: (event: KeyboardEvent) => void;
  private arrowVisibleRangeHandler?: () => void;
  private arrowResizeHandler?: () => void;
  private arrowClickHandler?: (event: MouseEvent) => void;
  private arrowMouseMoveHandler?: (event: MouseEvent) => void;
  private arrowMouseDownHandler?: (event: MouseEvent) => void;
  private arrowBoundChartElement: HTMLElement | null = null;
  private arrowDragTargetId: string | null = null;
  private arrowDragStartClient: { x: number; y: number } | null = null;
  private arrowDragOriginalStart: { x: number; y: number } | null = null;
  private arrowDragOriginalEnd: { x: number; y: number } | null = null;
  private arrowDragMoved = false;
  private suppressNextArrowClick = false;
  private arrowWindowMouseMoveHandler?: (event: MouseEvent) => void;
  private arrowWindowMouseUpHandler?: (event: MouseEvent) => void;
  private arrowSelectionClickHandler?: (event: MouseEvent) => void;
  private arrowSelectionBoundElement: HTMLElement | null = null;
  private selectedArrowDrawingId: string | null = null;
  private static readonly ARROW_DEFAULT_COLOR = '#2962FF';
  private static readonly ARROW_DEFAULT_WIDTH = 2;
  private arrowDefaultColor = TradingViewChartService.ARROW_DEFAULT_COLOR;
  private arrowDefaultWidth = TradingViewChartService.ARROW_DEFAULT_WIDTH;

  public getArrowMode(): boolean {
    return this.state.isArrowMode;
  }

  public getArrowDrawings(): ArrowDrawing[] {
    return [...this.state.arrowDrawings];
  }

  public getArrowDefaultColor(): string {
    return this.arrowDefaultColor;
  }

  public getArrowDefaultWidth(): number {
    return this.arrowDefaultWidth;
  }

  public setArrowDefaultColor(color: string): void {
    if (!color) return;
    this.arrowDefaultColor = color;
  }

  public setArrowDefaultWidth(width: number): void {
    if (!Number.isFinite(width)) return;
    this.arrowDefaultWidth = Math.max(1, Math.min(6, Math.round(width)));
  }

  public setArrowMode(enabled: boolean): void {
    this.state.isArrowMode = enabled;
    if (enabled) {
      this.initializeArrowTool();
    } else {
      this.cleanupArrowTool();
    }
  }

  public clearAllArrowDrawings(): void {
    this.state.arrowDrawings = [];
    this.selectedArrowDrawingId = null;
    this.arrowStartPoint = null;
    this.removeArrowPreview();
    this.redrawAllArrowDrawings();
    if (this.onArrowSelectedCallback) {
      this.onArrowSelectedCallback(null);
    }
    if (this.onLinesChangedCallback) {
      this.onLinesChangedCallback();
    }
  }

  public removeArrowDrawing(id: string): void {
    this.state.arrowDrawings = this.state.arrowDrawings.filter(a => a.id !== id);
    if (this.selectedArrowDrawingId === id) {
      this.selectedArrowDrawingId = null;
      if (this.onArrowSelectedCallback) {
        this.onArrowSelectedCallback(null);
      }
    }
    this.redrawAllArrowDrawings();
    if (this.onLinesChangedCallback) {
      this.onLinesChangedCallback();
    }
  }

  public updateArrowDrawing(
    id: string,
    updates: Partial<Pick<ArrowDrawing, 'color' | 'lineWidth' | 'name' | 'visible' | 'startTime' | 'startPrice' | 'endTime' | 'endPrice'>>
  ): void {
    const idx = this.state.arrowDrawings.findIndex(a => a.id === id);
    if (idx === -1) return;
    const current = this.state.arrowDrawings[idx];
    this.state.arrowDrawings[idx] = {
      ...current,
      ...updates,
      lineWidth: updates.lineWidth != null ? Math.max(1, Math.min(6, updates.lineWidth)) : current.lineWidth
    };
    this.redrawAllArrowDrawings();
    if (this.onLinesChangedCallback) {
      this.onLinesChangedCallback();
    }
  }

  public selectArrowDrawing(drawingId: string, clientX?: number, clientY?: number): void {
    const drawing = this.state.arrowDrawings.find(a => a.id === drawingId);
    if (!drawing) return;
    this.selectedArrowDrawingId = drawingId;
    this.redrawAllArrowDrawings();

    let x = clientX ?? 0;
    let y = clientY ?? 0;
    if ((clientX == null || clientY == null) && this.chart) {
      const startPx = this.chartToPixelCoordinates(drawing.startTime, drawing.startPrice);
      const endPx = this.chartToPixelCoordinates(drawing.endTime, drawing.endPrice);
      const chartElement = this.chart.chartElement();
      if (startPx && endPx && chartElement) {
        const rect = chartElement.getBoundingClientRect();
        x = rect.left + ((startPx.x + endPx.x) / 2);
        y = rect.top + ((startPx.y + endPx.y) / 2);
      }
    }

    if (this.onArrowSelectedCallback) {
      this.onArrowSelectedCallback(drawing, x, y);
    }
  }

  public deselectArrowDrawing(): void {
    this.selectedArrowDrawingId = null;
    this.redrawAllArrowDrawings();
    if (this.onArrowSelectedCallback) {
      this.onArrowSelectedCallback(null);
    }
  }

  public getSelectedArrowDrawing(): ArrowDrawing | null {
    if (!this.selectedArrowDrawingId) return null;
    return this.state.arrowDrawings.find(a => a.id === this.selectedArrowDrawingId) || null;
  }

  private initializeArrowTool(): void {
    if (!this.chart) return;

    this.ensureArrowDrawingsContainer();

    // Disable chart scroll / scale so a click actually lands as a click (not a pan)
    this.chart.applyOptions({
      handleScroll: false,
      handleScale: false,
    });

    const chartElement = this.chart.chartElement();
    if (!chartElement) return;
    this.arrowBoundChartElement = chartElement;

    // Suppress the chart's native mouse-drag so clicks stay as clicks
    this.arrowMouseDownHandler = (event: MouseEvent) => {
      if (!this.state.isArrowMode) return;
      // Left-button only; stop the chart's internal drag logic before it kicks in
      if (event.button === 0) {
        const rect = chartElement.getBoundingClientRect();
        const x = event.clientX - rect.left;
        const y = event.clientY - rect.top;
        const hitArrow = this.findArrowDrawingAtPoint(x, y);
        // Drag existing arrow only when not in the middle of placing a new one.
        if (hitArrow && !this.arrowStartPoint) {
          const startPx = this.chartToPixelCoordinates(hitArrow.startTime, hitArrow.startPrice);
          const endPx = this.chartToPixelCoordinates(hitArrow.endTime, hitArrow.endPrice);
          if (startPx && endPx) {
            this.arrowDragTargetId = hitArrow.id;
            this.arrowDragStartClient = { x: event.clientX, y: event.clientY };
            this.arrowDragOriginalStart = { x: startPx.x, y: startPx.y };
            this.arrowDragOriginalEnd = { x: endPx.x, y: endPx.y };
            this.arrowDragMoved = false;
            this.selectArrowDrawing(hitArrow.id, event.clientX, event.clientY);
            this.ensureArrowDragWindowListeners();
          }
        }
        event.stopPropagation();
      }
    };
    chartElement.addEventListener('mousedown', this.arrowMouseDownHandler, true);

    // Click places / completes the arrow.
    this.arrowClickHandler = (event: MouseEvent) => {
      if (!this.state.isArrowMode || !this.chart) return;
      if (event.button !== 0) return;
      if (this.suppressNextArrowClick) {
        this.suppressNextArrowClick = false;
        event.preventDefault();
        event.stopPropagation();
        return;
      }

      const rect = chartElement.getBoundingClientRect();
      const x = event.clientX - rect.left;
      const y = event.clientY - rect.top;
      if (x < 0 || y < 0 || x > rect.width || y > rect.height) return;

      // Priority: if user clicks an existing arrow, open edit toolbar instead of starting a new arrow
      const clickedArrow = this.findArrowDrawingAtPoint(x, y);
      if (clickedArrow && !this.arrowStartPoint) {
        event.preventDefault();
        event.stopPropagation();
        this.selectArrowDrawing(clickedArrow.id, event.clientX, event.clientY);
        return;
      }

      const coords = this.pixelToChartCoordinates(x, y);
      if (!coords) return;

      event.preventDefault();
      event.stopPropagation();

      if (!this.arrowStartPoint) {
        this.arrowStartPoint = { time: coords.time, price: coords.price };
        this.showArrowPreview(x, y, x, y);
      } else {
        this.completeArrowDrawing(coords.time, coords.price);
      }
    };
    chartElement.addEventListener('click', this.arrowClickHandler, true);

    // Live preview tracking between first and second click
    this.arrowMouseMoveHandler = (event: MouseEvent) => {
      if (!this.state.isArrowMode) return;
      const rect = chartElement.getBoundingClientRect();
      const x = event.clientX - rect.left;
      const y = event.clientY - rect.top;
      const hoverArrow = this.findArrowDrawingAtPoint(x, y);
      chartElement.style.cursor = this.arrowStartPoint ? 'crosshair' : (hoverArrow ? 'pointer' : 'crosshair');
      if (!this.arrowStartPoint) return;
      const startPx = this.chartToPixelCoordinates(this.arrowStartPoint.time, this.arrowStartPoint.price);
      if (!startPx) return;
      this.showArrowPreview(startPx.x, startPx.y, x, y);
    };
    chartElement.addEventListener('mousemove', this.arrowMouseMoveHandler);

    // ESC cancels an in-progress arrow
    this.arrowEscapeHandler = (event: KeyboardEvent) => {
      if (event.key !== 'Escape' || !this.state.isArrowMode) return;
      if (this.arrowStartPoint) {
        this.arrowStartPoint = null;
        this.removeArrowPreview();
      }
    };
    document.addEventListener('keydown', this.arrowEscapeHandler);

    // Reposition arrows on zoom / pan
    if (!this.arrowVisibleRangeHandler) {
      this.arrowVisibleRangeHandler = () => this.redrawAllArrowDrawings();
      this.chart.timeScale().subscribeVisibleTimeRangeChange(this.arrowVisibleRangeHandler);
    }

    // Reposition on resize
    if (!this.arrowResizeHandler) {
      this.arrowResizeHandler = () => setTimeout(() => this.redrawAllArrowDrawings(), 50);
      window.addEventListener('resize', this.arrowResizeHandler);
    }

    chartElement.style.cursor = 'crosshair';
    this.redrawAllArrowDrawings();
  }

  private cleanupArrowTool(): void {
    this.arrowStartPoint = null;
    this.removeArrowPreview();

    const chartElement = this.arrowBoundChartElement;
    if (chartElement) {
      if (this.arrowClickHandler) {
        chartElement.removeEventListener('click', this.arrowClickHandler, true);
      }
      if (this.arrowMouseMoveHandler) {
        chartElement.removeEventListener('mousemove', this.arrowMouseMoveHandler);
      }
      if (this.arrowMouseDownHandler) {
        chartElement.removeEventListener('mousedown', this.arrowMouseDownHandler, true);
      }
      chartElement.style.cursor = 'default';
    }
    this.arrowClickHandler = undefined;
    this.arrowMouseMoveHandler = undefined;
    this.arrowMouseDownHandler = undefined;
    this.arrowBoundChartElement = null;
    this.resetArrowDragState();
    if (this.arrowWindowMouseMoveHandler) {
      window.removeEventListener('mousemove', this.arrowWindowMouseMoveHandler, true);
      this.arrowWindowMouseMoveHandler = undefined;
    }
    if (this.arrowWindowMouseUpHandler) {
      window.removeEventListener('mouseup', this.arrowWindowMouseUpHandler, true);
      this.arrowWindowMouseUpHandler = undefined;
    }

    if (this.arrowEscapeHandler) {
      document.removeEventListener('keydown', this.arrowEscapeHandler);
      this.arrowEscapeHandler = undefined;
    }

    // Restore chart pan / zoom once arrow tool is off
    if (this.chart) {
      this.chart.applyOptions({
        handleScroll: true,
        handleScale: true,
      });
    }

    // Keep visible-range/resize handlers alive so existing arrows continue to track chart on zoom/pan
    this.redrawAllArrowDrawings();
  }

  private findArrowDrawingAtPoint(x: number, y: number): ArrowDrawing | null {
    for (let i = this.state.arrowDrawings.length - 1; i >= 0; i--) {
      const arrow = this.state.arrowDrawings[i];
      if (!arrow.visible) continue;
      const start = this.chartToPixelCoordinates(arrow.startTime, arrow.startPrice);
      const end = this.chartToPixelCoordinates(arrow.endTime, arrow.endPrice);
      if (!start || !end) continue;
      const hitThreshold = Math.max(8, arrow.lineWidth * 2 + 4);
      const dist = this.distanceToLineSegment(x, y, start.x, start.y, end.x, end.y);
      if (dist <= hitThreshold) return arrow;
    }
    return null;
  }

  private ensureArrowDragWindowListeners(): void {
    if (this.arrowWindowMouseMoveHandler && this.arrowWindowMouseUpHandler) return;

    this.arrowWindowMouseMoveHandler = (event: MouseEvent) => {
      if (!this.arrowDragTargetId || !this.arrowDragStartClient || !this.arrowDragOriginalStart || !this.arrowDragOriginalEnd) return;
      if (!this.chart) return;

      const chartElement = this.chart.chartElement();
      const rect = chartElement.getBoundingClientRect();
      const dx = event.clientX - this.arrowDragStartClient.x;
      const dy = event.clientY - this.arrowDragStartClient.y;

      if (!this.arrowDragMoved && (Math.abs(dx) > 2 || Math.abs(dy) > 2)) {
        this.arrowDragMoved = true;
      }

      const nextStart = this.pixelToChartCoordinates(this.arrowDragOriginalStart.x + dx, this.arrowDragOriginalStart.y + dy);
      const nextEnd = this.pixelToChartCoordinates(this.arrowDragOriginalEnd.x + dx, this.arrowDragOriginalEnd.y + dy);
      if (!nextStart || !nextEnd) return;

      this.updateArrowDrawing(this.arrowDragTargetId, {
        startTime: nextStart.time,
        startPrice: nextStart.price,
        endTime: nextEnd.time,
        endPrice: nextEnd.price
      });

      // Keep toolbar following drag
      const midX = ((this.arrowDragOriginalStart.x + dx) + (this.arrowDragOriginalEnd.x + dx)) / 2;
      const midY = ((this.arrowDragOriginalStart.y + dy) + (this.arrowDragOriginalEnd.y + dy)) / 2;
      this.selectArrowDrawing(this.arrowDragTargetId, rect.left + midX, rect.top + midY);
      event.preventDefault();
      event.stopPropagation();
    };

    this.arrowWindowMouseUpHandler = (event: MouseEvent) => {
      if (this.arrowDragMoved) {
        this.suppressNextArrowClick = true;
        event.preventDefault();
        event.stopPropagation();
      }
      this.resetArrowDragState();
    };

    window.addEventListener('mousemove', this.arrowWindowMouseMoveHandler, true);
    window.addEventListener('mouseup', this.arrowWindowMouseUpHandler, true);
  }

  private resetArrowDragState(): void {
    this.arrowDragTargetId = null;
    this.arrowDragStartClient = null;
    this.arrowDragOriginalStart = null;
    this.arrowDragOriginalEnd = null;
    this.arrowDragMoved = false;
  }

  private ensureArrowSelectionListener(chartElement: HTMLElement): void {
    if (this.arrowSelectionBoundElement && this.arrowSelectionBoundElement !== chartElement && this.arrowSelectionClickHandler) {
      this.arrowSelectionBoundElement.removeEventListener('click', this.arrowSelectionClickHandler, true);
      this.arrowSelectionBoundElement = null;
    }
    if (this.arrowSelectionBoundElement) return;

    this.arrowSelectionClickHandler = (event: MouseEvent) => {
      if (!this.chart || this.state.isArrowMode) return;
      const rect = chartElement.getBoundingClientRect();
      const x = event.clientX - rect.left;
      const y = event.clientY - rect.top;
      if (x < 0 || y < 0 || x > rect.width || y > rect.height) return;
      const hitArrow = this.findArrowDrawingAtPoint(x, y);
      if (hitArrow) {
        this.selectArrowDrawing(hitArrow.id, event.clientX, event.clientY);
        event.stopPropagation();
      } else if (this.selectedArrowDrawingId) {
        this.deselectArrowDrawing();
      }
    };

    chartElement.addEventListener('click', this.arrowSelectionClickHandler, true);
    this.arrowSelectionBoundElement = chartElement;
  }

  private ensureArrowDrawingsContainer(): void {
    if (!this.chart) return;
    const chartElement = this.chart.chartElement();
    if (!chartElement) return;
    this.ensureArrowSelectionListener(chartElement);

    // Drop a stale reference when the chart was re-rendered and the container was detached
    if (this.arrowDrawingsContainer && !this.arrowDrawingsContainer.parentElement) {
      this.arrowDrawingsContainer = null;
    }
    if (this.arrowDrawingsContainer) {
      // Keep it in sync with current chart size so SVG placement stays accurate
      const rect = chartElement.getBoundingClientRect();
      this.arrowDrawingsContainer.style.width = `${rect.width}px`;
      this.arrowDrawingsContainer.style.height = `${rect.height}px`;
      this.resizeArrowCanvas(rect.width, rect.height);
      return;
    }

    chartElement.style.position = 'relative';
    const rect = chartElement.getBoundingClientRect();
    this.arrowDrawingsContainer = document.createElement('div');
    this.arrowDrawingsContainer.setAttribute('data-role', 'arrow-drawings');
    this.arrowDrawingsContainer.style.cssText =
      'position:absolute;inset:0;width:100%;height:100%;z-index:1000001;pointer-events:none;visibility:visible;overflow:visible;';
    chartElement.appendChild(this.arrowDrawingsContainer);

    this.arrowCanvas = document.createElement('canvas');
    this.arrowCanvas.setAttribute('data-role', 'arrow-canvas');
    this.arrowCanvas.style.cssText = 'position:absolute;inset:0;width:100%;height:100%;pointer-events:none;display:block;';
    this.arrowDrawingsContainer.appendChild(this.arrowCanvas);

    this.arrowContext = this.arrowCanvas.getContext('2d');
    this.resizeArrowCanvas(rect.width, rect.height);
  }

  private setArrowDrawingsVisibility(visible: boolean): void {
    if (!this.arrowDrawingsContainer) {
      this.ensureArrowDrawingsContainer();
    }
    if (this.arrowDrawingsContainer) {
      this.arrowDrawingsContainer.style.visibility = visible ? 'visible' : 'hidden';
    }
  }

  private showArrowPreview(x1: number, y1: number, x2: number, y2: number): void {
    void x1;
    void y1;
    this.arrowPreviewEndPoint = { x: x2, y: y2 };
    this.redrawAllArrowDrawings();
  }

  private removeArrowPreview(): void {
    this.arrowPreviewEndPoint = null;
    this.redrawAllArrowDrawings();
  }

  private completeArrowDrawing(endTime: Time, endPrice: number): void {
    if (!this.arrowStartPoint) return;

    if (
      this.arrowStartPoint.time === endTime &&
      this.arrowStartPoint.price === endPrice
    ) {
      return;
    }

    const arrow: ArrowDrawing = {
      id: `arrow-${Date.now()}-${Math.random().toString(36).substr(2, 6)}`,
      startTime: this.arrowStartPoint.time,
      startPrice: this.arrowStartPoint.price,
      endTime,
      endPrice,
      color: this.arrowDefaultColor,
      lineWidth: this.arrowDefaultWidth,
      visible: true,
      name: `Arrow ${this.state.arrowDrawings.length + 1}`,
      createdAt: new Date()
    };

    this.state.arrowDrawings.push(arrow);
    console.log('[Arrow] Created', arrow, 'total:', this.state.arrowDrawings.length);
    this.arrowStartPoint = null;
    this.removeArrowPreview();
    this.redrawAllArrowDrawings();

    if (this.onLinesChangedCallback) {
      this.onLinesChangedCallback();
    }
  }

  /** Rebuild all arrow SVGs (called on zoom/pan/resize). */
  private redrawAllArrowDrawings(): void {
    this.ensureArrowDrawingsContainer();
    if (!this.arrowDrawingsContainer || !this.arrowCanvas || !this.arrowContext) {
      console.warn('[Arrow] redraw skipped: no container');
      return;
    }
    const width = this.arrowCanvas.clientWidth || this.arrowCanvas.width;
    const height = this.arrowCanvas.clientHeight || this.arrowCanvas.height;
    this.arrowContext.clearRect(0, 0, width, height);

    console.log('[Arrow] redrawing', this.state.arrowDrawings.length, 'arrows');
    for (const arrow of this.state.arrowDrawings) {
      if (!arrow.visible) continue;
      const start = this.chartToPixelCoordinates(arrow.startTime, arrow.startPrice);
      const end = this.chartToPixelCoordinates(arrow.endTime, arrow.endPrice);
      if (!start || !end) {
        console.warn('[Arrow] could not project arrow to pixels', arrow, { start, end });
        continue;
      }

      const isSelected = this.selectedArrowDrawingId === arrow.id;
      this.drawArrowOnCanvas(
        start.x, start.y, end.x, end.y,
        isSelected ? '#FF6B35' : arrow.color,
        isSelected ? arrow.lineWidth + 1 : arrow.lineWidth,
        false
      );
      console.log('[Arrow] drew', arrow.id, 'from', start, 'to', end);
    }

    if (this.arrowStartPoint && this.arrowPreviewEndPoint) {
      const startPx = this.chartToPixelCoordinates(this.arrowStartPoint.time, this.arrowStartPoint.price);
      if (startPx) {
        this.drawArrowOnCanvas(
          startPx.x,
          startPx.y,
          this.arrowPreviewEndPoint.x,
          this.arrowPreviewEndPoint.y,
          this.arrowDefaultColor,
          this.arrowDefaultWidth,
          true
        );
      }
    }
  }

  /** Public redraw hook used by component after delayed chart hydration. */
  public redrawArrowDrawings(): void {
    this.redrawAllArrowDrawings();
  }

  private resizeArrowCanvas(width: number, height: number): void {
    if (!this.arrowCanvas || !this.arrowContext) return;
    const w = Math.max(1, width);
    const h = Math.max(1, height);
    const dpr = window.devicePixelRatio || 1;
    this.arrowCanvas.width = Math.round(w * dpr);
    this.arrowCanvas.height = Math.round(h * dpr);
    this.arrowCanvas.style.width = `${w}px`;
    this.arrowCanvas.style.height = `${h}px`;
    this.arrowContext.setTransform(dpr, 0, 0, dpr, 0, 0);
  }

  private drawArrowOnCanvas(
    x1: number,
    y1: number,
    x2: number,
    y2: number,
    color: string,
    lineWidth: number,
    isPreview: boolean
  ): void {
    if (!this.arrowContext) return;
    const ctx = this.arrowContext;
    const dx = x2 - x1;
    const dy = y2 - y1;
    const len = Math.hypot(dx, dy);
    if (len < 1) return;

    const ux = dx / len;
    const uy = dy / len;
    const headLength = Math.max(8, lineWidth * 5);
    const shaftEndX = x2 - ux * headLength;
    const shaftEndY = y2 - uy * headLength;

    ctx.save();
    ctx.strokeStyle = color;
    ctx.fillStyle = color;
    ctx.lineWidth = lineWidth;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    if (isPreview) {
      ctx.setLineDash([4, 3]);
      ctx.globalAlpha = 0.85;
    } else {
      ctx.setLineDash([]);
      ctx.globalAlpha = 1;
    }

    // Shaft
    ctx.beginPath();
    ctx.moveTo(x1, y1);
    ctx.lineTo(shaftEndX, shaftEndY);
    ctx.stroke();

    // Arrow head
    const perpX = -uy;
    const perpY = ux;
    const wing = Math.max(4, headLength * 0.42);
    const leftX = shaftEndX + perpX * wing;
    const leftY = shaftEndY + perpY * wing;
    const rightX = shaftEndX - perpX * wing;
    const rightY = shaftEndY - perpY * wing;

    ctx.beginPath();
    ctx.moveTo(x2, y2);
    ctx.lineTo(leftX, leftY);
    ctx.lineTo(rightX, rightY);
    ctx.closePath();
    ctx.fill();
    ctx.restore();
  }

  private buildArrowSvg(
    idSuffix: string,
    x1: number, y1: number, x2: number, y2: number,
    color: string, lineWidth: number,
    isPreview: boolean
  ): SVGSVGElement | null {
    if (!this.chart) return null;
    const chartElement = this.chart.chartElement();
    if (!chartElement) return null;

    const rect = chartElement.getBoundingClientRect();
    const w = Math.max(1, rect.width);
    const h = Math.max(1, rect.height);

    const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    svg.setAttribute('id', idSuffix);
    svg.setAttribute('width', String(w));
    svg.setAttribute('height', String(h));
    svg.setAttribute('viewBox', `0 0 ${w} ${h}`);
    svg.setAttribute('xmlns', 'http://www.w3.org/2000/svg');
    svg.style.cssText = `position:absolute;top:0;left:0;pointer-events:none;overflow:visible;display:block;`;
    if (isPreview) svg.style.opacity = '0.85';

    const markerId = `arrowhead-${idSuffix}`;
    const defs = document.createElementNS('http://www.w3.org/2000/svg', 'defs');
    const marker = document.createElementNS('http://www.w3.org/2000/svg', 'marker');
    marker.setAttribute('id', markerId);
    marker.setAttribute('viewBox', '0 0 10 10');
    marker.setAttribute('refX', '8');
    marker.setAttribute('refY', '5');
    marker.setAttribute('markerWidth', '6');
    marker.setAttribute('markerHeight', '6');
    marker.setAttribute('orient', 'auto-start-reverse');
    const markerPath = document.createElementNS('http://www.w3.org/2000/svg', 'path');
    markerPath.setAttribute('d', 'M 0 0 L 10 5 L 0 10 Z');
    markerPath.setAttribute('fill', color);
    marker.appendChild(markerPath);
    defs.appendChild(marker);
    svg.appendChild(defs);

    const line = document.createElementNS('http://www.w3.org/2000/svg', 'line');
    line.setAttribute('data-role', 'arrow-line');
    line.setAttribute('stroke', color);
    line.setAttribute('stroke-width', String(lineWidth));
    line.setAttribute('stroke-linecap', 'round');
    line.setAttribute('marker-end', `url(#${markerId})`);
    if (isPreview) {
      line.setAttribute('stroke-dasharray', '4 3');
    }
    svg.appendChild(line);

    this.updateArrowSvgGeometry(svg, x1, y1, x2, y2);
    return svg;
  }

  private updateArrowSvgGeometry(svg: SVGSVGElement, x1: number, y1: number, x2: number, y2: number): void {
    const line = svg.querySelector('line[data-role="arrow-line"]') as SVGLineElement | null;
    if (!line) return;

    const padding = 16;
    const minX = Math.min(x1, x2);
    const minY = Math.min(y1, y2);
    const maxX = Math.max(x1, x2);
    const maxY = Math.max(y1, y2);

    const left = minX - padding;
    const top = minY - padding;
    const width = Math.max(1, maxX - minX + padding * 2);
    const height = Math.max(1, maxY - minY + padding * 2);

    svg.style.left = `${left}px`;
    svg.style.top = `${top}px`;
    svg.style.width = `${width}px`;
    svg.style.height = `${height}px`;
    svg.setAttribute('width', String(width));
    svg.setAttribute('height', String(height));
    svg.setAttribute('viewBox', `0 0 ${width} ${height}`);

    const localX1 = x1 - left;
    const localY1 = y1 - top;
    const localX2 = x2 - left;
    const localY2 = y2 - top;

    const dx = localX2 - localX1;
    const dy = localY2 - localY1;
    const len = Math.hypot(dx, dy);
    const strokeWidth = parseFloat(line.getAttribute('stroke-width') || '2');
    // Shorten the line a little at the head so the marker sits flush at the target point
    const headInset = len > 4 ? Math.min(Math.max(strokeWidth * 3, 6), len - 2) : 0;
    const ux = len > 0 ? dx / len : 0;
    const uy = len > 0 ? dy / len : 0;
    const adjX2 = localX2 - ux * headInset;
    const adjY2 = localY2 - uy * headInset;

    line.setAttribute('x1', String(localX1));
    line.setAttribute('y1', String(localY1));
    line.setAttribute('x2', String(adjX2));
    line.setAttribute('y2', String(adjY2));
  }

  // Recalculates pane heights to assure primary chart uses available height and indicators are fixed.
  private recalculatePaneLayouts(): void {
    if (!this.chart) return;
    
    // Defer the recalculation to allow Lightweight Charts to destroy empty panes synchronously first.
    setTimeout(() => {
      try {
        if (!this.chart) return;
        const panes = this.chart.panes();
        const numPanes = panes.length;
        if (numPanes <= 1) return;

        // Ensure lower indicator panes stay consistently sized (120px height conceptually)
        for (let i = 1; i < numPanes; i++) {
          panes[i].setHeight(120);
        }
      } catch (e) {
        console.warn('[CHART] Could not recalculate pane layouts', e);
      }
    }, 0);
  }
} 